// Backend API client for the Deskte CRM frontend.
//
// Uses the apiFetch helper from AuthGate.jsx, which automatically attaches
// the MSAL access token. All endpoints are proxied through Vite to
// http://localhost:3000.
//
// Each domain function returns data already mapped to the shape the existing
// frontend expects (camelCase, with field names matching SEED_CLIENTS etc.),
// so callers can swap from SEED data to real data with minimal churn.

import { apiFetch, intentosDeLaPeticion } from './AuthGate.jsx';
import { formatPhoneUS, componerMemberIds } from './utils.js';
import { STATUS_BACK_TO_FRONT, estadoParaElServidor } from './policyStatuses.js';
import { POLICY_DETAIL_KEYS } from './camposDePoliza.js';
// Armar la poliza para el servidor vive aparte para poder probarse, y porque
// un guardado parcial no puede poner la prima en cero (2026-09-13).
import { mapPolicyToApi } from './polizaParaElServidor.js';
// Las tres tablas del TIPO de poliza viven en el catalogo, no aqui: estaban
// copiadas en tres archivos y cada copia se quedo corta por su lado (ver la
// cabecera de esas tablas en policyTypes.js). Se importan con los nombres de
// siempre para no tocar ni una linea de lo que ya las usa.
import {
  TIPO_A_ENUM as POLICY_TYPE_FRONT_TO_BACK,
  ENUM_A_TIPO as POLICY_TYPE_BACK_TO_FRONT,
  ALIAS_ANTIGUOS as LEGACY_FRONT_TYPE_ALIAS,
} from './policyTypes.js';
// El estatus migratorio, por el mismo motivo que los tipos de poliza: la lista
// y la regla de como se guarda estan en un solo archivo. Aqui se tenia una
// copia del mapa, y esa copia se quedo sin enterarse el dia que la agencia
// pudo escribir estatus propios — borraba el estatus en cada guardado.
import { IMMIGRATION_MAP, estatusMigratorioParaGuardar } from './estatusMigratorio.js';
import { limpiarOtrosIngresos } from './otrosIngresos.js';

const API_BASE = import.meta.env.VITE_API_URL || '';

// H5: DOB ciphertext lives in the DB; the API returns the date as
// (monthDay, year) so birthday alerts + age computations don't need to
// decrypt per row. composeDob reassembles "YYYY-MM-DD" for the legacy
// frontend code (ClientForm date inputs, age helpers in deskte-full.jsx)
// that still works in full-date strings.
//
// The legacy plaintext `dateOfBirth` column was dropped 2026-05-20 after
// the backfill encrypted 197 prod rows. The third argument is kept as a
// no-op fallback for old in-flight payloads still sitting in browser
// tabs — once everyone reloads, only (monthDay, year) remains.
function composeDob(monthDay, year, legacyFull) {
  if (monthDay && (year || year === 0)) {
    // Year stored as int — pad just in case it's 1-3 digits (won't be in
    // practice, but defensive).
    const y = String(year).padStart(4, '0');
    return `${y}-${monthDay}`;
  }
  if (legacyFull) {
    // Use LOCAL date parts — toISOString() is UTC and shifts a day in the
    // evening for users behind UTC (feedback_local_day_not_utc).
    if (legacyFull instanceof Date) return `${legacyFull.getFullYear()}-${String(legacyFull.getMonth() + 1).padStart(2, '0')}-${String(legacyFull.getDate()).padStart(2, '0')}`;
    if (typeof legacyFull === 'string') return legacyFull.split('T')[0];
  }
  return '';
}

// ── Clients ──────────────────────────────────────────────────────────────────

// Backend returns: { id, firstName, lastName, email, phone, city, state,
//   status: 'ACTIVE'|'INACTIVE'|..., tags, createdAt,
//   agent: {id, displayName}, _count: { policies } }
//
// Frontend SEED shape uses: { id, firstName, lastName, email, phone, city,
//   state, status: 'active'|'inactive', tags, agentId, ... }
function mapClientFromApi(c) {
  // Address fields are persisted on the backend as `address` / `city` /
  // `state` / `zipCode`, but the legacy ClientForm + ClientDetail still
  // read `addressStreet` / `addressCity` / `addressState` / `addressZip`.
  // Surface BOTH names so the form can prefill on edit and the detail
  // view can render the address after a page reload (otherwise the data
  // is in the DB but invisible to the UI). Same idea for `dob` ↔ DOB.
  // Per-person coverage toggles (cov_* keys) live in the `coverage` JSON
  // column: { primary: {...}, spouse: {...} }. Spread primary onto the
  // top-level client and spouse onto the spouse object so the edit form's
  // toggles prefill and memberHasCoverage() reads them inline. Dependents
  // keep their cov_* inside their own JSON (already spread via ...d below).
  const coverage = (c.coverage && typeof c.coverage === 'object') ? c.coverage : {};
  return {
    ...(coverage.primary && typeof coverage.primary === 'object' ? coverage.primary : {}),
    id: c.id,
    firstName: c.firstName,
    middleName: c.middleName || '',
    lastName: c.lastName,
    email: c.email || '',
    phone: formatPhoneUS(c.phone || ''),
    phoneSecondary: formatPhoneUS(c.phoneSecondary || ''),
    address: c.address || '',
    addressStreet: c.address || '',
    city: c.city || '',
    addressCity: c.city || '',
    state: c.state || '',
    addressState: c.state || '',
    zipCode: c.zipCode || '',
    addressZip: c.zipCode || '',
    county: c.county || '',
    dob: composeDob(c.dateOfBirthMonthDay, c.dateOfBirthYear, c.dateOfBirth),
    status: (c.status || 'ACTIVE').toLowerCase(),
    // LISTA PARA SOMETER. Es una MARCA aparte y no un valor de `status`: la
    // persona sigue siendo prospecto o referido y no se mueve de su lista —
    // solo lleva su etiqueta. `readyToSubmitAt` es cuando se marcó (null = no
    // está marcada); se expone también como booleano porque es lo que miran
    // la etiqueta y el desplegable, y que cada pantalla haga su propio
    // `!= null` es como se cuelan las diferencias.
    readyToSubmitAt: c.readyToSubmitAt || null,
    readyToSubmit: !!c.readyToSubmitAt,
    tags: c.tags || [],
    attendedBySeatId: c.attendedBySeatId || '',
    // El nombre de quien atiende cuando esa persona todavia no tiene asiento
    // (lo trajo una importacion). El chip lo ensena en gris; en cuanto se elige
    // un asiento de verdad, el servidor lo borra.
    attendedByName: c.attendedByName || '',
    source: c.source || '',
    preferredLanguage: c.preferredLanguage || 'en',
    // Legacy alias: ClientForm + the Clients list 🇪🇸 ES badge + the
    // greeting templates all branch on `preferredLang: 'spanish'|'english'`.
    // Without this alias, API-hydrated clients never get the badge or the
    // Spanish greeting templates because that field is undefined.
    preferredLang: (c.preferredLanguage === 'es') ? 'spanish' : 'english',
    agentId: c.agent?.id || null,
    agentName: c.agent?.displayName || '',
    // "Agente asignado" — editable display label. Falls back to the owning
    // agent's name so existing clients show an agent until one is set.
    assignedAgent: c.assignedAgent || c.agent?.displayName || '',
    policyCount: c._count?.policies ?? 0,
    // Backend returns the masked SSN ("***-**-1234") when the client has
    // one stored. The encrypted value is never sent to the frontend; full
    // SSN reveal goes through /clients/:id/reveal-ssn with a PIN. The
    // companion `hasSsn` flag is true whenever the row has SSN on file,
    // even if `ssnMasked` is null because the decrypt threw — the UI
    // uses it to show a "stored but unreadable" warning instead of
    // pretending there's no SSN at all (which is how the bug from
    // 2026-05-14 hid the field for weeks). `ssnDecryptError` is the
    // explicit signal.
    ssnMasked: c.ssnMasked || '',
    hasSsn: !!c.hasSsn,
    ssnDecryptError: !!c.ssnDecryptError,
    // Household + life context (server-side as of 2026-05-11; legacy
    // ClientForm reads these exact keys, so no remapping needed).
    maritalStatus: c.maritalStatus || '',
    gender: c.gender || '',
    immigrationStatus: c.immigrationStatus || '',
    employer: c.employer || '',
    employerPhone: c.employerPhone || '',
    employmentType: c.employmentType || '',
    employmentDesc: c.employmentDesc || '',
    annualIncome: c.annualIncome || '',
    // Los ingresos de MAS del titular (crm-app/otrosIngresos.js). Solo cuando
    // el servidor manda la columna: el listado no la trae, y una lista vacia
    // inventada aqui borraria la guardada al salvar una ficha abierta desde la
    // lista (la trampa de siempre, ver la hidratacion del formulario).
    ...(c.otrosIngresos !== undefined ? { otrosIngresos: limpiarOtrosIngresos(c.otrosIngresos && c.otrosIngresos.titular) } : {}),
    householdSize: c.householdSize != null ? String(c.householdSize) : '',
    insuredMembers: c.insuredMembers != null ? String(c.insuredMembers) : '',
    // Referral metadata — the legacy form uses ref* short names; expose
    // both so prefill works whether the form reads ref* or referredBy*.
    refFirstName: c.referredByFirstName || '',
    refLastName: c.referredByLastName || '',
    refPhone: formatPhoneUS(c.referredByPhone || ''),
    referredByFirstName: c.referredByFirstName || '',
    referredByLastName: c.referredByLastName || '',
    referredByPhone: formatPhoneUS(c.referredByPhone || ''),
    // Hard link to the client who referred this one (used by the profile's
    // "Referido" list and the Prospects "Referido por" badge).
    referredByClientId: c.referredByClientId || '',
    // Adult-dependent → head-of-household link. Backend-persisted as of
    // 2026-05-15 (was localStorage-only, so the "↗ <parent>" badge used
    // to vanish on reload / client merge).
    linkedParentId: c.linkedParentId || '',
    linkedParentName: c.linkedParentName || '',
    // External cloud-storage folder link (Drive/OneDrive/Dropbox/etc).
    driveUrl: c.driveUrl || '',
    // Free-text agent notes. Decrypted server-side (notesEncrypted) only on
    // scoped detail reads, so this is populated on GET /:id, create + update
    // responses — same flow as the F4 fields below.
    notes: c.notes || '',
    // F4 — Expanded ficha (principal). Decrypted server-side via
    // decryptF4FieldsWithAudit before sending; ciphertext columns are
    // stripped. Bundle fields are `{tags: string[], notes: string}` (or
    // null when not set yet) — keep the shape stable so the form can do
    // `value?.tags ?? []` without nullish checks everywhere.
    placeOfBirth: c.placeOfBirth || '',
    driverLicenseNumber: c.driverLicenseNumber || '',
    driverLicenseState: c.driverLicenseState || '',
    heightInches: typeof c.heightInches === 'number' ? c.heightInches : '',
    weightLbs: typeof c.weightLbs === 'number' ? c.weightLbs : '',
    smoker: c.smoker || '',
    medicaid: c.medicaid || '',
    hasDui: !!c.hasDui,
    duiDetails: c.duiDetails || '',
    hasFelony: !!c.hasFelony,
    felonyDetails: c.felonyDetails || '',
    hasTicket: !!c.hasTicket,
    ticketDetails: c.ticketDetails || '',
    medicalConditions: c.medicalConditions || { tags: [], notes: '' },
    familyHealthHistory: c.familyHealthHistory || { tags: [], notes: '' },
    // Immigration document details (principal). Decrypted server-side; default
    // to the empty 4-key shape so the form reads without nullish guards.
    immigrationDocs: c.immigrationDocs || { uscisNumber: '', cardNumber: '', category: '', issueDate: '', expirationDate: '', bornCitizen: '', naturalizationNumber: '', naturalizationDate: '' },
    // Custom client tabs — per-client VALUES map { fieldId: value }. The
    // tab/field DEFINITIONS are agency-shared (agency-settings.customTabDefs).
    // Default to {} so the form/detail can always read without nullish guards.
    customValues: (c.customValues && typeof c.customValues === 'object' && !Array.isArray(c.customValues)) ? c.customValues : {},
    // Spouse — collapse the backend scalars back into the nested `spouse`
    // object the legacy form expects. Phone is still client-side only.
    // SSN is sent back as `spouseSsnMasked` (e.g. `***-**-1234`) so the
    // form can show the last 4 without a PIN-gated decrypt; the full
    // SSN is revealed via /clients/:id/reveal-ssn (PIN-gated) once that
    // endpoint accepts a `target=spouse` parameter.
    spouse: {
      ...(coverage.spouse && typeof coverage.spouse === 'object' ? coverage.spouse : {}),
      firstName: c.spouseFirstName || '',
      middleName: c.spouseMiddleName || '',
      lastName: c.spouseLastName || '',
      dob: composeDob(c.spouseDateOfBirthMonthDay, c.spouseDateOfBirthYear, c.spouseDateOfBirth),
      employer: c.spouseEmployer || '',
      employerPhone: c.spouseEmployerPhone || '',
      gender: c.spouseGender || '',
      employmentType: c.spouseEmploymentType || '',
      employmentDesc: c.spouseEmploymentDesc || '',
      immigrationStatus: c.spouseImmigrationStatus || '',
      income: c.spouseAnnualIncome || '',
      ...(c.otrosIngresos !== undefined ? { otrosIngresos: limpiarOtrosIngresos(c.otrosIngresos && c.otrosIngresos.conyuge) } : {}),
      phone: formatPhoneUS(c.spousePhone || ''),
      email: c.spouseEmail || '',
      ssn: '',
      ssnMasked: c.spouseSsnMasked || '',
      ssnLast4: c.spouseSsnLast4 || '',
      // F4 — Spouse mirror of the principal expanded ficha. Same shape.
      placeOfBirth: c.spousePlaceOfBirth || '',
      driverLicenseNumber: c.spouseDriverLicenseNumber || '',
      driverLicenseState: c.spouseDriverLicenseState || '',
      heightInches: typeof c.spouseHeightInches === 'number' ? c.spouseHeightInches : '',
      weightLbs: typeof c.spouseWeightLbs === 'number' ? c.spouseWeightLbs : '',
      smoker: c.spouseSmoker || '',
      medicaid: c.spouseMedicaid || '',
      hasDui: !!c.spouseHasDui,
      duiDetails: c.spouseDuiDetails || '',
      hasFelony: !!c.spouseHasFelony,
      felonyDetails: c.spouseFelonyDetails || '',
      hasTicket: !!c.spouseHasTicket,
      ticketDetails: c.spouseTicketDetails || '',
      medicalConditions: c.spouseMedicalConditions || { tags: [], notes: '' },
      familyHealthHistory: c.spouseFamilyHealthHistory || { tags: [], notes: '' },
      immigrationDocs: c.spouseImmigrationDocs || { uscisNumber: '', cardNumber: '', category: '', issueDate: '', expirationDate: '', bornCitizen: '', naturalizationNumber: '', naturalizationDate: '' },
    },
    // Dependents — backend returns the JSON array as-is. Normalize each
    // entry's `dob` to YYYY-MM-DD. H5: prefer the encrypted-split fields
    // the backend now writes per-dependent; fall back to the legacy
    // string for old rows that pre-date the backfill.
    // F4: serializeDependentsForDetail already decrypted per-dep blobs
    // into plaintext siblings; default the medical/family bundles to the
    // empty shape so the form can render without nullish guards.
    dependents: Array.isArray(c.dependents)
      ? c.dependents.map((d, i) => ({
          ...d,
          // Guarantee a STABLE non-empty id. Legacy / imported dependents
          // were stored without one, which made the PIN-gated SSN modal POST
          // /update-ssn with no dependentId → 400 "dependentId is required".
          // The id is deterministic by index so the SAME dependent gets the
          // SAME id across the list, detail and form reads (so hydration
          // matches by id). It becomes a real persisted id on the next save.
          id: (d && d.id) ? d.id : ('dep_' + i),
          phone: formatPhoneUS(d.phone || ''),
          dob: composeDob(d.dateOfBirthMonthDay, d.dateOfBirthYear, d.dob),
          placeOfBirth: d.placeOfBirth || '',
          driverLicenseNumber: d.driverLicenseNumber || '',
          driverLicenseState: d.driverLicenseState || '',
          heightInches: typeof d.heightInches === 'number' ? d.heightInches : '',
          weightLbs: typeof d.weightLbs === 'number' ? d.weightLbs : '',
          hasDui: !!d.hasDui,
          duiDetails: d.duiDetails || '',
          hasFelony: !!d.hasFelony,
          felonyDetails: d.felonyDetails || '',
          hasTicket: !!d.hasTicket,
          ticketDetails: d.ticketDetails || '',
          medicalConditions: d.medicalConditions || { tags: [], notes: '' },
          familyHealthHistory: d.familyHealthHistory || { tags: [], notes: '' },
          immigrationDocs: d.immigrationDocs || { uscisNumber: '', cardNumber: '', category: '', issueDate: '', expirationDate: '', bornCitizen: '', naturalizationNumber: '', naturalizationDate: '' },
        }))
      : [],
    createdAt: c.createdAt,
    // Optimistic-concurrency base version. The edit form captures this when it
    // loads and sends it back as baseUpdatedAt so the backend can reject a
    // save that would silently overwrite a teammate's concurrent edit.
    updatedAt: c.updatedAt,
  };
}

// Backend Zod schema rejects rows with values outside its enums (status,
// language, marital, immigration). For imports especially, sources tend to
// use human-readable variants ("Married", "US Citizen", "english"); we
// normalize at the boundary so the upload succeeds instead of 400-ing the
// whole row. If we can't confidently map a value, drop it (set to null)
// rather than block — the row is still useful even without that field.

// EXPORTADOS DESDE EL 2026-09-14 (antes eran privados de este archivo).
//
// El importador de SOLO POLIZAS pasó a poder mapear los datos del cliente para
// las fichas que crea (Lissette: *"necesito mapear para esos nuevos clientes
// toda la información como si fuera cliente"*), y esas columnas traen el mismo
// texto suelto que las del importador de clientes: "Casado", "Married",
// "Permiso de trabajo", "EAD"… Sin pasar por estos mapas, el valor se guarda
// crudo, la ficha lo enseña vacío porque no coincide con ninguna opción del
// desplegable, y nadie ve un error. Copiarlos habría sido tener dos
// vocabularios del mismo campo separándose con el tiempo.
export const STATUS_MAP = {
  active: 'ACTIVE',
  inactive: 'INACTIVE',
  prospect: 'PROSPECT',
  referral: 'REFERRAL', // referred lead — kept distinct from a cold prospect
  pending: 'PROSPECT', // closest backend equivalent — "still a lead, not yet a customer"
  lead: 'PROSPECT',
  archived: 'INACTIVE',
  cancelled: 'INACTIVE',
  // Spanish variants — imported sheets from Latino agencies use these.
  activo: 'ACTIVE', activa: 'ACTIVE',
  inactivo: 'INACTIVE', inactiva: 'INACTIVE',
  prospecto: 'PROSPECT',
  referido: 'REFERRAL', referida: 'REFERRAL',
  pendiente: 'PROSPECT',
};

export const LANG_MAP = {
  en: 'en', english: 'en', 'en-us': 'en', english_us: 'en',
  es: 'es', spanish: 'es', español: 'es', espanol: 'es',
};

// Mirrors MARITAL_STATUSES on the backend. Anything not in the map → null.
export const MARITAL_MAP = {
  single: 'single', soltero: 'single', soltera: 'single',
  married: 'married', casado: 'married', casada: 'married',
  widowed: 'widowed', widow: 'widowed', viudo: 'widowed', viuda: 'widowed',
  divorced: 'divorced', divorciado: 'divorced', divorciada: 'divorced',
  separated: 'separated', separado: 'separated', separada: 'separated',
  domestic_partner: 'domestic_partner', 'domestic partner': 'domestic_partner',
};

// Mirrors GENDERS on the backend ('Male' | 'Female') — the same capitalization
// the Marketplace quoter already uses, so a saved client feeds a quote with no
// translation. Accepts the Spanish words and the bare initials because imports
// arrive in every shape; anything else → null rather than failing the save.
// Fumador. Acepta lo que teclee la agente o traiga un import ('Si', 'Yes',
// 'fumador'...) y lo reduce a los dos valores que guarda el backend. Sin
// coincidencia -> null, que significa 'no se ha preguntado', NO 'no fuma'.
const SMOKER_MAP = {
  yes: 'yes', si: 'yes', 'sí': 'yes', y: 'yes', s: 'yes', true: 'yes', fumador: 'yes', fumadora: 'yes', smoker: 'yes', fuma: 'yes',
  no: 'no', n: 'no', false: 'no', 'no fuma': 'no', 'non-smoker': 'no', 'no smoker': 'no', nofumador: 'no',
};
// MEDICAID: los mismos dos valores, con las palabras que escribe la gente. Se
// separa de SMOKER_MAP a proposito -- "fumador" no significa nada aqui, y
// mezclarlos haria que un importador con la columna equivocada colara un valor.
const MEDICAID_MAP = {
  yes: 'yes', si: 'yes', 'sí': 'yes', y: 'yes', s: 'yes', true: 'yes', medicaid: 'yes', 'tiene medicaid': 'yes', 'con medicaid': 'yes',
  no: 'no', n: 'no', false: 'no', 'no tiene': 'no', 'sin medicaid': 'no', 'no medicaid': 'no',
};

export const GENDER_MAP = {
  male: 'Male', m: 'Male', masculino: 'Male', hombre: 'Male', h: 'Male',
  female: 'Female', f: 'Female', femenino: 'Female', mujer: 'Female',
};

// El mapa de formas conocidas y la regla de "como se guarda un estatus" viven
// en el catalogo (estatusMigratorio.js), que es el unico sitio donde se escribe
// esta lista. Se reexporta con el nombre de siempre: PoliciesView.jsx lo
// importa de aqui para leer la columna de una hoja de polizas.
export { IMMIGRATION_MAP };

// Strings CSV exporters love to use as placeholders for empty cells. Treat
// any of these as null — they aren't real data and would either fail the
// backend's stricter validators (email, dob) or pollute the record.
const JUNK_PLACEHOLDERS = new Set([
  '', '-', '--', '---', 'n/a', 'na', 'none', 'null', 'tbd', 'pending',
  '?', '???', 'unknown', 'desconocido', 'pendiente', 'xxx', 'xxx-xx-xxxx',
  '000-00-0000', '0', 'no aplica', 'no aplica.',
]);

function isJunk(value) {
  if (value === null || value === undefined) return true;
  const s = String(value).trim().toLowerCase();
  return JUNK_PLACEHOLDERS.has(s);
}

export function normalizeEnum(value, map) {
  if (value === null || value === undefined) return null;
  // The map is consulted BEFORE the junk heuristic: 'pending'/'pendiente' are
  // both CSV junk placeholders AND the legitimate "En Proceso / Pending"
  // immigration status — junk-filtering first made that option impossible to
  // save (it silently became null on every save, 2026-07-31). Anything not in
  // the map maps to null anyway, so the junk list adds nothing here.
  const k = String(value).toLowerCase().trim();
  return map[k] || null;
}

// CSV DOBs frequently arrive as MM/DD/YYYY or M/D/YYYY. The backend
// Zod schema rejects anything `new Date(s)` can't parse, which is strict
// enough that locale-ambiguous values, 2-digit years, or junk strings
// kill the whole row. Normalize to ISO YYYY-MM-DD when we recognize a
// US-style date; for anything we can't confidently parse, return null
// (drop the field) so the row still saves — the user can fix the date
// in the client's detail view later.
function normalizeDob(value) {
  if (isJunk(value)) return null;
  const s = String(value).trim();
  // Already ISO (YYYY-MM-DD or full ISO timestamp): leave alone.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // A first number >12 can never be a month — the cell is day-first (DD/MM).
  // Swap so the date survives instead of producing an invalid month that the
  // backend rejects and silently drops (Wendy Cercio import, 2026-08-02).
  const dayFirstFix = (mm, dd) => (parseInt(mm, 10) > 12 && parseInt(dd, 10) <= 12) ? [dd, mm] : [mm, dd];
  // MM/DD/YYYY or M/D/YYYY → ISO
  let m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) {
    const [mm, dd] = dayFirstFix(m[1], m[2]);
    return `${m[3]}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  // MM/DD/YY → assume 1900s for years >= 30, 2000s otherwise (insurance
  // clients are mostly adults; this matches the typical convention).
  m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})$/);
  if (m) {
    const [mm, dd] = dayFirstFix(m[1], m[2]);
    const yyyy = parseInt(m[3], 10) >= 30 ? `19${m[3]}` : `20${m[3]}`;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  // Anything else: let JS try. If it fails, drop the field rather than
  // reject the row.
  const t = new Date(s).getTime();
  if (isNaN(t)) return null;
  // Reject epoch-0-ish placeholder dates (year < 1900 is almost always
  // a parsing accident).
  const year = new Date(t).getUTCFullYear();
  if (year < 1900 || year > 2100) return null;
  return new Date(t).toISOString().slice(0, 10);
}

// Backend `optionalEmail` rejects anything non-empty that isn't a valid
// email — so junk like "TBD" or "—" sinks the whole row. Drop to null
// for anything that doesn't look like an email.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function normalizeEmail(value) {
  if (isJunk(value)) return null;
  const s = String(value).trim();
  return EMAIL_RE.test(s) ? s : null;
}

// Backend `optionalSSN` strips non-digits then requires 9 digits. Pre-
// strip and pre-validate so a malformed SSN ("XXX-XX-XXXX", "000-00-
// 0000", or just "?") drops to undefined instead of nuking the row.
function normalizeSsn(value) {
  if (isJunk(value)) return undefined;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 9) return undefined;
  // Reject all-zero / repeated-digit placeholders ("000000000",
  // "111111111", etc.) that are almost always sentinel values.
  if (/^(\d)\1{8}$/.test(digits)) return undefined;
  return digits;
}

// Backend `optionalInt` accepts numbers within a min/max range. Drop
// strings that can't be parsed, or out-of-range values, instead of
// failing the row.
function normalizeInt(value, min, max) {
  // A literal zero is DATA, not a placeholder: '0' sits in JUNK_PLACEHOLDERS
  // for free-text CSV cells, but 0 household members / $0 income are
  // meaningful (Medicaid eligibility). The range check below still applies
  // (2026-08-02).
  const isLiteralZero = value === 0 || String(value ?? '').trim() === '0';
  if (!isLiteralZero && isJunk(value)) return null;
  const n = Number(String(value).replace(/[^0-9.\-]/g, ''));
  if (isNaN(n)) return null;
  if (n < min || n > max) return null;
  return n;
}

// Like cleanStr but keeps a literal zero — for money-ish string fields
// (annualIncome) where "$0" is real data, not a CSV placeholder (2026-08-02).
function cleanMoneyStr(value) {
  if (value === 0 || String(value ?? '').trim() === '0') return '0';
  return cleanStr(value);
}

// Generic "either return the trimmed string or null" used for free-text
// fields. Treats placeholder strings (n/a, --, etc.) as null so they
// don't litter the DB.
function cleanStr(value) {
  if (isJunk(value)) return null;
  const s = String(value).trim();
  return s || null;
}

// Returned by checkRequiredNames() so callers (ImportView) can tell
// whether a row only uploaded because we filled in a placeholder for
// the required firstName / lastName fields. The placeholders look like
// "(falta nombre)" / "(falta apellido)" — they pass the backend's
// .min(1) check, and the Clients list highlights any row containing
// "(falta" with an amber badge so the user can find + correct them.
export const MISSING_FIRST_NAME = '(falta nombre)';
export const MISSING_LAST_NAME = '(falta apellido)';

export function checkRequiredNames(c) {
  const f = c.firstName == null ? '' : String(c.firstName).trim();
  const l = c.lastName == null ? '' : String(c.lastName).trim();
  return { firstMissing: f === '', lastMissing: l === '' };
}

// Legacy fixed coverage keys that pre-date the cov_<typeId> scheme.
const LEGACY_COV_KEYS = ['covHealth','covDental','covVision','covLife','covAuto','covHome','covMedicare','covSupp'];
// Collect the per-person coverage toggles (cov_* + legacy keys) off a member
// object so they can be persisted in the `coverage` JSON column. Returns only
// the truthy toggles to keep the payload small; an empty object if none.
function pickCoverage(m) {
  if (!m || typeof m !== 'object') return {};
  const out = {};
  for (const k of Object.keys(m)) {
    if ((k.startsWith('cov_') || LEGACY_COV_KEYS.includes(k)) && m[k]) out[k] = true;
  }
  return out;
}

function mapClientToApi(c) {
  // Read both new (`address`) and legacy (`addressStreet`) names. The
  // ClientForm in deskte-full.jsx still writes the legacy ones; without
  // this fallback the address fields silently round-trip to null on save.
  // The legacy form uses `preferredLang: "spanish"|"english"` while the
  // backend wants `preferredLanguage: 'es'|'en'`. Accept either.
  //
  // Every optional field is routed through a normalizer that returns null
  // (or undefined for `ssn` which is `optional()` but not `nullable()`)
  // when the value is junk or fails the backend's stricter validators
  // (email format, DOB parse, SSN length). This makes bulk imports of
  // imperfect CSVs upload row-by-row instead of dying on the whole row
  // because of one bad cell.
  //
  // For the two REQUIRED fields (firstName, lastName), substitute a
  // searchable placeholder instead of letting the row 400 — same
  // motivation as the optional normalizers, applied to the only fields
  // the backend won't accept as null. The Clients list flags these so
  // the user can fix them after the import.
  // The ClientForm's language <Select> writes `preferredLang` ("spanish"|
  // "english"); `preferredLanguage` ("es"|"en") is the API alias set at load
  // and is NOT updated by the form. Read the form field FIRST so the user's
  // change wins — `preferredLanguage || preferredLang` made the stale loaded
  // value win and silently dropped the edit (Lissette 2026-06-17: "el idioma
  // se borra"). E-019.
  const rawLang = c.preferredLang || c.preferredLanguage;
  const { firstMissing, lastMissing } = checkRequiredNames(c);
  return {
    firstName: firstMissing ? MISSING_FIRST_NAME : c.firstName,
    middleName: cleanStr(c.middleName),
    lastName: lastMissing ? MISSING_LAST_NAME : c.lastName,
    email: normalizeEmail(c.email),
    phone: cleanStr(c.phone),
    phoneSecondary: cleanStr(c.phoneSecondary),
    // PREFER the address* names — that's what the ClientForm binds to and
    // edits. mapClientFromApi sets BOTH `address` and `addressStreet` to the
    // same value on load, so when the user edits `addressStreet` the plain
    // `address` stays stale; reading `c.address || c.addressStreet` then
    // re-sent the OLD address and the edit never persisted (Rosangel Chirinos
    // 2026-05-28 — confirmed: address save logged no change). Use address* when
    // present (form path), fall back to the plain names for the import path.
    // `!== undefined` (not `||`) so clearing a field to '' is respected.
    address: cleanStr(c.addressStreet !== undefined ? c.addressStreet : c.address),
    city: cleanStr(c.addressCity !== undefined ? c.addressCity : c.city),
    state: cleanStr(c.addressState !== undefined ? c.addressState : c.state),
    zipCode: cleanStr(c.addressZip !== undefined ? c.addressZip : c.zipCode),
    county: cleanStr(c.county),
    dateOfBirth: normalizeDob(c.dob),
    preferredLanguage: normalizeEnum(rawLang, LANG_MAP) || 'en',
    source: cleanStr(c.source),
    // Solo va cuando quien llama lo pide: le dice al servidor que NO cree la
    // tarjeta de Seguimiento porque esta ficha viene de una importación, no de
    // un alta nueva. Sin esta línea el mapeo la descartaría y el servidor no se
    // enteraría — la trampa de la lista blanca de siempre.
    ...(c.skipOnboarding ? { skipOnboarding: true } : {}),
    // "Agente asignado" — only send when the form/caller carries it so a partial
    // patch never clears it.
    ...(c.assignedAgent !== undefined ? { assignedAgent: cleanStr(c.assignedAgent) || null } : {}),
    status: STATUS_MAP[(c.status || 'active').toString().toLowerCase()] || 'ACTIVE',
    tags: c.tags || [],
    ...(c.attendedBySeatId !== undefined ? { attendedBySeatId: c.attendedBySeatId || null } : {}),
    ...(c.attendedByName !== undefined ? { attendedByName: cleanStr(c.attendedByName) || null } : {}),
    ssn: normalizeSsn(c.ssn),
    // Household + life context — drop unrecognized enum values so the row
    // still uploads. The user can fix the field later in the detail view.
    maritalStatus: normalizeEnum(c.maritalStatus, MARITAL_MAP),
    gender: normalizeEnum(c.gender, GENDER_MAP),
    spouseGender: normalizeEnum(c.spouse && c.spouse.gender, GENDER_MAP),
    // Acepta tambien el estatus que se invento la agencia. Ver la cabecera de
    // `estatusMigratorioParaGuardar`: aqui se borraba en silencio.
    immigrationStatus: estatusMigratorioParaGuardar(c.immigrationStatus),
    employer: cleanStr(c.employer),
    employerPhone: cleanStr(c.employerPhone),
    employmentType: cleanStr(c.employmentType),
    employmentDesc: cleanStr(c.employmentDesc),
    annualIncome: cleanMoneyStr(c.annualIncome),
    // Ingresos de mas de titular y conyuge. Solo si quien llama los TRAE (el
    // formulario, ya hidratado): un objeto de la lista no los tiene, y mandar
    // listas vacias por defecto borraria las guardadas.
    ...((Array.isArray(c.otrosIngresos) || Array.isArray(c.spouse && c.spouse.otrosIngresos))
      ? { otrosIngresos: { titular: limpiarOtrosIngresos(c.otrosIngresos), conyuge: limpiarOtrosIngresos(c.spouse && c.spouse.otrosIngresos) } }
      : {}),
    householdSize: normalizeInt(c.householdSize, 0, 50),
    // "Miembros a asegurar" was removed from the client editor (#744) but the
    // column still exists. Only send it when the caller actually carries a real
    // value, so a Save-before-hydration race (form not yet hydrated) never nulls
    // the stored count — same conditional-send pattern as assignedAgent above
    // (Lissette 2026-06-17: History showed "Miembros Asegurados 1 → —"). E-012.
    ...(normalizeInt(c.insuredMembers, 0, 50) != null ? { insuredMembers: normalizeInt(c.insuredMembers, 0, 50) } : {}),
    // Per-person coverage toggles for principal + spouse. Dependents keep
    // their cov_* inside the dependents JSON (passed through below), so we
    // only persist principal + spouse here. The detail/list serializers spread
    // coverage.primary / coverage.spouse back onto the form on load.
    coverage: { primary: pickCoverage(c), spouse: pickCoverage(c.spouse) },
    // Referral — accept both the legacy ref* names and the new long names.
    referredByFirstName: cleanStr(c.referredByFirstName || c.refFirstName),
    referredByLastName: cleanStr(c.referredByLastName || c.refLastName),
    referredByPhone: cleanStr(c.referredByPhone || c.refPhone),
    referredByClientId: cleanStr(c.referredByClientId),
    // Persist the adult-dependent → head-of-household link so the
    // "↗ <parent>" badge survives reloads and merges.
    linkedParentId: cleanStr(c.linkedParentId),
    linkedParentName: cleanStr(c.linkedParentName),
    // Free-text agent notes. Safe to send because the ClientForm hydrates the
    // full record (incl. decrypted notes) before Save and refuses to save a
    // partial form — so this never wipes stored notes the way driveUrl could.
    notes: cleanStr(c.notes),
    // NOTE: driveUrl is intentionally NOT sent here. The main ClientForm has
    // no driveUrl field, so c.driveUrl is whatever happened to be loaded
    // (often empty when the form opened from the clients list, which omits
    // it). Sending it would let a routine Save (e.g. editing Marital Status)
    // overwrite drive_url with null — exactly the wipe that hit Lissette's
    // client 2026-05-28. driveUrl is owned EXCLUSIVELY by PUT /clients/:id/drive
    // (setClientDriveUrl). See [[feedback_partial_patch_and_repair_cascade]].
    // F4 — Expanded ficha (principal). Backend re-validates with the
    // Zod schema; we just pass plain values through. Backend will encrypt
    // license-number / DUI/felony details / medical+family bundles before
    // persistence. Empty values are sent as null/'' so the backend can
    // clear the field cleanly (Zod treats '' on optionalStr as null).
    placeOfBirth: cleanStr(c.placeOfBirth),
    driverLicenseNumber: cleanStr(c.driverLicenseNumber),
    driverLicenseState: cleanStr(c.driverLicenseState),
    heightInches: normalizeInt(c.heightInches, 0, 120),
    weightLbs: normalizeInt(c.weightLbs, 0, 1500),
    smoker: normalizeEnum(c.smoker, SMOKER_MAP),
    medicaid: normalizeEnum(c.medicaid, MEDICAID_MAP),
    hasDui: !!c.hasDui,
    duiDetails: cleanStr(c.duiDetails),
    hasFelony: !!c.hasFelony,
    felonyDetails: cleanStr(c.felonyDetails),
    hasTicket: !!c.hasTicket,
    ticketDetails: cleanStr(c.ticketDetails),
    medicalConditions: normalizeMedicalBundle(c.medicalConditions),
    familyHealthHistory: normalizeMedicalBundle(c.familyHealthHistory),
    // Immigration document details (principal). Backend encrypts the USCIS /
    // card numbers; category + expiration ride in the same blob.
    immigrationDocs: normalizeImmigrationDocs(c.immigrationDocs),
    // Custom client tabs — per-client VALUES map { fieldId: value }, encrypted
    // server-side. Definitions are agency-shared, not sent per client.
    customValues: normalizeCustomValues(c.customValues),
    // Spouse — split the legacy `spouse` object into the backend scalars.
    // Phone is intentionally dropped (not on the backend yet). SSN gets
    // sent under `spouseSsn`; the backend encrypts it at rest.
    spouseFirstName: cleanStr(c.spouse?.firstName),
    spouseMiddleName: cleanStr(c.spouse?.middleName),
    spouseLastName: cleanStr(c.spouse?.lastName),
    spousePhone: cleanStr(c.spouse?.phone),
    spouseEmail: normalizeEmail(c.spouse?.email),
    spouseDateOfBirth: normalizeDob(c.spouse?.dob),
    spouseEmployer: cleanStr(c.spouse?.employer),
    spouseEmployerPhone: cleanStr(c.spouse?.employerPhone),
    spouseEmploymentType: cleanStr(c.spouse?.employmentType),
    spouseEmploymentDesc: cleanStr(c.spouse?.employmentDesc),
    spouseImmigrationStatus: estatusMigratorioParaGuardar(c.spouse?.immigrationStatus),
    spouseAnnualIncome: cleanMoneyStr(c.spouse?.income),
    spouseSsn: normalizeSsn(c.spouse?.ssn),
    // F4 — Spouse mirror.
    spousePlaceOfBirth: cleanStr(c.spouse?.placeOfBirth),
    spouseDriverLicenseNumber: cleanStr(c.spouse?.driverLicenseNumber),
    spouseDriverLicenseState: cleanStr(c.spouse?.driverLicenseState),
    spouseHeightInches: normalizeInt(c.spouse?.heightInches, 0, 120),
    spouseWeightLbs: normalizeInt(c.spouse?.weightLbs, 0, 1500),
    spouseSmoker: normalizeEnum(c.spouse?.smoker, SMOKER_MAP),
    spouseMedicaid: normalizeEnum(c.spouse?.medicaid, MEDICAID_MAP),
    spouseHasDui: !!c.spouse?.hasDui,
    spouseDuiDetails: cleanStr(c.spouse?.duiDetails),
    spouseHasFelony: !!c.spouse?.hasFelony,
    spouseFelonyDetails: cleanStr(c.spouse?.felonyDetails),
    spouseHasTicket: !!c.spouse?.hasTicket,
    spouseTicketDetails: cleanStr(c.spouse?.ticketDetails),
    spouseMedicalConditions: normalizeMedicalBundle(c.spouse?.medicalConditions),
    spouseFamilyHealthHistory: normalizeMedicalBundle(c.spouse?.familyHealthHistory),
    spouseImmigrationDocs: normalizeImmigrationDocs(c.spouse?.immigrationDocs),
    // Dependents — pass the array through. The backend strips any
    // plaintext `ssn` field from each row, encrypts it, and stores the
    // ciphertext + last-4. Normalize each row's DOB + immigration so
    // one bad dependent doesn't fail the parent row.
    // F4: per-dep ficha fields ride along — backend's sanitize step
    // will encrypt the four sensitive ones and pass the rest through.
    dependents: Array.isArray(c.dependents)
      ? c.dependents.map((d, i) => ({
          ...d,
          // Persist a stable non-empty id so a dependent can always be
          // targeted by the PIN-gated /update-ssn endpoint afterwards
          // (legacy rows had none → "dependentId is required"). Deterministic
          // by index as a last-resort fallback; normal form deps already
          // carry an id from addDep / mapClientFromApi.
          id: (d && d.id) ? d.id : ('dep_' + i),
          dob: normalizeDob(d.dob),
          immigrationStatus: estatusMigratorioParaGuardar(d.immigrationStatus),
          ssn: normalizeSsn(d.ssn),
          placeOfBirth: cleanStr(d.placeOfBirth),
          driverLicenseNumber: cleanStr(d.driverLicenseNumber),
          driverLicenseState: cleanStr(d.driverLicenseState),
          heightInches: normalizeInt(d.heightInches, 0, 120),
          weightLbs: normalizeInt(d.weightLbs, 0, 1500),
          smoker: normalizeEnum(d.smoker, SMOKER_MAP),
          medicaid: normalizeEnum(d.medicaid, MEDICAID_MAP),
          hasDui: !!d.hasDui,
          duiDetails: cleanStr(d.duiDetails),
          hasFelony: !!d.hasFelony,
          felonyDetails: cleanStr(d.felonyDetails),
          hasTicket: !!d.hasTicket,
          ticketDetails: cleanStr(d.ticketDetails),
          medicalConditions: normalizeMedicalBundle(d.medicalConditions),
          familyHealthHistory: normalizeMedicalBundle(d.familyHealthHistory),
          immigrationDocs: normalizeImmigrationDocs(d.immigrationDocs),
          ...(Array.isArray(d.otrosIngresos) ? { otrosIngresos: limpiarOtrosIngresos(d.otrosIngresos) } : {}),
        }))
      : undefined,
  };
}

// F4 — Trim the wire shape to `{tags: string[], notes: string}` exactly so
// the backend's strict() Zod schema accepts it. Empty bundles round-trip as
// null which clears the column. Defensive against the form passing strings
// or arrays in the wrong slot from a stale localStorage migration.
function normalizeMedicalBundle(b) {
  if (!b || typeof b !== 'object') return null;
  const tags = Array.isArray(b.tags)
    ? b.tags.map((t) => (typeof t === 'string' ? t.trim() : '')).filter(Boolean)
    : [];
  const notes = typeof b.notes === 'string' ? b.notes.trim() : '';
  if (tags.length === 0 && !notes) return null;
  return { tags, notes };
}

// Immigration document details. Trim to exactly the four keys the backend's
// strict() Zod schema accepts ({uscisNumber, cardNumber, category,
// expirationDate}) as plain strings — never null, since the sub-keys are
// `.optional()` (string|undefined), not nullable. Empty strings round-trip and
// the backend stores null when every field is blank (clearing the column).
function normalizeImmigrationDocs(d) {
  // No field at all (CSV import, AI actions, any caller without the docs UI)
  // → send undefined so the backend carries the stored ciphertext forward.
  // Only a caller that actually HAS the docs object (the ClientForm, which
  // hydrates it from GET /:id before save) may send the 4-key shape — where
  // all-empty means an intentional clear. Sending an empty object for an
  // absent field was wiping the encrypted blob on every save (2026-07-31).
  if (d === null || d === undefined) return undefined;
  const o = typeof d === 'object' && !Array.isArray(d) ? d : {};
  const s = (v) => (typeof v === 'string' ? v.trim() : '');
  return {
    uscisNumber: s(o.uscisNumber),
    cardNumber: s(o.cardNumber),
    category: s(o.category),
    // Emisión y los tres de ciudadanía se añadieron el 2026-08-10, cuando el
    // formulario pasó a mostrar unas casillas u otras según el estatus. Se
    // envían SIEMPRE los ocho, aunque el estatus del momento solo enseñe
    // tres: el backend reescribe el blob cifrado entero en cada guardado, así
    // que una clave que no viaje se borra sin error ni aviso — es el mismo
    // fallo que se arregló aquí en julio, ahora con más campos que perder.
    issueDate: s(o.issueDate),
    expirationDate: s(o.expirationDate),
    bornCitizen: s(o.bornCitizen),
    naturalizationNumber: s(o.naturalizationNumber),
    naturalizationDate: s(o.naturalizationDate),
  };
}

// Custom-tab VALUES (per-client). Trim every value, drop empties, keep the
// field-id keys so the backend matches them against the agency-shared
// definitions. The backend re-validates + encrypts the map as one blob.
// Returns {} (never null) — the update handler treats `customValues: {}` as
// "clear the column".
function normalizeCustomValues(values) {
  if (!values || typeof values !== 'object' || Array.isArray(values)) return {};
  const out = {};
  for (const [k, v] of Object.entries(values)) {
    const key = typeof k === 'string' ? k.trim() : '';
    if (!key) continue;
    const val = typeof v === 'string' ? v.trim() : '';
    if (!val) continue;
    out[key] = val;
  }
  return out;
}

// FULL client rows for the Clientes-module Excel/CSV export — the regular
// list is deliberately light (no address/income/marital/spouse detail/notes),
// so exports built from it came out mostly empty. Returns decrypted ficha
// fields + notes + the Notes-tab thread for every client in scope.
// `format` ('excel' | 'csv') y `exported` (cuantas filas va a escribir la
// pantalla, ya recortadas por sus filtros) viajan solo para que queden en el
// historial de exportaciones: el servidor no puede saber ninguno de los dos.
//
// Pide el PIN (auditoría de seguridad 2026-09-27): va en el CUERPO de un POST,
// nunca en la URL, que queda escrita en los registros. Los errores llevan
// .code (PIN_WRONG, PIN_LOCKED, EXPORT_NOT_ALLOWED, EXPORT_RATE_LIMITED…).
export async function exportClientsFull({ format, exported, pin } = {}) {
  const r = await apiFetch('/api/v1/clients/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pin,
      format: format || null,
      exported: Number.isFinite(exported) ? exported : null,
    }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `exportClientsFull failed: ${r.status}`);
  return json.data;
}

// Quien descargo la cartera, cuando y cuanta. La duena de la agencia recibe
// todas las descargas; los demas roles, las suyas (`scope` lo dice).
export async function getClientExportHistory() {
  const r = await apiFetch('/api/v1/clients/export-history');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getClientExportHistory failed: ${r.status}`);
  return { data: json.data || [], scope: json.scope || 'mine' };
}
// Papelera de clientes. Borrar un cliente es SUAVE (deletedAt) y se lleva sus
// polizas y comisiones con la misma marca de tiempo, pero hasta 2026-08-19 no
// habia forma de deshacerlo desde la aplicacion (E-092).
export async function listDeletedClients(days = 90) {
  const r = await apiFetch(`/api/v1/clients/trash?days=${encodeURIComponent(days)}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listDeletedClients failed: ${r.status}`);
  return json.data || [];
}

// Devuelve al cliente y, con el, SOLO lo que cayo en el mismo borrado.
export async function restoreClient(id) {
  const r = await apiFetch(`/api/v1/clients/${encodeURIComponent(id)}/restore`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `restoreClient failed: ${r.status}`);
  return json.data || { id, policiesRestored: 0, commissionsRestored: 0 };
}

export async function listClients({
  page = 1,
  limit = 100,
  search,
  status,
  policyType,
  carrier,
  referredByClientId,
} = {}) {
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('limit', String(limit));
  if (search) qs.set('search', search);
  if (status) qs.set('status', status);
  if (referredByClientId) qs.set('referredByClientId', referredByClientId);
  // policyType is an array of FRONTEND ids (e.g. ['aca', 'life']). The
  // backend enum is uppercase + coarser (HEALTH, LIFE). Map and dedupe
  // before sending so the URL stays compact and the backend never sees
  // unknown values.
  if (Array.isArray(policyType) && policyType.length) {
    const backendTypes = [
      ...new Set(
        policyType
          .map((id) => POLICY_TYPE_FRONT_TO_BACK[id])
          .filter(Boolean),
      ),
    ];
    if (backendTypes.length) qs.set('policyType', backendTypes.join(','));
  }
  if (Array.isArray(carrier) && carrier.length) qs.set('carrier', carrier.join(','));
  const r = await apiFetch(`/api/v1/clients?${qs.toString()}`);
  if (!r.ok) throw new Error(`listClients failed: ${r.status}`);
  const json = await r.json();
  return {
    clients: (json.data || []).map(mapClientFromApi),
    pagination: json.pagination,
  };
}

// The referrals a given client gave us — the prospects whose
// referredByClientId points back to this client. The backend decrypts each
// referral's email / phone / note (a focused, authorized PHI read). Used by
// the "Referido" panel on the client profile. Returns a plain array of
// { id, firstName, lastName, email, phone, note, status, createdAt }.
export async function listClientReferralsGiven(clientId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/referrals`);
  if (!r.ok) throw new Error(`listClientReferralsGiven failed: ${r.status}`);
  const json = await r.json();
  return (json.data || []).map((x) => ({
    id: x.id,
    firstName: x.firstName || '',
    lastName: x.lastName || '',
    email: x.email || '',
    phone: formatPhoneUS(x.phone || ''),
    note: x.note || '',
    status: x.status || '',
    createdAt: x.createdAt || '',
  }));
}

// Detach a referral from the client who gave it: clears referredByClientId on
// the referral prospect so it drops off that client's "Referido" list. The
// prospect itself survives (it stays in Prospects & Referrals) — no data loss.
// Sends ONLY referredByClientId as '' (the backend PUT turns '' → null); does
// NOT go through mapClientToApi, which would default status/names on a partial
// patch and clobber the prospect.
export async function unlinkClientReferral(referralClientId) {
  const r = await apiFetch(`/api/v1/clients/${referralClientId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ referredByClientId: '' }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `unlinkClientReferral failed: ${r.status}`));
  }
  return true;
}

// Distinct policy types and carriers actually in use by this agency.
// Drives the Clients view filter dropdowns without forcing the SPA to load
// every policy. AGENT/SEAT see only what's reachable through their own
// clients (server enforces row-level visibility).
//
// Backend returns the raw enum (UPPERCASE). We translate to frontend ids
// (lowercase, e.g. 'aca', 'auto') so the UI can render them with the
// existing POLICY_TYPES icon/label catalog directly.
export async function getClientFilterOptions() {
  const r = await apiFetch('/api/v1/clients/filter-options');
  if (!r.ok) throw new Error(`getClientFilterOptions failed: ${r.status}`);
  const json = await r.json();
  const rawTypes = json.data?.policyTypes || [];
  const policyTypes = [
    ...new Set(
      rawTypes
        .map((t) => POLICY_TYPE_BACK_TO_FRONT[t])
        .filter(Boolean),
    ),
  ];
  return {
    policyTypes,
    carriers: json.data?.carriers || [],
    // QUIEN ATIENDE, para poder filtrar por ello sin tener la cartera cargada
    // (el constructor de reportes no la tiene). Llegan los asientos con su
    // nombre ya resuelto por el servidor —el mismo que sale en la columna del
    // Excel— y aparte los nombres sueltos de quien todavia no tiene asiento.
    atiende: {
      asientos: json.data?.atiende?.asientos || [],
      nombres: json.data?.atiende?.nombres || [],
    },
  };
}

// PIN status — does the current user (or their supervising agent if they
// are a seat) have a PIN configured? Drives the "PIN Security" panel and
// the SSN reveal modal's setup-prompt fallback. The auth namespace is
// mounted at /auth (NOT /api/v1/auth — see deskte-api index.ts:85).
export async function getPinStatus() {
  const r = await apiFetch('/auth/pin-status');
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getPinStatus failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Verify the current user's PIN (or their supervising agent's PIN if
// they're a seat). No PHI is decrypted — just answers "is this the right
// PIN?" — used by UI gates like the payment-methods tab. Failed attempts
// are audit-logged on the backend.
// Every PIN endpoint answers with more than a code: `remainingAttempts` on a
// wrong PIN, `retryAfterSec` once the 5-strike lock trips. Carrying both on
// the Error is what lets a modal say "te quedan 2 intentos" instead of a bare
// "PIN incorrecto" — the difference between a warning and a surprise 15-minute
// lockout that closes every SSN door in the CRM at once.
function pinError(json, fallback) {
  const e = new Error(json.error || fallback);
  e.code = json.code;
  if (typeof json.remainingAttempts === 'number') e.remainingAttempts = json.remainingAttempts;
  if (typeof json.retryAfterSec === 'number') e.retryAfterSec = json.retryAfterSec;
  return e;
}

export async function verifyPin(pin) {
  const r = await apiFetch('/auth/verify-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `verifyPin failed: ${r.status}`);
  return json;
}

// Set or rotate the agent's PIN.
//   - First-time setup: pass only newPin.
//   - Rotation: pass newPin + currentPin.
// Throws an error whose .code matches the backend code on validation failures.
export async function setPin({ newPin, currentPin }) {
  const r = await apiFetch('/auth/set-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newPin, currentPin: currentPin || undefined }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `setPin failed: ${r.status}`);
    e.code = json.code;
    throw e;
  }
  return json;
}

// Mails a 6-digit reset code to the user's MSAL email. Returns
// { expiresAt, sentTo } where sentTo is a masked address like
// "lis***@example.com" — never the full email, never the code.
// Backend enforces role gating (only AGENCY_OWNER / AGENT / SOLO_AGENT)
// and rejects with PIN_NOT_SET if there is no PIN to reset.
export async function requestPinReset() {
  const r = await apiFetch('/auth/request-pin-reset', { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `requestPinReset failed: ${r.status}`);
    e.code = json.code;
    throw e;
  }
  return json.data;
}

// Consumes the 6-digit code from the user's email and installs a new PIN.
// On success the reset slot is cleared; calling again with the same code
// returns NO_RESET_REQUEST. Backend writes pin.reset_completed (success)
// or pin.reset_denied (wrong code) to the audit log.
export async function resetPin({ code, newPin }) {
  const r = await apiFetch('/auth/reset-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, newPin }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `resetPin failed: ${r.status}`);
    e.code = json.code;
    throw e;
  }
  return json;
}

// ── Autenticador (Microsoft/Google Authenticator) ─────────────────────────
// Auditoría de seguridad 2026-09-27: el código por correo no protege a quien
// le roban el correo. Estas cinco llaman a /auth/mfa/* (deskte-api
// routes/autenticador.routes.ts). Los errores llevan .code, .remainingAttempts
// y .retryAfterSec igual que los del PIN (pinError).
async function mfaPost(ruta, body) {
  const r = await apiFetch(`/auth/mfa${ruta}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `mfa${ruta} failed: ${r.status}`);
  return json.data || {};
}

/** { enabled, verified, recoveryCodesLeft } */
export async function getMfaStatus() {
  const r = await apiFetch('/auth/mfa/status');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `getMfaStatus failed: ${r.status}`);
  return json.data;
}
/** Semilla nueva → { otpauthUrl, secret }. Aún no queda activo. */
export const startMfaEnroll = () => mfaPost('/enroll');
/** Primer código correcto → activo. Devuelve { recoveryCodes } UNA sola vez. */
export const confirmMfaEnroll = (code) => mfaPost('/enroll/confirm', { code });
/** Código de la app, o uno de recuperación → esta sesión pasa. */
export const verifyMfa = ({ code, recoveryCode }) => mfaPost('/verify', code ? { code } : { recoveryCode });
/** Quitarlo pide un código válido de la app. */
export const disableMfa = (code) => mfaPost('/disable', { code });
/** "Cerrar sesión en todas partes": echa a todos los navegadores de esta cuenta, este incluido. */
export async function logoutEverywhere() {
  const r = await apiFetch('/auth/logout-everywhere', { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `logoutEverywhere failed: ${r.status}`);
  return json;
}
/** Platform Admin: cerrar todas las sesiones de alguien cuya cuenta parece robada. */
export async function forceLogoutUser(userId) {
  const r = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/force-logout`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `forceLogoutUser failed: ${r.status}`);
  return json;
}
/** Solo la dueña de la plataforma: quitárselo a quien perdió el teléfono. */
export const resetMfaFor = (userId) => mfaPost(`/reset-for/${encodeURIComponent(userId)}`);

/** Platform Admin: { obligatorio, activos, conAutenticador } */
export async function leerAutenticadorObligatorio() {
  const r = await apiFetch('/api/v1/admin/autenticador-obligatorio');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `leerAutenticadorObligatorio failed: ${r.status}`);
  return json.data;
}
export async function cambiarAutenticadorObligatorio(valor) {
  const r = await apiFetch('/api/v1/admin/autenticador-obligatorio', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ valor: valor === true }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `cambiarAutenticadorObligatorio failed: ${r.status}`);
  return json.data;
}

// Reveal the full SSN of a client by submitting the agent's PIN.
// Backend audits both successes and failures.
// target: 'primary' (default) | 'spouse' — same endpoint, the spouse path
// hits client.spouseSsnEncrypted and writes an audit row with
// field='spouse_ssn' so the access log distinguishes the two.
// target: 'primary' | 'spouse' | 'dependent'. For 'dependent', pass the
// dependent's stable row id so the backend knows which one to decrypt.
export async function revealClientSsn(clientId, pin, target = 'primary', dependentId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/reveal-ssn`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin, target, ...(dependentId ? { dependentId } : {}) }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `revealClientSsn failed: ${r.status}`);
  return json.data; // { ssn, masked, target, dependentId? }
}

// PIN-gated correction of a stored SSN. target: 'primary' | 'spouse' |
// 'dependent' (pass dependentId for the last). `ssn` is the corrected
// 9-digit value, or '' to clear. Returns { masked, ssnLast4, hasSsn? }
// so the caller can refresh the masked display without a re-fetch. The
// PIN is the same one the agent just used to reveal the value; it is
// passed inline per call and never cached.
export async function updateClientSsn(clientId, pin, target, ssn, dependentId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/update-ssn`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin, target, ssn, ...(dependentId ? { dependentId } : {}) }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw pinError(json, `updateClientSsn failed: ${r.status}`);
  return json.data; // { target, masked, ssnLast4, hasSsn? }
}

// ---- Client payment methods ----------------------------------------------
// Cards / bank accounts on file to run premium payments. List/create/update
// return MASKED rows (last-4, brand, expiry — never the full number). The full
// card/account numbers come back only through revealClientPaymentMethod, gated
// by the same 6-digit PIN as the SSN/vault reveal. The card CVV is never stored
// server-side (PCI-DSS) — it is dropped on save.

export async function listClientPaymentMethods(clientId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/payment-methods`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listClientPaymentMethods failed: ${r.status}`);
  return json.data || [];
}

export async function createClientPaymentMethod(clientId, payload) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/payment-methods`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createClientPaymentMethod failed: ${r.status}`);
  return json.data;
}

export async function updateClientPaymentMethod(clientId, pmId, payload) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/payment-methods/${pmId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateClientPaymentMethod failed: ${r.status}`);
  return json.data;
}

export async function deleteClientPaymentMethod(clientId, pmId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/payment-methods/${pmId}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteClientPaymentMethod failed: ${r.status}`);
  return json;
}

// ── LOS MEDICOS DE LA CASA (punto 10, fase 1) ───────────────────────────────
//
// Se ven con `clients.view` y se editan con `clients.edit` —Lissette, 2026-09-16:
// *"que lo vean todos"*—, asi que aqui no hay PIN ni revelado: el servidor manda
// el detalle ya descifrado a quien puede abrir la ficha.
//
// `persona` es la clave de la persona de la casa ("primary", "spouse", el id del
// dependiente), la misma que `policyInsured`. Los nombres NO viajan: se ponen en
// pantalla cruzando con la ficha (`personasDelHogar.js`).

// Los proveedores médicos que la agencia ya registró en otras fichas, para
// elegirlos en vez de escribirlos otra vez (Fabiola Montano, E-405).
export async function getDoctorsDirectory(q = '') {
  const r = await apiFetch(`/api/v1/clients/doctors-directory${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getDoctorsDirectory failed: ${r.status}`);
  return json.data || [];
}

export async function listClientDoctors(clientId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/doctors`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listClientDoctors failed: ${r.status}`);
  return json.data || [];
}

// Devuelve { medico, recortados }: `recortados` son los campos que el servidor
// tuvo que cortar por largos. Se pasan tal cual para que la pantalla avise —un
// recorte callado es una mentira (regla de oro 83).
export async function createClientDoctor(clientId, payload) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/doctors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createClientDoctor failed: ${r.status}`);
  return { medico: json.data, recortados: json.recortados || [] };
}

export async function updateClientDoctor(clientId, doctorId, payload) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/doctors/${doctorId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateClientDoctor failed: ${r.status}`);
  return { medico: json.data, recortados: json.recortados || [] };
}

export async function deleteClientDoctor(clientId, doctorId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/doctors/${doctorId}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteClientDoctor failed: ${r.status}`);
  return json;
}

// ── Los medicamentos de la ficha (Lissette 2026-10-15) ──────────────────────
// Mismo molde que los medicos de arriba: por persona de la casa, el detalle
// cifrado en el servidor, y `recortados` para avisar de lo que se corto.

export async function listClientMedications(clientId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/medications`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listClientMedications failed: ${r.status}`);
  return json.data || [];
}

export async function createClientMedication(clientId, payload) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/medications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createClientMedication failed: ${r.status}`);
  return { medicamento: json.data, recortados: json.recortados || [] };
}

export async function updateClientMedication(clientId, medicationId, payload) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/medications/${medicationId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateClientMedication failed: ${r.status}`);
  return { medicamento: json.data, recortados: json.recortados || [] };
}

export async function deleteClientMedication(clientId, medicationId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/medications/${medicationId}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteClientMedication failed: ${r.status}`);
  return json;
}

/**
 * El buscador (RxTerms de la NLM, a traves del servidor). Nunca lanza:
 * devuelve `{ data, unavailable }` y la pantalla deja escribir a mano cuando
 * no hay respuesta.
 */
export async function buscarMedicamentos(q) {
  try {
    const r = await apiFetch(`/api/v1/medicamentos/buscar?q=${encodeURIComponent(q || '')}`);
    if (!r.ok) return { data: [], unavailable: true };
    const j = await r.json().catch(() => ({}));
    return { data: Array.isArray(j.data) ? j.data : [], unavailable: !!j.unavailable };
  } catch {
    return { data: [], unavailable: true };
  }
}

// ── LAS CITAS MEDICAS (punto 10, fase 2) ────────────────────────────────────
//
// Un solo juego de rutas para las dos pantallas: el bloque de la ficha pasa
// `clientId` y la lista global no. Mismos permisos que los medicos
// (`clients.view` / `clients.edit`), sin PIN.

// `filtros`: { clientId, status (csv), from, to, q, limit }.
// Devuelve { citas, hayMas } — `hayMas` dice que el servidor corto la lista, en
// vez de callarlo (regla de oro 83).
export async function listMedicalAppointments(filtros = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const cola = qs.toString();
  const r = await apiFetch(`/api/v1/medical-appointments${cola ? `?${cola}` : ''}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listMedicalAppointments failed: ${r.status}`);
  return { citas: json.data || [], hayMas: !!json.hayMas };
}

// Devuelve { cita, recortados } — los campos que el servidor tuvo que cortar.
export async function createMedicalAppointment(payload) {
  const r = await apiFetch('/api/v1/medical-appointments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createMedicalAppointment failed: ${r.status}`);
  // `confirmacion`: qué pasó con el correo al cliente si se pidió (null si no).
  return { cita: json.data, recortados: json.recortados || [], confirmacion: json.confirmacion || null };
}

export async function updateMedicalAppointment(id, payload) {
  const r = await apiFetch(`/api/v1/medical-appointments/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateMedicalAppointment failed: ${r.status}`);
  return { cita: json.data, recortados: json.recortados || [] };
}

// El desplegable de la lista: se marca el estatus sin abrir la cita.
export async function setMedicalAppointmentStatus(id, status) {
  const r = await apiFetch(`/api/v1/medical-appointments/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `setMedicalAppointmentStatus failed: ${r.status}`);
  return json.data;
}

export async function deleteMedicalAppointment(id) {
  const r = await apiFetch(`/api/v1/medical-appointments/${id}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteMedicalAppointment failed: ${r.status}`);
  return json;
}

// PIN-gated decrypt of the full card/account numbers for one method. The PIN is
// the same one the agent used to unlock the tab; passed inline, never cached.
// Returns { cardNumber, accountNumber, routingNumber, billingAddress, notes }.
export async function revealClientPaymentMethod(clientId, pmId, pin) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/payment-methods/${pmId}/reveal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `revealClientPaymentMethod failed: ${r.status}`);
    e.code = json.code;
    throw e;
  }
  return json.data;
}

// ---- Password / portal vault ---------------------------------------------
// The agent's carrier/portal logins, grouped by policy type. List/create/
// update/delete return masked rows (booleans, never secrets). Passwords and
// security answers come back only through revealVaultCredential, gated by the
// same 6-digit PIN as the SSN reveal.

// Las dos bóvedas usan las MISMAS rutas con otra raíz: la de la agencia y la
// PERSONAL de cada agente (solo él la ve; Lissette 2026-10-14). Cada función
// recibe la raíz al final y, si no se le pasa, es la de la agencia.
export const BOVEDA_AGENCIA = '/api/v1/vault';
export const BOVEDA_PERSONAL = '/api/v1/mi-boveda';

export async function listVault(base = BOVEDA_AGENCIA) {
  const r = await apiFetch(base);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listVault failed: ${r.status}`);
  return json.data || [];
}

export async function createVaultCredential(payload, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(base, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createVaultCredential failed: ${r.status}`);
  return json.data;
}

export async function updateVaultCredential(id, payload, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateVaultCredential failed: ${r.status}`);
  return json.data;
}

// Cambia el tipo de TODAS las entradas del dueño que lo usan (renombrar o
// "borrar" un tipo personalizado = pasarlas a otro). Devuelve { count }.
export async function renameVaultType(from, to, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/types/rename`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `renameVaultType failed: ${r.status}`);
  return json.data;
}

export async function deleteVaultCredential(id, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/${id}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteVaultCredential failed: ${r.status}`);
  return true;
}

// Append a comment to a credential's note thread. Author + timestamp are
// stamped server-side. Returns the updated credential (with noteThread).
// `files`: documentos ya subidos con uploadNoteAttachment ({ name, type, size,
// blobUrl }); van como referencia, no dentro de la nota (vault.routes.ts).
export async function addVaultNote(id, text, images, base = BOVEDA_AGENCIA, files = []) {
  const r = await apiFetch(`${base}/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text,
      images: Array.isArray(images) ? images : undefined,
      files: Array.isArray(files) && files.length ? files : undefined,
    }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `addVaultNote failed: ${r.status}`);
  return json.data;
}

// Delete one comment from a credential's note thread. Returns the updated credential.
export async function deleteVaultNote(id, noteId, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/${id}/notes/${noteId}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteVaultNote failed: ${r.status}`);
  return json.data;
}

// Pin/unpin one comment in a credential's note thread. Returns the updated credential.
export async function toggleVaultNotePin(id, noteId, pinned, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/${id}/notes/${noteId}/pin`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pinned: !!pinned }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `toggleVaultNotePin failed: ${r.status}`);
  return json.data;
}

// Change/access history for one credential: [{ id, action, by, at }] newest
// first. `action` is vault.create/update/delete/reveal/reveal_denied/pin.locked.
export async function getVaultHistory(id, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/${id}/history`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getVaultHistory failed: ${r.status}`);
  return json.data || [];
}

// PIN-gated reveal of the password + security answers. Throws an Error with
// .code (PIN_WRONG / PIN_LOCKED / PIN_NOT_SET / ...) on failure so the modal
// can show the right message — same shape as revealClientSsn.
export async function revealVaultCredential(id, pin, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/${id}/reveal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `revealVaultCredential failed: ${r.status}`);
    e.code = json.code;
    throw e;
  }
  return json.data; // { password, secA1, secA2, secA3 }
}

// Baúl completo con las contraseñas descifradas, para sacar una copia. Mismo
// PIN y mismo bloqueo por intentos que la revelación de una fila, y el servidor
// además lo rechaza para los asistentes y lo deja anotado en el historial.
// Devuelve las filas de la lista más `password` y `securityAnswers` (alineadas
// 1:1 con `security`). Lanza un Error con .code igual que revealVaultCredential.
export async function exportVaultWithPasswords(pin, base = BOVEDA_AGENCIA) {
  const r = await apiFetch(`${base}/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `exportVaultWithPasswords failed: ${r.status}`);
    e.code = json.code;
    throw e;
  }
  return json.data || [];
}

// ── Company directory (Company Information tab) ───────────────────────────────
// Plain reference data: carrier/portal phones, links, emails and training-video
// links. Server-backed, gated by the vault.* seat permissions. Every row carries
// a `history` array ([{action, by, byName, at}]) recording create/update/delete.

export async function listCompanyInfo() {
  const r = await apiFetch('/api/v1/company');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listCompanyInfo failed: ${r.status}`);
  return json.data || [];
}

export async function createCompanyInfo(payload) {
  const r = await apiFetch('/api/v1/company', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createCompanyInfo failed: ${r.status}`);
  return json.data;
}

// Bulk import — one request for the whole sheet. Returns { data, count }.
export async function bulkCreateCompanyInfo(rows) {
  const r = await apiFetch('/api/v1/company/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `bulkCreateCompanyInfo failed: ${r.status}`);
  return { data: json.data || [], count: json.count || 0 };
}

export async function updateCompanyInfo(id, payload) {
  const r = await apiFetch(`/api/v1/company/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateCompanyInfo failed: ${r.status}`);
  return json.data;
}

// Info de Compañías: cambiar el nombre de una categoría propia en todas sus
// filas, o pasarlas a otra ('' = Sin categoría). Como renameVaultType.
export async function renameCompanyCategory(from, to) {
  const r = await apiFetch('/api/v1/company/categories/rename', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `renameCompanyCategory failed: ${r.status}`);
  return json.data || { count: 0 };
}

export async function deleteCompanyInfo(id) {
  const r = await apiFetch(`/api/v1/company/${id}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteCompanyInfo failed: ${r.status}`);
  return true;
}

// ── Payment-reminder templates (per policy type + carrier) ───────────────────
// Agency-shared. The Monthly Payment reminder composer loads the most specific
// match and substitutes {nombre}/{compania}/{tipo}/{mes}/{poliza}/{prima} per
// client. La sustitucion vive ENTERA en `fillReminder` (PoliciesView.jsx): el
// servidor guarda la plantilla con las etiquetas dentro, tal cual.
export async function listReminderTemplates() {
  const r = await apiFetch('/api/v1/reminder-templates');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listReminderTemplates failed: ${r.status}`);
  return json.data || [];
}

// Upsert by (policyType, carrier). '' for either means "any".
export async function saveReminderTemplate(payload) {
  const r = await apiFetch('/api/v1/reminder-templates', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `saveReminderTemplate failed: ${r.status}`);
  return json.data;
}

export async function deleteReminderTemplate(id) {
  const r = await apiFetch(`/api/v1/reminder-templates/${id}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteReminderTemplate failed: ${r.status}`);
  return true;
}

// Duplicate detection — groups of same-name clients that also share a
// DOB / email / phone. Returns [{ name, clients: [...] }]. Raw API
// shape (not mapped through mapClientFromApi) since the dedupe UI only
// needs the lightweight summary fields the endpoint already returns.
export async function listClientDuplicates() {
  const r = await apiFetch('/api/v1/clients/duplicates');
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `listClientDuplicates failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data || [];
}

// Merge `secondaryId` into `primaryId`. `fields` is a map of
// { fieldName: 'primary'|'secondary' } resolving conflicts where both
// rows have a (different) value. Empty fields on the survivor are
// auto-filled from the secondary server-side regardless of `fields`.
export async function mergeClients(primaryId, secondaryId, fields) {
  const r = await apiFetch(`/api/v1/clients/${primaryId}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ secondaryId, fields: fields || {} }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const e = new Error(err.error || `mergeClients failed: ${r.status}`);
    e.code = err.code;
    throw e;
  }
  const json = await r.json();
  return json.data;
}

// Fetches the full client record (single GET /clients/:id) so the frontend
// can pick up fields the LIST endpoint omits for size/security reasons —
// notably ssnMasked, which the list view doesn't decrypt in bulk.
export async function getClient(id) {
  const r = await apiFetch(`/api/v1/clients/${id}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getClient failed: ${r.status}`);
  }
  const json = await r.json();
  return mapClientFromApi(json.data);
}

// Fetch a single policy in frontend shape (used by the AI Undo to capture a
// policy's current values before editing it).
export async function getPolicy(id) {
  const r = await apiFetch(`/api/v1/policies/${id}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getPolicy failed: ${r.status}`);
  }
  const json = await r.json();
  return mapPolicyFromApi(json.data);
}

export async function createClient(client) {
  const r = await apiFetch('/api/v1/clients', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapClientToApi(client)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `createClient failed: ${r.status}`));
  }
  const json = await r.json();
  return mapClientFromApi(json.data);
}

// `pin` is optional and only consumed by the backend when the patch
// mutates an existing SSN (primary or spouse). For all other update
// paths the field is ignored. The caller is expected to NOT cache
// or persist the PIN — pass it inline per call.
export async function updateClient(id, patch, pin) {
  const payload = mapClientToApi(patch);
  // ssn/spouseSsn are intentionally NOT stripped here. The ClientForm
  // never inherits a plaintext SSN into form state (base.ssn = '' at
  // mount), and mapClientToApi runs every '' through normalizeSsn which
  // returns undefined → JSON.stringify omits the key → backend sees
  // body.ssn === undefined → no PIN gate trip on routine edits.
  //
  // Letting the keys through unblocks two legitimate flows that used
  // to silently drop the SSN:
  //   - "+ AGREGAR" pill on a client with no SSN: typing in the form
  //     and saving now actually persists.
  //   - Import → merge into existing client with no spouse SSN: the
  //     PDF's spouse SSN now reaches the backend instead of being
  //     stripped on the way out.
  // The backend's PIN gate (clients.routes.ts: mutatingPrimarySsn /
  // mutatingSpouseSsn) still fires when there's a PRE-EXISTING SSN, so
  // overwrites stay PIN-protected.
  if (pin) payload.pin = pin;
  // Optimistic concurrency ("no te piso"). The form attaches the updatedAt it
  // loaded with as `__baseUpdatedAt`; we forward it as `baseUpdatedAt` so the
  // backend can 409 if someone else saved in between. A forced save (the user
  // chose "guardar de todos modos" after the conflict prompt) sets `__force`,
  // which omits the base version so the write goes through. These `__`-prefixed
  // keys are transient form metadata — mapClientToApi never copies them into the
  // payload, so they can't leak to the backend by accident.
  if (patch && patch.__baseUpdatedAt && !patch.__force) {
    payload.baseUpdatedAt = patch.__baseUpdatedAt;
  }
  const r = await apiFetch(`/api/v1/clients/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    // Surface the backend's structured error code so the caller can
    // open the PIN modal when the gate trips, instead of swallowing
    // the response into a generic "updateClient failed" string.
    const e = new Error(formatApiError(err, `updateClient failed: ${r.status}`));
    e.code = err.code;          // 'PIN_REQUIRED_FOR_SSN_EDIT' | 'STALE_WRITE' | …
    e.requires = err.requires;
    e.status = r.status;
    // The SSN gate on this route now answers with the same lockout data as
    // the dedicated PIN endpoints, so the retry modal can count down the
    // attempts instead of just repeating "PIN incorrecto".
    if (typeof err.remainingAttempts === 'number') e.remainingAttempts = err.remainingAttempts;
    if (typeof err.retryAfterSec === 'number') e.retryAfterSec = err.retryAfterSec;
    throw e;
  }
  const json = await r.json();
  return mapClientFromApi(json.data);
}

// Live "who is viewing this client" heartbeat. Best-effort: presence is a
// nice-to-have heads-up, never load-bearing, so callers fire-and-forget and
// swallow failures. `action` is 'enter' on open, 'heartbeat' periodically, and
// 'leave' on close. Returns the current viewer list (also pushed via SSE).
export async function postClientPresence(id, action, name) {
  const r = await apiFetch(`/api/v1/clients/${id}/presence`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, name: name || '' }),
  });
  if (!r.ok) return { viewers: [] };
  const json = await r.json().catch(() => ({}));
  return { viewers: Array.isArray(json.viewers) ? json.viewers : [] };
}

// Narrow endpoint that ONLY writes Client.driveUrl. Goes straight to the
// backend instead of through updateClient → mapClientToApi, which would
// expand a partial { driveUrl } patch into a full payload with
// firstName="(falta nombre)" placeholders and null everywhere — the bug
// that destroyed Rosina Albano (#145). Backend route: PUT /clients/:id/drive.
export async function setClientDriveUrl(id, driveUrl) {
  const r = await apiFetch(`/api/v1/clients/${id}/drive`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ driveUrl: driveUrl == null ? '' : String(driveUrl) }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `setClientDriveUrl failed: ${r.status}`));
  }
  const json = await r.json();
  return mapClientFromApi(json.data);
}

// "Agente asignado" quick-edit. Dedicated narrow endpoint (like setClientDriveUrl)
// so we never round-trip the full client through mapClientToApi. Returns the
// narrow { id, assignedAgent } delta — the caller merges it into local state.
export async function setClientAssignedAgent(id, assignedAgent) {
  const r = await apiFetch(`/api/v1/clients/${id}/assigned-agent`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assignedAgent: assignedAgent == null ? '' : String(assignedAgent) }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `setClientAssignedAgent failed: ${r.status}`));
  return json.data || { id, assignedAgent };
}

// Client status quick-toggle (active / inactive / prospect). Dedicated narrow
// endpoint (like setClientAssignedAgent) so the profile-header toggle never
// round-trips the full client through mapClientToApi. Returns the narrow
// { id, status } delta (lowercase) — the caller merges it into local state.
export async function setClientStatus(id, status) {
  const r = await apiFetch(`/api/v1/clients/${id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: String(status || '').toLowerCase() }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `setClientStatus failed: ${r.status}`));
  return json.data || { id, status };
}

// DUPLICAR UN CLIENTE (Lissette 2026-09-21).
//
// "Que no sean espejo": la copia NO queda atada al original — es una ficha
// nueva y entera, y cambiar una no toca a la otra. El servidor copia solo los
// datos de la PERSONA (nombre, contacto, dirección, fecha de nacimiento,
// hogar/dependientes, trabajo y etiquetas) y deja fuera pólizas, comisiones,
// documentos y notas, además del SSN, la licencia, el historial médico y el
// legal, que identifican a UNA persona y no pueden viajar a otra ficha.
//
// Devuelve la copia ya creada, con el mismo mapeo que cualquier cliente, para
// que la pantalla la abra sin pedirla otra vez.
export async function duplicateClient(id) {
  const r = await apiFetch(`/api/v1/clients/${id}/duplicate`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `duplicateClient failed: ${r.status}`));
  return mapClientFromApi(json.data);
}

// MARCAR "LISTA PARA SOMETER" (Lissette 2026-09-21).
//
// Va por la MISMA ruta estrecha que el estatus, y no por una propia, porque
// para quien la usa es otra opción del mismo desplegable. Marcarla NO cambia
// el estatus: la persona sigue siendo prospecto o referido y no se mueve de
// su lista. Al marcar, el servidor deja la línea en el canal "Lista para
// someter" — desmarcar no avisa a nadie, que sería ruido.
export async function setClientReadyToSubmit(id, ready) {
  const r = await apiFetch(`/api/v1/clients/${id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ readyToSubmit: !!ready }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `setClientReadyToSubmit failed: ${r.status}`));
  return json.data || { id, readyToSubmit: !!ready };
}

// ── System announcements ("Novedades" 📣) — cross-tenant. Read by everyone;
// create/edit/delete are platform-owner only (backend-gated). ────────────────
export async function getAnnouncements() {
  const r = await apiFetch('/api/v1/announcements');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `getAnnouncements failed: ${r.status}`));
  return json.data || [];
}
export async function createAnnouncement(payload) {
  const r = await apiFetch('/api/v1/announcements', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `createAnnouncement failed: ${r.status}`));
  return json.data;
}
export async function updateAnnouncement(id, patch) {
  const r = await apiFetch(`/api/v1/announcements/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `updateAnnouncement failed: ${r.status}`));
  return json.data;
}
export async function deleteAnnouncement(id) {
  const r = await apiFetch(`/api/v1/announcements/${id}`, { method: 'DELETE' });
  if (!r.ok) { const json = await r.json().catch(() => ({})); throw new Error(formatApiError(json, `deleteAnnouncement failed: ${r.status}`)); }
  return true;
}

// ── Help Center articles (bilingual). Read by everyone; saved (full array) by
// the platform owner only. ──────────────────────────────────────────────────
export async function getHelpArticles() {
  const r = await apiFetch('/api/v1/help-articles');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `getHelpArticles failed: ${r.status}`));
  return json.data || [];
}
export async function saveHelpArticles(items) {
  const r = await apiFetch('/api/v1/help-articles', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `saveHelpArticles failed: ${r.status}`));
  return json.data || items;
}

// Upload an image or short tutorial video for a Help Center article. Same
// two-step SAS pattern as uploadEmailImage: get a write SAS, PUT the bytes
// straight to Azure, get back a PERMANENT public URL (served by /help-media,
// no SAS to expire) to paste into the article body as `![alt](url)`.
// Platform-owner only — the backend 403s anyone else at the SAS-mint step.
const HELP_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'video/mp4', 'video/webm'];
export async function uploadHelpMedia(file) {
  if (!file) throw new Error('No file provided');
  if (!HELP_MEDIA_TYPES.includes(file.type)) {
    throw new Error('Only PNG, JPEG, WebP, GIF images or MP4/WebM video are accepted.');
  }
  const sasRes = await apiFetch('/api/v1/help-articles/media-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, contentType: file.type }),
  });
  const sasJson = await sasRes.json().catch(() => ({}));
  if (!sasRes.ok) throw new Error(formatApiError(sasJson, `help media SAS failed: ${sasRes.status}`));
  const { uploadUrl, blobUrl, maxBytes } = sasJson.data || {};
  if (!uploadUrl || !blobUrl) throw new Error('Bad SAS response');
  if (maxBytes && file.size > maxBytes) {
    throw new Error(`File is larger than ${(maxBytes / (1024 * 1024)).toFixed(0)} MB.`);
  }
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload to storage failed: ${put.status}`);
  return blobUrl;
}

export async function deleteClient(id) {
  const r = await apiFetch(`/api/v1/clients/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteClient failed: ${r.status}`);
  }
  return true;
}

// ── Client Emails ────────────────────────────────────────────────────────────
// Conversation thread (inbound + outbound) for a single client. Replies are
// captured via the Reply-To trick: every OUTBOUND row gets a unique
// cliente-{token}@reply.deskte.com Reply-To header that SendGrid Inbound
// Parse routes back into our DB.

export async function listClientEmails(clientId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/emails`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `listClientEmails failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data || [];
}

// `to` manda a un miembro del HOGAR (cónyuge o dependiente con correo propio)
// en vez de a la dirección del expediente. El backend lo valida contra las
// direcciones del propio cliente: no es un destinatario libre. Sin `to`, se
// comporta exactamente como siempre.
// `source` ('birthday_greeting' | 'holiday_greeting') marca el correo como
// saludo para que cuente en el control de repetidos: ese informe lee la cola de
// envíos, y un saludo mandado en el momento nunca pasa por la cola. Es solo una
// etiqueta — no cambia a dónde va ni qué dice.
// `cc` copia en VISIBLE al conyuge y a los dependientes que tienen correo
// propio. Misma lista blanca que `to` — el backend solo admite direcciones de
// la propia ficha. Devuelve `ccOmitidas` con los copiados que se quedaron
// fuera por estar dados de baja: el correo salio igual, pero a esos no.
export async function sendClientEmail(clientId, { subject, html, text, docs, acknowledgePhi, to, cc, source } = {}) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, html, text, ...(to ? { to } : {}), ...(cc && cc.length ? { cc } : {}), ...(source ? { source } : {}), ...(docs && docs.length ? { docs } : {}), ...(acknowledgePhi ? { acknowledgePhi: true } : {}) }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const e = new Error(err.error || `sendClientEmail failed: ${r.status}`);
    e.code = err.code; e.findings = err.findings;
    throw e;
  }
  const json = await r.json();
  return json.data;
}

export async function deleteClientEmail(clientId, msgId) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/emails/${msgId}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteClientEmail failed: ${r.status}`);
  }
  return true;
}

// Update the user-applied labels on a single email row. `labels` is an
// array of strings (or null to clear). Agencies pick their own taxonomy.
export async function updateClientEmailLabels(clientId, msgId, labels) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/emails/${msgId}/labels`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ labels: labels && labels.length > 0 ? labels : null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateClientEmailLabels failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Agency-wide email feed for the "Sent Emails" view. Returns rows from
// client_email_messages across all clients in the agency, filtered by
// direction (default OUTBOUND), date range, scope (mine/team/agency),
// or single client. Each row includes the linked client + agent so the
// UI can show names without a separate lookup.
export async function listAgencyEmails({ page = 1, limit = 50, direction = 'OUTBOUND', clientId, fromDate, toDate, scope = 'mine' } = {}) {
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('limit', String(limit));
  if (direction) qs.set('direction', direction);
  if (clientId) qs.set('clientId', clientId);
  if (fromDate) qs.set('fromDate', fromDate);
  if (toDate) qs.set('toDate', toDate);
  if (scope) qs.set('scope', scope);
  const r = await apiFetch(`/api/v1/emails?${qs.toString()}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `listAgencyEmails failed: ${r.status}`);
  }
  const json = await r.json();
  return { data: json.data || [], pagination: json.pagination };
}

// ── Policies ─────────────────────────────────────────────────────────────────

// Frontend uses short type IDs (aca, ma, medsup...) and lowercase status.
// Backend uses long Prisma enums (HEALTH, MEDICARE_ADVANTAGE..., uppercase).
// Las dos tablas que traducen entre los dos están en `policyTypes.js` y se
// importan arriba con los nombres de siempre.
// Frontend-only fields (plan, memberId, familySize, vehicleYear...) get
// preserved verbatim — backend ignores them on write, and on read we attach
// them back from the original `policies` localStorage cache when available.

// Ids que NUNCA existieron en el catálogo pero SÍ están sellados en filas
// reales: el asistente de migración de Monday escribía 'health' y 'medicare'
// —palabras, no ids— en `typeDetails.frontType`. El servidor las guardó como
// HEALTH (por el respaldo de `mapPolicyToApi`), así que el dato no se perdió;
// lo que se perdió fue el ramo en pantalla, porque un id que el catálogo no
// reconoce no cuenta en NINGUNA pestaña y la tarjeta cae al primer ramo de la
// lista, que es Salud. Se veía como Salud sin entrar en Salud.
//
// Arreglar el importador no mueve lo ya migrado, así que se traducen al leer:
// las filas aparecen bien desde el primer refresco, sin migración de datos, y
// se re-sellan solas con el id bueno en el próximo guardado.
//
// OJO con 'medicare': esas filas están en la base como HEALTH, así que salen
// como **Salud**, que es lo que el servidor dice que son. Las que de verdad
// sean Medicare hay que reclasificarlas a mano — no se puede adivinar aquí si
// eran Advantage o Suplementario, y elegir por ellas sería inventar un dato.
const aliasLegacyFrontType = (ft) => (ft && LEGACY_FRONT_TYPE_ALIAS[ft]) || ft;

export function mapPolicyFromApi(p) {
  return {
    id: p.id,
    clientId: p.clientId,
    // Prefer the exact front-end type stamped into type_details on save. The
    // backend enum has no distinct value for Part D / Supplemental / Referred
    // (all → HEALTH) or Final Expense (→ LIFE), so without this they read back
    // as their sibling (aca/life) and appear to "morph" on refresh. We only
    // trust the stored frontType when it still maps to the SAME backend enum we
    // got (guards a type later changed by a path that didn't stamp it). Legacy
    // rows have no frontType → fall back to the enum mapping (unchanged).
    // A CUSTOM agency type (an id that isn't in the built-in map at all) is
    // always trusted: it has no backend enum of its own — mapPolicyToApi stores
    // it as HEALTH — so the "same enum" check would reject it and the card
    // would morph back to ACA on refresh (Lissette 2026-07-21, agencia Pomares:
    // el tipo "Assure For Life" volvía a "ACA / Marketplace" al dar F5).
    type: ((ft) => {
      if (ft && !POLICY_TYPE_FRONT_TO_BACK[ft]) return ft;
      return ft && POLICY_TYPE_FRONT_TO_BACK[ft] === p.type
        ? ft
        : (POLICY_TYPE_BACK_TO_FRONT[p.type] || 'aca');
    })(
      aliasLegacyFrontType(
        p.typeDetails && typeof p.typeDetails === 'object' ? p.typeDetails.frontType : null,
      ),
    ),
    carrier: p.carrier || '',
    plan: p.planName || '',
    policyNumber: p.policyNumber || '',
    // Member ID y # de póliza son DOS campos independientes y aquí se leen tal
    // cual están guardados. Antes memberId caía de vuelta en policyNumber para
    // que las pólizas viejas (importadas cuando memberId no tenía columna)
    // siguieran enseñando el número; el efecto secundario era que los dos
    // campos se espejaban en el formulario: escribías el Member ID y el # de
    // póliza aparecía con lo mismo, y al borrar uno el otro parecía borrarse
    // también (Lissette 2026-08-14). El respaldo para las pólizas viejas sigue
    // existiendo donde corresponde —en la TARJETA, que pinta
    // `memberId || policyNumber` con su etiqueta correcta—, así que no se
    // pierde de vista ningún número: solo deja de escribirse solo.
    memberId: p.memberId || '',
    // Un member id por persona (Lissette 2026-08-28). El titular se compone:
    // sale del mapa si esta, y si no del memberId de siempre — sin eso, una
    // poliza vieja o importada saldria con la fila del titular vacia mientras
    // la tarjeta si enseña su numero.
    memberIdsByPerson: componerMemberIds(p),
    monthlyPremium: p.premium != null ? String(p.premium) : '',
    familySize: '1',
    membersActive: '1',
    effectiveDate: p.effectiveDate ? p.effectiveDate.split('T')[0] : '',
    renewalDate: p.expirationDate ? p.expirationDate.split('T')[0] : '',
    // Required by the API whenever the policy is cancelled.
    cancellationDate: p.cancellationDate ? p.cancellationDate.split('T')[0] : '',
    // A custom status id (if set) wins over the built-in enum for display.
    status: p.customStatus || STATUS_BACK_TO_FRONT[p.status] || (p.status || 'ACTIVE').toLowerCase(),
    customStatus: p.customStatus || '',
    notes: p.notes || '',
    coverageAmount: p.coverageAmount,
    deductible: p.deductible,
    beneficiary: p.beneficiary || '',
    // Per-policy insured selection. NULL/absent in the DB = legacy policy:
    // keep `policyInsured` undefined so the card defaults to primary+spouse.
    // An array (even empty) = an explicit selection the user saved.
    policyInsured: Array.isArray(p.policyInsured) ? p.policyInsured : undefined,
    policyInsuredExtra: Array.isArray(p.policyInsuredExtra) ? p.policyInsuredExtra : [],
    clientName: p.client ? `${p.client.firstName} ${p.client.lastName}`.trim() : '',
    agentName: p.agent?.displayName || '',
    // Policy-card classification (server-backed). agentLabel falls back to the
    // FK agent's displayName so existing policies show the writing agent.
    leadType: p.leadType || '',
    agentLabel: p.agentLabel || p.agent?.displayName || '',
    processedBy: p.processedBy || '',
    // Fecha en que la compañía procesó la póliza. Se corta el ISO por la 'T'
    // igual que effectiveDate: usar toISOString() correría el día en zonas
    // al oeste de UTC (la trampa de fechas que advierte CLAUDE.md).
    processingDate: p.processingDate ? String(p.processingDate).split('T')[0] : '',
    // Agent-defined extra fields ([{label,value}, ...]) — per-policy custom data.
    customFields: Array.isArray(p.customFields) ? p.customFields : [],
    // Etiquetas de PÓLIZA. Nada que ver con las del cliente: describen a la
    // póliza ("pendiente de firma", "pago atrasado") y se van con ella.
    tags: Array.isArray(p.tags) ? p.tags : [],
    // Per-row VALUES for the Monthly-Payments custom columns ({ columnId: text }).
    customData: (p.customData && typeof p.customData === 'object' && !Array.isArray(p.customData)) ? p.customData : {},
    // F2 Renewals pipeline. The stage only applies to the cycle it was set
    // for: if renewalStageCycle no longer matches the current expirationDate
    // (the policy was renewed into a new term), treat it as un-staged so the
    // board shows it fresh in the first column again.
    renewalStage: (() => {
      const expDay = p.expirationDate ? p.expirationDate.split('T')[0] : null;
      const cycleDay = p.renewalStageCycle ? p.renewalStageCycle.split('T')[0] : null;
      return p.renewalStage && expDay && cycleDay && expDay === cycleDay ? p.renewalStage : null;
    })(),
    // La etapa TAL CUAL está guardada, sin el reseteo de arriba, y el
    // vencimiento contra el que se fijó.
    //
    // El reseteo de `renewalStage` es correcto y se queda: una póliza que entró
    // en su término nuevo tiene que volver a salir "sin empezar" en el tablero.
    // Pero se llevaba por delante la única marca de que ALGUIEN LA RENOVÓ: al
    // marcarla, la fecha se mueve un año, el ciclo deja de coincidir y el DONE
    // desaparecía — así que la pestaña "Renovadas" no podía enseñarla nunca
    // (E-294).
    //
    // Aquí solo viajan los datos crudos. Quién cuenta como renovada y durante
    // cuánto tiempo lo decide `estaRenovada` en crm-app/utils.js, que es donde
    // vive esa regla y donde se puede probar sin React (E-138).
    renewalStageGuardada: p.renewalStage || null,
    renewalStageCycle: p.renewalStageCycle || null,
    renewalStageAt: p.renewalStageAt || null,
    renewalStageBy: p.renewalStageBy || null,
    renewalStageByName: p.renewalStageByName || null,
    renewalStageHistory: Array.isArray(p.renewalStageHistory) ? p.renewalStageHistory : [],
    renewalNote: p.renewalNote || '',
    renewalKitSentAt: p.renewalKitSentAt || null,
    // Restore the type-specific details (auto/home/business/autopay/coverage)
    // that live in the type_details JSON blob so the form repopulates them.
    ...(p.typeDetails && typeof p.typeDetails === 'object'
      ? Object.fromEntries(POLICY_DETAIL_KEYS.filter((k) => p.typeDetails[k] != null).map((k) => [k, p.typeDetails[k]]))
      : {}),
  };
}

// Type-specific policy fields (auto/home/business/autopay/coverage) that have
// no dedicated DB column — persisted together in the type_details JSON blob so
// nothing the form collects is silently dropped on save.


export async function listPolicies({ page = 1, limit = 200 } = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  const r = await apiFetch(`/api/v1/policies?${qs.toString()}`);
  if (!r.ok) throw new Error(`listPolicies failed: ${r.status}`);
  const json = await r.json();
  return {
    policies: (json.data || []).map(mapPolicyFromApi),
    pagination: json.pagination,
  };
}

// Format a Zod validation response into a single readable line. The
// backend returns { error: "Invalid input", issues: [{ path, message }] };
// surfacing just "Invalid input" loses the actionable detail.
// Friendly names for the backend field paths that show up in validation
// errors, so a save failure reads "Recipient email: Invalid email" instead of
// the raw "recipientEmail". Unknown fields fall back to a de-camelCased label.
const FIELD_LABELS = {
  recipientEmail: 'Recipient email', recipientName: 'Recipient name',
  email: 'Email', clientEmail: 'Client email', phone: 'Phone',
  title: 'Title', name: 'Name', firstName: 'First name', lastName: 'Last name',
  amount: 'Amount', unitPrice: 'Unit price', quantity: 'Quantity',
  dueDate: 'Due date', validUntil: 'Valid until', taxRate: 'Tax rate',
  description: 'Description', carrier: 'Carrier', premium: 'Premium',
};
function humanizeField(path) {
  if (!path) return 'Field';
  const key = String(path).split('.').pop();
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const spaced = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
function formatApiError(err, fallback) {
  if (!err || typeof err !== 'object') return fallback;
  const base = err.error || fallback;
  if (Array.isArray(err.issues) && err.issues.length) {
    const list = err.issues.map((i) => `${humanizeField(i.path)}: ${i.message}`).join('; ');
    return `${base} — ${list}`;
  }
  return base;
}

// Build an Error that preserves the backend's machine-readable fields
// (code, findings, hint, issues) on the thrown object. Callers can branch
// on `err.code === 'PHI_DETECTED'` and use `err.findings` to render the
// PHI warning modal, while still falling back to a sensible message for
// any error rendered as a string.
function makeApiError(payload, fallback) {
  const message = formatApiError(payload, fallback);
  const e = new Error(message);
  if (payload && typeof payload === 'object') {
    if (payload.code) e.code = payload.code;
    if (Array.isArray(payload.findings)) e.findings = payload.findings;
    if (payload.hint) e.hint = payload.hint;
    if (payload.detail) e.detail = payload.detail;
    // Varias rutas mandan el codigo legible por maquina en `error` y no en
    // `code` (no_supervisor, agent_no_mailbox...). Se conserva para que la
    // pantalla pueda decidir sin tener que adivinar por el texto.
    if (payload.error) e.serverCode = payload.error;
    if (Array.isArray(payload.issues)) e.issues = payload.issues;
    // Los NUMEROS de un "no cabe" (cuanto tiene y cuanto se admite). Sin ellos
    // la pantalla solo puede decir "es muy largo", que no dice por cuanto ni
    // cuanto hay que recortar (Crismar Perez, 2026-09-17).
    if (typeof payload.caracteres === 'number') e.caracteres = payload.caracteres;
    if (typeof payload.maximo === 'number') e.maximo = payload.maximo;
  }
  return e;
}

export async function createPolicy(policy) {
  const r = await apiFetch('/api/v1/policies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapPolicyToApi(policy, { paraCrear: true })),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    // El estado viaja: un 403 (sin permiso para ese ramo) no es un fallo de red.
    const e = new Error(formatApiError(err, `createPolicy failed: ${r.status}`));
    e.status = r.status;
    throw e;
  }
  const json = await r.json();
  return mapPolicyFromApi(json.data);
}

// Bulk import policies matched to (or creating) clients. ONE request per chunk
// so a founder browsing-as-agency doesn't burn the 30/hour impersonation cap.
// payload: { defaultType, createMissing, rows: [...] }. Returns
// { linked, createdClients, createdPolicies, ambiguous, skipped }.
export async function importPolicies(payload) {
  const r = await apiFetch('/api/v1/policies/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw makeApiError(json, `importPolicies failed: ${r.status}`);
  return json.data || {};
}

export async function updatePolicy(id, patch) {
  const r = await apiFetch(`/api/v1/policies/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapPolicyToApi(patch)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `updatePolicy failed: ${r.status}`));
  }
  const json = await r.json();
  return mapPolicyFromApi(json.data);
}

// MOVER LA POLIZA A OTRO CLIENTE (Maritza Mendoza, 2026-09-10).
//
// Ruta propia y no un campo del PUT, a proposito: arrastra comisiones,
// recordatorios y avisos, y resetea las personas aseguradas — un guardado
// normal no puede hacer eso sin que nadie lo pida. Ver la cabecera de
// deskte-api/src/utils/moverPoliza.ts.
//
// Devuelve la poliza YA MOVIDA y el resumen de lo que de verdad se movio
// (`movido`), que es lo que la pantalla enseña: contar la intencion en vez de
// la respuesta es la regla 142.
export async function movePolicyToClient(id, clientId) {
  const r = await apiFetch(`/api/v1/policies/${id}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `movePolicyToClient failed: ${r.status}`));
  return { policy: mapPolicyFromApi(json.data), movido: json.movido || {} };
}

export async function deletePolicy(id) {
  const r = await apiFetch(`/api/v1/policies/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deletePolicy failed: ${r.status}`);
  }
  return true;
}

// Soft-delete many policies in one request (reversible deletedAt). Server caps
// at 500 ids/request; the caller chunks larger selections. Returns the number
// actually deleted (only ids inside the caller's scope count).
export async function bulkDeletePolicies(ids) {
  const r = await apiFetch('/api/v1/policies/bulk-delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw makeApiError(json, `bulkDeletePolicies failed: ${r.status}`);
  return (json.data && json.data.deleted) || 0;
}

/**
 * CAMBIAR EL ESTATUS DE VARIAS POLIZAS A LA VEZ (Luis Rodriguez, 2026-09-10).
 *
 * `estatus` es el id de la pantalla —de serie o propio de la agencia— y se
 * convierte a las dos columnas del servidor con la MISMA regla que el guardado
 * de una poliza suelta (`estadoParaElServidor`), para que un estatus propio no
 * signifique una cosa al guardar una y otra al cambiar veinte.
 *
 * Devuelve lo que contesto el servidor: cuantas cambiaron, cuantas ya estaban
 * asi y cuantas de las que se mandaron no estaban al alcance de quien pide.
 * La pantalla las ensena todas — decir "listo" sobre veinte y haber cambiado
 * doce es lo que obliga a escribir a soporte.
 */
export async function bulkUpdatePolicyStatus(ids, estatus, opciones = {}) {
  const { status, customStatus } = estadoParaElServidor(estatus);
  const cuerpo = { ids, status, customStatus, ejecutarAutomatizaciones: !!opciones.ejecutarAutomatizaciones };
  // Solo viaja si la pantalla la pidio: mandarla siempre (aunque sea vacia) le
  // diria al servidor "borrame la fecha", y una poliza que sale de cancelada ya
  // la limpia sola.
  if (opciones.cancellationDate !== undefined) cuerpo.cancellationDate = opciones.cancellationDate || null;
  const r = await apiFetch('/api/v1/policies/bulk-status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw makeApiError(json, `bulkUpdatePolicyStatus failed: ${r.status}`);
  const d = json.data || {};
  return {
    cambiadas: d.cambiadas || 0,
    sinCambio: d.sinCambio || 0,
    pedidas: d.pedidas || (ids ? ids.length : 0),
    fuera: d.fuera || 0,
  };
}

// Cambio masivo de analista / agente / handle / estado (Lissette 2026-10-20).
// campo: 'analyst' | 'agent' | 'attends' | 'state'. Los dos ultimos cambian la
// ficha del CLIENTE. Devuelve las cuentas que contesto el servidor.
export async function bulkUpdatePolicyFields(ids, campo, valor) {
  const r = await apiFetch('/api/v1/policies/bulk-fields', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, campo, valor }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw makeApiError(json, `bulkUpdatePolicyFields failed: ${r.status}`);
  const d = json.data || {};
  return { cambiadas: d.cambiadas || 0, sinCambio: d.sinCambio || 0, fuera: d.fuera || 0, clientes: d.clientes || 0 };
}

// ── Monthly payment tracker ────────────────────────────────────────────────

// period = "YYYY-MM". Returns active policies for that month, each with a
// `payment` block ({ paid, paidAt, amount, note, markedByUserId }).
// `periods` (opcional): varios meses EN UNA SOLA peticion, para el modo
// "Desde -> Hasta". Antes ese modo llamaba a esta misma funcion una vez por mes
// -- hasta doce peticiones, cada una trayendose el listado ENTERO de polizas de
// la agencia con su ficha de cliente y sus columnas propias. Doce veces las
// mismas filas para cambiar de que mes es el pago. (2026-09-02, queja 39.)
// `ligero`: pedir SOLO lo que cambia por mes (el pago, su historial, el
// contador de notas) y no la poliza entera. El CRM ya tiene las polizas en
// memoria —se las trae al entrar— y Pagos del Mes se las estaba bajando otra
// vez: 53 columnas por fila, siete de ellas JSON. Quien llama con esto pone
// el cuerpo de cada fila desde memoria. (2026-09-02, queja 39.)
export async function getPaymentTracker({ period, periods = null, ligero = false, type = '', carrier = '', clientId = '' } = {}) {
  const qs = new URLSearchParams({ period });
  if (Array.isArray(periods) && periods.length) qs.set('periods', periods.join(','));
  if (ligero) qs.set('ligero', '1');
  if (type) qs.set('type', POLICY_TYPE_FRONT_TO_BACK[type] || type);
  if (carrier) qs.set('carrier', carrier);
  if (clientId) qs.set('clientId', clientId);
  // Cronometro del NAVEGADOR (Lissette 2026-09-02, queja 39). El servidor trae
  // el suyo en `meta.ms`; lo que falta por saber es cuanto se va en el viaje y
  // cuanto en convertir las filas aqui. Sin esto no se puede decidir si la
  // reforma va en el servidor o en la pantalla, y ya se fallo una vez por
  // suponerlo.
  //
  // `performance.now()` y no `Date.now()`: mide de verdad el tiempo
  // transcurrido aunque el reloj del equipo se ajuste a mitad de la carga.
  const reloj = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const t0 = reloj();
  const r = await apiFetch(`/api/v1/policies/payments?${qs.toString()}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getPaymentTracker failed: ${r.status}`);
  }
  const json = await r.json();
  // Servidor + red + leer el JSON. El tramo del servidor se resta luego con
  // `meta.ms.total`, y lo que queda es el viaje de verdad.
  const msRespuesta = Math.round(reloj() - t0);
  // Cuantos intentos costo. Sin esto, "viaje = 37 s" no distingue una conexion
  // lenta de un despliegue en marcha (apiFetch espera hasta 88 s reintentando
  // los 502/503 del contenedor que arranca, y esas esperas caen dentro del
  // viaje). Con el numero de intentos delante, la respuesta es inmediata.
  const intentos = intentosDeLaPeticion.get(r) || 1;
  const t1 = reloj();
  // `cancelled` from the RAW enum so a custom-status label can't hide it — the
  // Monthly Payments view badges these rows and keeps them out of the totals.
  // La bandera la manda el SERVIDOR, no se deduce de lo que se pidio: el
  // frontend se despliega unos minutos antes que el backend (CLAUDE.md), asi
  // que hay una ventana en la que se pide ligero y contestan completo. Si se
  // diera por hecho, esos minutos la tabla saldria vacia.
  const vinoLigero = json.ligero === true;
  const rows = (json.data || []).map((p) => {
    // En ligero NO se pasa por mapPolicyFromApi: la fila no trae carrier, ni
    // plan, ni fechas — solo lo justo para casarla con la de memoria. Mapearla
    // produciria una poliza fantasma con todo vacio.
    const fila = vinoLigero
      ? {
          id: p.id,
          clientId: p.clientId,
          customData: (p.customData && typeof p.customData === 'object' && !Array.isArray(p.customData)) ? p.customData : {},
          cancelled: p.status === 'CANCELLED',
          payment: p.payment || { paid: false },
        }
      : { ...mapPolicyFromApi(p), cancelled: p.status === 'CANCELLED', payment: p.payment || { paid: false } };
    // Solo viene cuando se pidieron varios meses. Se copia tal cual: la
    // pantalla ya sabe leer `payByPeriod['YYYY-MM']`.
    if (p.payByPeriod) fila.payByPeriod = p.payByPeriod;
    return fila;
  });
  const msMapeo = Math.round(reloj() - t1);
  return {
    period: json.period,
    periods: json.periods || [json.period],
    ligero: vinoLigero,
    // Cronometro del servidor (queja 39). Va tal cual desde la ruta: cuantas
    // filas y cuantos milisegundos tardo cada tramo. Solo numeros.
    meta: json.meta || null,
    tiempos: { msRespuesta, msMapeo, intentos },
    rows,
  };
}

// Save the per-policy VALUES for the Monthly-Payments custom columns
// ({ [columnId]: text }). Merged server-side, persistent per policy. Any seat
// with policies.edit can save (Lissette 2026-07-08).
export async function savePaymentCustomData({ policyId, customData }) {
  const r = await apiFetch('/api/v1/policies/payments/custom-data', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyId, customData: customData || {} }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `savePaymentCustomData failed: ${r.status}`));
  }
  return (await r.json()).data;
}

export async function markPolicyPayment({ policyId, period, paid, statusLabel = '', amount, note }) {
  // amount/note are OMITTED from the request unless the caller passes them —
  // the backend only updates the fields present, so a paid/unpaid toggle no
  // longer wipes a saved note or an imported amount.
  const body = { policyId, period, paid, statusLabel };
  if (amount !== undefined) body.amount = amount;
  if (note !== undefined) body.note = note;
  const r = await apiFetch('/api/v1/policies/payments/mark', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `markPolicyPayment failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data;
}

// Saves ONLY the per-month note for a policy (no paid/reviewed side effects).
// noteImageUrl: undefined = keep the stored image; '' or null = clear it.
export async function savePolicyPaymentNote({ policyId, period, note, noteImageUrl }) {
  const body = { policyId, period, note: note || '' };
  if (noteImageUrl !== undefined) body.noteImageUrl = noteImageUrl || '';
  const r = await apiFetch('/api/v1/policies/payments/note', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `savePolicyPaymentNote failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data;
}

// Uploads a pasted/attached image OR PDF for a per-month payment note straight
// to Azure Blob via a short-lived SAS (same 2-step flow as uploadDocument).
// Returns the plain blobUrl to store on the note.
export async function uploadPaymentNoteImage(file) {
  const sasRes = await apiFetch('/api/v1/policies/payments/note-image-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `type` va SIEMPRE: el servidor solo miraba la extension, y un audio con
    // una extension fuera de su lista (.amr, .webm...) se rechazaba aunque el
    // navegador supiera perfectamente que era audio (Jennifer Acevedo,
    // 2026-08-21).
    body: JSON.stringify({ fileName: file.name || 'pasted-image.png', size: file.size, type: file.type || '' }),
  });
  if (!sasRes.ok) {
    const err = await sasRes.json().catch(() => ({}));
    throw new Error(formatApiError(err, `note-image-sas failed: ${sasRes.status}`));
  }
  const { data: sas } = await sasRes.json();
  const putRes = await fetch(sas.uploadUrl, {
    method: 'PUT',
    // Never force image/png: a PDF stored with an image Content-Type makes the
    // browser download it as a broken image instead of opening the viewer.
    // Fall back by extension only when the File carries no type at all.
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || (/\.pdf$/i.test(file.name || '') ? 'application/pdf' : 'image/png'),
    },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '');
    throw new Error(`blob upload failed: ${putRes.status} ${text.slice(0, 200)}`);
  }
  return sas.blobUrl;
}

// ── Payment note THREAD (many messages, each with several images) ──────────

// Full thread for (policy, period), oldest first. Each message comes with
// authorName resolved and imageUrls already read-signed (open directly).
export async function listPolicyPaymentNotes({ policyId, period }) {
  const qs = new URLSearchParams({ policyId, period });
  const r = await apiFetch(`/api/v1/policies/payments/notes?${qs.toString()}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `listPolicyPaymentNotes failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data || [];
}

// Appends one message to the thread. imageUrls = plain blobUrls returned by
// uploadPaymentNoteImage. No paid/reviewed side effects.
/**
 * Añade un mensaje al hilo de notas de un pago.
 *
 * `espejo` lo REFLEJA en las notas del cliente (Lissette 2026-09-16: *"pero
 * necesito que sea un espejo"*). Lo hace el servidor, no dos llamadas desde
 * aqui: las dos notas quedan enlazadas, y al editar o borrar esta se va con
 * ella. La respuesta trae `espejo` con lo que se consiguio de verdad.
 */
export async function addPolicyPaymentNote({ policyId, period, text, imageUrls, espejo, lang }) {
  const r = await apiFetch('/api/v1/policies/payments/notes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      policyId, period, text: text || '', imageUrls: imageUrls || [],
      ...(espejo ? { espejo: true, lang: lang === 'en' ? 'en' : 'es' } : {}),
    }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `addPolicyPaymentNote failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data;
}

// Refleja DE UNA VEZ todas las notas del hilo de una póliza que todavía no lo
// están (Lissette 2026-09-16: *"que se pueda seleccionar para todas de una
// vez"*). Solo las tuyas: las de otra persona no se tocan.
//
// Devuelve el recuento ENTERO —{ total, reflejadas, yaEstaban, deOtros,
// fallaron }— para que la pantalla pueda decir lo que de verdad pasó en vez de
// un "listo" sobre un hilo a medias reflejar.
export async function mirrorAllPaymentNotes(policyId, lang) {
  const r = await apiFetch('/api/v1/policies/payments/notes/mirror-all', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyId, lang: lang === 'en' ? 'en' : 'es' }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `mirrorAllPaymentNotes failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data;
}

// Deletes ONE of your own thread messages (author-only, backend-enforced).
export async function deletePolicyPaymentNote(id) {
  const r = await apiFetch(`/api/v1/policies/payments/notes/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `deletePolicyPaymentNote failed: ${r.status}`));
  }
  return true;
}

// Edits ONE of your own thread messages (author-only, backend-enforced). Pass
// `imageUrls` to REPLACE the photos (add/remove on edit); omit to keep them.
export async function updatePolicyPaymentNote(id, text, imageUrls, espejo, lang) {
  const body = { text: text || '' };
  if (imageUrls !== undefined) body.imageUrls = imageUrls || [];
  // Sin `espejo` el que hubiera se queda como esta (y se reescribe con el texto
  // nuevo). Con `true` o `false` se enciende o se apaga.
  if (espejo !== undefined) { body.espejo = !!espejo; body.lang = lang === 'en' ? 'en' : 'es'; }
  const r = await apiFetch(`/api/v1/policies/payments/notes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `updatePolicyPaymentNote failed: ${r.status}`));
  }
  return (await r.json()).data;
}

// ── Policy NOTE thread (#3) — independent from payment notes ────────────────
// General notes on a policy (bubble on the policy card). Reuses the same image
// upload (uploadPaymentNoteImage) but a separate table/endpoints.
export async function listPolicyNotes(policyId) {
  const r = await apiFetch(`/api/v1/policies/${encodeURIComponent(policyId)}/notes`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `listPolicyNotes failed: ${r.status}`));
  }
  return (await r.json()).data || [];
}

export async function addPolicyNote({ policyId, text, imageUrls }) {
  const r = await apiFetch(`/api/v1/policies/${encodeURIComponent(policyId)}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: text || '', imageUrls: imageUrls || [] }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `addPolicyNote failed: ${r.status}`));
  }
  return (await r.json()).data;
}

export async function deletePolicyNote(id) {
  const r = await apiFetch(`/api/v1/policies/policy-notes/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `deletePolicyNote failed: ${r.status}`));
  }
  return true;
}

// Pin/unpin a policy note (any editor — not author-restricted).
export async function togglePolicyNotePin(id, isPinned) {
  const r = await apiFetch(`/api/v1/policies/policy-notes/${encodeURIComponent(id)}/pin`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ isPinned: !!isPinned }),
  });
  if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(formatApiError(err, `togglePolicyNotePin failed: ${r.status}`)); }
  return true;
}

// Pin/unpin a monthly-payment note (any editor).
export async function togglePaymentNotePin(id, isPinned) {
  const r = await apiFetch(`/api/v1/policies/payments/notes/${encodeURIComponent(id)}/pin`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ isPinned: !!isPinned }),
  });
  if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(formatApiError(err, `togglePaymentNotePin failed: ${r.status}`)); }
  return true;
}

// Pass `imageUrls` to REPLACE the note's photos (add/remove on edit); omit to
// keep them unchanged.
export async function updatePolicyNote(id, text, imageUrls) {
  const body = { text: text || '' };
  if (imageUrls !== undefined) body.imageUrls = imageUrls || [];
  const r = await apiFetch(`/api/v1/policies/policy-notes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `updatePolicyNote failed: ${r.status}`));
  }
  return (await r.json()).data;
}

// ── Seguimiento / Onboarding (#5) ───────────────────────────────────────────
// ── Columnas propias de las tablas de Renovaciones y Prospecto y Referido ──
// Las DEFINICIONES ([{id,name}]) viven en los ajustes de la agencia
// (tableCustomColumns) y se leen/guardan con getAgencySettings /
// updateAgencySettings, igual que las de Baúl, Info de Empresa, Pagos del Mes y
// Seguimientos. Lo de aquí son solo los VALORES de cada casilla.
//
// El servidor filtra por alcance: un asiento con la vista limitada recibe solo
// las casillas de las filas que puede ver. Por eso NO se le manda la lista de
// filas desde aquí — lo que manda el navegador lo escribe quien quiera.

/** { [idDeFila]: { [idDeColumna]: "texto" } } del módulo. */
export async function getCustomColumnValues(modulo) {
  const r = await apiFetch(`/api/v1/custom-columns/${encodeURIComponent(modulo)}/values`);
  if (!r.ok) throw new Error(`getCustomColumnValues failed: ${r.status}`);
  return (await r.json()).data || {};
}

/**
 * Las columnas borradas que todavía tienen datos: [{ id, filas, ejemplos }].
 * Para recuperarlas desde la ventana de columnas (E-391, ticket de Marilyn
 * Sequera 2026-10-28).
 */
export async function getColumnasHuerfanas(modulo) {
  const r = await apiFetch(`/api/v1/custom-columns/${encodeURIComponent(modulo)}/huerfanas`);
  if (!r.ok) throw new Error(`getColumnasHuerfanas failed: ${r.status}`);
  return (await r.json()).data || [];
}

/** Guarda TODAS las casillas de una fila de una vez. Borrar = no mandarla. */
export async function saveCustomColumnValues(modulo, rowId, cells) {
  const r = await apiFetch(`/api/v1/custom-columns/${encodeURIComponent(modulo)}/values/${encodeURIComponent(rowId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cells: cells || {} }),
  });
  if (!r.ok) throw new Error(`saveCustomColumnValues failed: ${r.status}`);
  return (await r.json()).data;
}

export async function listOnboarding() {
  const r = await apiFetch('/api/v1/onboarding');
  if (!r.ok) throw new Error(`listOnboarding failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function addOnboarding(clientId) {
  const r = await apiFetch('/api/v1/onboarding', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId }),
  });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(formatApiError(e, `addOnboarding failed: ${r.status}`)); }
  return (await r.json());
}
export async function patchOnboarding(id, patch) {
  const r = await apiFetch(`/api/v1/onboarding/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch || {}),
  });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(formatApiError(e, `patchOnboarding failed: ${r.status}`)); }
  return (await r.json()).data;
}
export async function deleteOnboarding(id) {
  const r = await apiFetch(`/api/v1/onboarding/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteOnboarding failed: ${r.status}`);
  return true;
}

// Sacar a varias personas de Prospectos y Referidos de una vez. Quita la
// tarjeta y, a quien siga marcado como Prospecto/Referido, lo devuelve a
// Activo — si no, la fila reaparece sola en el siguiente refresco. NUNCA borra
// al cliente ni sus polizas (Lissette 2026-08-19: dos agencias importaron su
// cartera entera y el unico boton que habia borraba al cliente completo).
// Maximo 500 ids por llamada, igual que el limite del backend.
export async function bulkRemoveOnboarding(clientIds, opts = {}) {
  const ids = (clientIds || []).filter(Boolean);
  if (!ids.length) return { cardsRemoved: 0, promoted: 0 };
  const r = await apiFetch('/api/v1/onboarding/bulk-remove', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientIds: ids, ...(opts.promote === false ? { promote: false } : {}) }),
  });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(formatApiError(e, `bulkRemoveOnboarding failed: ${r.status}`)); }
  return (await r.json()).data || { cardsRemoved: 0, promoted: 0 };
}

// Short-lived read URL for the image saved on (policy, period)'s note.
export async function getPaymentNoteImageUrl({ policyId, period }) {
  const r = await apiFetch('/api/v1/policies/payments/note-image-read-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyId, period }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `note-image-read-sas failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data.url;
}

// Sends the payment reminder email server-side from the agent's connected
// mailbox. WhatsApp/SMS stay as device links in the modal.
export async function sendPaymentReminder({ policyId, period, subject, message }) {
  const r = await apiFetch('/api/v1/policies/payments/remind', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyId, period, subject, message }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.detail || formatApiError(json, `sendPaymentReminder failed: ${r.status}`));
  return json;
}

// ── Monthly-payment import (carrier roster CSV → match → mark this month) ────

// Send the parsed+classified rows; the server matches each to one of the
// caller's policies and returns a per-row verdict. Writes NOTHING (preview).
// rows: [{ index, name, dob, policyNumber, memberId, paid, statusLabel, amount, rawStatus }]
export async function importPaymentsPreview({ period, carrier = '', rows = [] }) {
  const r = await apiFetch('/api/v1/policies/payments/import/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ period, carrier, rows }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `importPaymentsPreview failed: ${r.status}`));
  return { data: json.data || [], summary: json.summary || {} };
}

// Persist the confirmed rows for the period.
// items: [{ policyId, paid, statusLabel, amount, note }] → { applied, skipped }
export async function importPaymentsCommit({ period, items = [] }) {
  const r = await apiFetch('/api/v1/policies/payments/import/commit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ period, items }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `importPaymentsCommit failed: ${r.status}`));
  return { applied: json.applied || 0, skipped: json.skipped || 0 };
}

// Remove the marks a file import created for the period (hand-set marks stay).
// → { deleted }
export async function undoPaymentImport({ period }) {
  const r = await apiFetch('/api/v1/policies/payments/import/undo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ period }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `undoPaymentImport failed: ${r.status}`));
  return { deleted: json.deleted || 0 };
}

// Hybrid AI fallback for an unknown carrier format: ask the server (Azure
// OpenAI) which header maps to each logical field. Returns the mapping object,
// or null if AI is unavailable / failed (caller falls back to manual mapping).
export async function aiMapPaymentColumns({ headers = [], sample = [] }) {
  const r = await apiFetch('/api/v1/policies/payments/import/ai-map', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ headers, sample }),
  });
  if (!r.ok) return null;
  const json = await r.json().catch(() => ({}));
  return json.data || null;
}

// F2 Renewals pipeline. Moves a policy to a renewal stage (a built-in id
// PROCESO|REVISION|SOMETIDA|DONE or a custom status id) for its current cycle.
// Returns { data, kit } — kit.sent/reason tells whether the DONE welcome-kit
// email went out so the UI can toast accordingly.
// `renovadaConPolizaId`: renovada con OTRA póliza del cliente — el kit sale
// con los datos de esa (Lissette 2026-09-26).
// Vista previa del kit de renovación, ANTES de marcar renovada (Lissette
// 2026-10-17). El servidor arma el mensaje con las mismas piezas que el envío y
// no manda nada. Devuelve { subject, html, text, mensaje, archivos, faltan, tieneCorreo }.
export async function previewRenewalKit({ policyId, renovadaConPolizaId }) {
  const cuerpo = { policyId };
  if (renovadaConPolizaId) cuerpo.renovadaConPolizaId = renovadaConPolizaId;
  const r = await apiFetch('/api/v1/policies/kit-preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `previewRenewalKit failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data;
}

export async function setRenewalStage({ policyId, stage, forceKit, renovadaConPolizaId }) {
  const cuerpo = { policyId, stage };
  if (forceKit) cuerpo.forceKit = true;
  if (renovadaConPolizaId) cuerpo.renovadaConPolizaId = renovadaConPolizaId;
  const r = await apiFetch('/api/v1/policies/renewal-stage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `setRenewalStage failed: ${r.status}`));
  }
  const json = await r.json();
  return { data: json.data, kit: json.kit || { sent: false } };
}

// CANCELACION PROGRAMADA (Lissette 2026-10-20). Fecha futura: se guarda y la
// poliza NO cambia de estatus hasta ese dia (la aplica un trabajo del
// servidor). Hoy o pasada: se cancela ya. Devuelve { cancelada, programada }.
export async function programarCancelacion(policyId, { fecha, motivo }) {
  const r = await apiFetch(`/api/v1/policies/${encodeURIComponent(policyId)}/cancelacion-programada`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fecha, motivo }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `programarCancelacion failed: ${r.status}`));
  return { cancelada: !!json.data?.cancelada, programada: json.data?.programada || null };
}

export async function quitarCancelacionProgramada(policyId) {
  const r = await apiFetch(`/api/v1/policies/${encodeURIComponent(policyId)}/cancelacion-programada`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `quitarCancelacionProgramada failed: ${r.status}`));
  }
}

// F4 Welcome Kit. Manually send the welcome-kit email for a policy (template
// resolved by policy type on the backend). Returns { ok, kit } where
// kit.sent/reason drives the toast (e.g. reason 'missing_docs' lists the docs).
export async function sendWelcomeKit({ policyId, clientId, policyType, forceKit, contexto } = {}) {
  const body = {};
  // 'prospecto' | 'renovacion': qué mensaje de kit usar (crm-app/kitPorContexto.js).
  if (contexto) body.contexto = contexto;
  if (policyId) body.policyId = policyId;
  if (clientId) body.clientId = clientId;
  if (policyType) body.policyType = policyType;
  if (forceKit) body.forceKit = true;
  const r = await apiFetch('/api/v1/policies/send-welcome-kit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `sendWelcomeKit failed: ${r.status}`));
  }
  const json = await r.json();
  return { ok: !!json.ok, kit: json.kit || { sent: false } };
}

// F2 Renewals. Saves the free-text note shown in the Renewals list (kept off
// the policy form so it never touches PHI-bearing fields).
export async function setRenewalNote({ policyId, note }) {
  const r = await apiFetch('/api/v1/policies/renewal-note', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyId, note }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `setRenewalNote failed: ${r.status}`));
  }
  const json = await r.json();
  return json.data;
}

// ── Tasks ────────────────────────────────────────────────────────────────────

// Backend Task model has NO `clientId` (gap — frontend ties tasks to clients).
// We preserve the frontend clientId locally only; it doesn't survive a reload
// from the API. If task↔client linking matters, add `clientId` to Task in
// schema.prisma.
const TASK_PRIORITY_FRONT_TO_BACK = {
  low: 'LOW', medium: 'MEDIUM', high: 'HIGH', urgent: 'URGENT',
};
const TASK_PRIORITY_BACK_TO_FRONT = {
  LOW: 'low', MEDIUM: 'medium', HIGH: 'high', URGENT: 'high',
};
const TASK_STATUS_FRONT_TO_BACK = {
  todo: 'PENDING', inprogress: 'IN_PROGRESS', done: 'COMPLETED', cancelled: 'CANCELLED',
};
const TASK_STATUS_BACK_TO_FRONT = {
  PENDING: 'todo', IN_PROGRESS: 'inprogress', COMPLETED: 'done', CANCELLED: 'todo',
};

export function mapTaskFromApi(t) {
  return {
    id: t.id,
    title: t.title,
    description: t.description || '',
    notes: t.notes || '',
    priority: TASK_PRIORITY_BACK_TO_FRONT[t.priority] || 'medium',
    // An agency-custom status (if set) overrides the built-in enum for display.
    // The frontend `status` field then holds the custom id (e.g. "cs_123").
    status: t.customStatus || TASK_STATUS_BACK_TO_FRONT[t.status] || 'todo',
    dueDate: t.dueDate ? t.dueDate.split('T')[0] : '',
    // Task type (call, follow-up, renewal…) — stored in the DB `category` field.
    category: t.category || '',
    assignedTo: t.assigneeId || null,
    // Original assignee (set at creation). When it differs from assignedTo the
    // task was reassigned, which the Tasks table surfaces in its own column.
    firstAssignee: t.firstAssigneeId || null,
    clientId: t.clientId || null,
    createdBy: t.createdById || null,
    createdAt: t.createdAt ? t.createdAt.split('T')[0] : '',
    completedAt: t.completedAt ? t.completedAt.split('T')[0] : null,
    completedBy: t.completedById || null,
    // Pasted images [{name,type,size,blobUrl,url}] — `url` is the signed
    // read URL the backend attaches on serialize (blobUrl is the stored ref).
    attachments: Array.isArray(t.attachments) ? t.attachments : [],
  };
}

// UUIDs created by Prisma look like 8-4-4-4-12 hex chars. SEED IDs in the
// frontend ("u1", "c1", etc.) won't match — when they don't, omit the field
// so the backend falls back to tenant defaults (current user / no client).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);

function mapTaskToApi(t) {
  const raw = (t.status || 'todo');
  const builtinStatus = TASK_STATUS_FRONT_TO_BACK[raw.toLowerCase()];
  // A custom status id isn't in the built-in map → store it in customStatus and
  // keep the enum at PENDING (active). A built-in pick clears customStatus.
  const isCustom = !builtinStatus;
  return {
    title: t.title || '',
    description: t.description || null,
    notes: t.notes || null,
    dueDate: t.dueDate || null,
    category: t.category || null,
    priority: TASK_PRIORITY_FRONT_TO_BACK[(t.priority || 'medium').toLowerCase()] || 'MEDIUM',
    status: isCustom ? 'PENDING' : builtinStatus,
    customStatus: isCustom ? raw : null,
    assigneeId: isUuid(t.assignedTo) ? t.assignedTo : undefined,
    clientId: isUuid(t.clientId) ? t.clientId : undefined,
    // Send only the bare blob refs (never the signed `url` or local previews);
    // omit entirely when absent so a partial PUT doesn't wipe the list.
    attachments: Array.isArray(t.attachments)
      ? t.attachments.map((a) => ({ name: a.name || 'imagen', type: a.type || '', size: a.size || 0, blobUrl: a.blobUrl }))
      : undefined,
  };
}

export async function listTasks({ page = 1, limit = 200, clientId } = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (clientId) qs.set('clientId', clientId);
  const r = await apiFetch(`/api/v1/tasks?${qs.toString()}`);
  if (!r.ok) throw new Error(`listTasks failed: ${r.status}`);
  const json = await r.json();
  return {
    tasks: (json.data || []).map(mapTaskFromApi),
    pagination: json.pagination,
  };
}

export async function createTask(task) {
  const r = await apiFetch('/api/v1/tasks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapTaskToApi(task)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createTask failed: ${r.status}`);
  }
  const json = await r.json();
  return mapTaskFromApi(json.data);
}

export async function updateTask(id, patch) {
  const r = await apiFetch(`/api/v1/tasks/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapTaskToApi(patch)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateTask failed: ${r.status}`);
  }
  const json = await r.json();
  return mapTaskFromApi(json.data);
}

// Papelera de tareas (2026-10-15): borrar ya no destruye la tarea.
export async function listDeletedTasks(days = 90) {
  const r = await apiFetch(`/api/v1/tasks/trash?days=${encodeURIComponent(days)}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listDeletedTasks failed: ${r.status}`);
  return json.data || [];
}

export async function restoreTask(id) {
  const r = await apiFetch(`/api/v1/tasks/${encodeURIComponent(id)}/restore`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `restoreTask failed: ${r.status}`);
  return mapTaskFromApi(json.data);
}

export async function deleteTask(id) {
  const r = await apiFetch(`/api/v1/tasks/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteTask failed: ${r.status}`);
  }
  return true;
}

// ── Leads ────────────────────────────────────────────────────────────────────

const LEAD_STATUS_FRONT_TO_BACK = {
  new: 'NEW', contacted: 'CONTACTED', qualified: 'QUALIFIED',
  proposal: 'PROPOSAL', converted: 'WON', won: 'WON', lost: 'LOST',
};
const LEAD_STATUS_BACK_TO_FRONT = {
  NEW: 'new', CONTACTED: 'contacted', QUALIFIED: 'qualified',
  PROPOSAL: 'proposal', WON: 'converted', LOST: 'lost',
};

export function mapLeadFromApi(l) {
  return {
    id: l.id,
    firstName: l.firstName,
    lastName: l.lastName,
    email: l.email || '',
    phone: formatPhoneUS(l.phone || ''),
    source: l.source || 'referral',
    status: LEAD_STATUS_BACK_TO_FRONT[l.status] || 'new',
    notes: l.notes || '',
    interestType: l.interestType || '',
    policyInterest: Array.isArray(l.policyInterests) && l.policyInterests.length
      ? l.policyInterests
      : (l.interestType ? [l.interestType] : []),
    assignedTo: l.agent?.displayName || 'Agente',
    assignedLabel: l.assignedLabel || '',
    createdAt: l.createdAt ? l.createdAt.split('T')[0] : '',
    convertedAt: l.convertedAt ? l.convertedAt.split('T')[0] : null,
    followUp: l.followUpDate || '',
    lang: l.language || 'spanish',
  };
}

function mapLeadToApi(l) {
  return {
    firstName: l.firstName || '',
    lastName: l.lastName || '',
    email: l.email || null,
    phone: l.phone || null,
    source: l.source || null,
    status: LEAD_STATUS_FRONT_TO_BACK[(l.status || 'new').toLowerCase()] || 'NEW',
    interestType: Array.isArray(l.policyInterest) && l.policyInterest.length
      ? l.policyInterest[0]
      : (l.interestType || null),
    // Persist the FULL multi-select list (a lead can want several). interestType
    // above keeps the first value for legacy/back-compat displays.
    policyInterests: Array.isArray(l.policyInterest) ? l.policyInterest.filter(Boolean) : undefined,
    notes: l.notes || null,
    assignedLabel: l.assignedLabel || null,
    language: l.lang || null,
    followUpDate: l.followUp || null,
  };
}

export async function listLeads({ page = 1, limit = 200 } = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  const r = await apiFetch(`/api/v1/leads?${qs.toString()}`);
  if (!r.ok) throw new Error(`listLeads failed: ${r.status}`);
  const json = await r.json();
  return {
    leads: (json.data || []).map(mapLeadFromApi),
    pagination: json.pagination,
  };
}

export async function createLead(lead) {
  const r = await apiFetch('/api/v1/leads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapLeadToApi(lead)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createLead failed: ${r.status}`);
  }
  const json = await r.json();
  return mapLeadFromApi(json.data);
}

export async function updateLead(id, patch) {
  const r = await apiFetch(`/api/v1/leads/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapLeadToApi(patch)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateLead failed: ${r.status}`);
  }
  const json = await r.json();
  return mapLeadFromApi(json.data);
}

export async function deleteLead(id) {
  const r = await apiFetch(`/api/v1/leads/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteLead failed: ${r.status}`);
  }
  return true;
}

// ── Commissions ──────────────────────────────────────────────────────────────
//
// Backend only exposes GET / (list) + POST /import (bulk CSV) + POST /match.
// No single-commission create/update endpoints, so this module currently only
// supports listing for hydration. Writes still go through the existing
// localStorage path until a manual-create endpoint is added.

const COMM_STATUS_BACK_TO_FRONT = {
  PENDING: 'pending', MATCHED: 'paid', UNMATCHED: 'unmatched',
  DISPUTED: 'disputed', PAID: 'paid',
};
const COMM_TYPE_BACK_TO_FRONT = {
  NEW_BUSINESS: 'new', RENEWAL: 'renew', BONUS: 'bonus', OVERRIDE: 'override',
};
const COMM_TYPE_FRONT_TO_BACK = {
  new: 'NEW_BUSINESS', renew: 'RENEWAL', bonus: 'BONUS', override: 'OVERRIDE',
};
const COMM_STATUS_FRONT_TO_BACK = {
  pending: 'PENDING', paid: 'MATCHED', unmatched: 'UNMATCHED', disputed: 'DISPUTED',
};

function mapCommissionFromApi(c) {
  const dateStr = c.commissionDate ? c.commissionDate.split('T')[0] : '';
  const month = dateStr.slice(0, 7); // "YYYY-MM"
  // Prefer the verbatim values captured at upload time over values derived
  // from the linked CRM client/policy. Unmatched rows have no client, so
  // without clientNameRaw the carrier's name would be lost on reload.
  return {
    id: c.id,
    month,
    carrier: c.carrier || '',
    policyType: c.policyTypeRaw || (c.policy?.type ? String(c.policy.type).toLowerCase() : ''),
    clientNameRaw:
      c.clientNameRaw ||
      (c.client ? `${c.client.firstName} ${c.client.lastName}`.trim() : ''),
    clientId: c.clientId || null,
    memberIdRaw: c.memberIdRaw || c.policy?.policyNumber || '',
    amount: c.amount != null ? Number(c.amount) : 0,
    matchScore: c.matchConfidence != null ? Math.round(c.matchConfidence * 100) : 0,
    saleType: c.rawSaleType || COMM_TYPE_BACK_TO_FRONT[c.type] || 'new',
    state: c.stateRaw || '',
    status: c.rawStatus || COMM_STATUS_BACK_TO_FRONT[c.status] || 'pending',
    // UNA FILA ATADA A UN ARCHIVO ES DE UNA SUBIDA, aunque le falte el sello.
    // El importador de CSV solo guardaba `commissionFileId`, asi que sus filas
    // se leian como "a mano": sumaban en el total y NO salian en el Historial
    // de Subidas, que es justo lo que hacia que no cuadrara y no hubiera forma
    // de borrarlas (Maricarmen Nieto, 2026-09-10). Mirar tambien el archivo
    // recupera las que ya estan guardadas, sin tocar la base.
    source: (c.sourceFile || c.batchTs || c.commissionFileId) ? 'upload' : 'manual',
    dueMonth: dateStr,
    importedAt: c.createdAt ? c.createdAt.split('T')[0] : '',
    srcFile: c.sourceFile || (c.commissionFile && c.commissionFile.fileName) || '',
    batchTs: c.batchTs || '',
    commissionFileId: c.commissionFileId || '',
    notes: c.notes || '',
  };
}

export async function listCommissions({ page = 1, limit = 500 } = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  const r = await apiFetch(`/api/v1/commissions?${qs.toString()}`);
  if (!r.ok) throw new Error(`listCommissions failed: ${r.status}`);
  const json = await r.json();
  return {
    commissions: (json.data || []).map(mapCommissionFromApi),
    summary: json.summary,
    pagination: json.pagination,
  };
}

// Build a commissionDate from frontend's `month` ("YYYY-MM") or `dueMonth`/explicit date.
function commissionDateFromFront(c) {
  if (c.dueMonth) return c.dueMonth;
  if (c.month && /^\d{4}-\d{2}$/.test(c.month)) return `${c.month}-01`;
  // LOCAL day, not UTC: toISOString() rolls to tomorrow after ~8pm in Florida
  // (UTC-4/-5), bucketing the commission into the wrong month.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export async function deleteCommission(id) {
  const r = await apiFetch(`/api/v1/commissions/${id}`, { method: 'DELETE' });
  // DELETE is idempotent: a 404 means the row isn't on the backend (e.g.
  // localStorage-only rows from a file upload that was never persisted
  // server-side). The caller's goal — "this commission should not exist"
  // — is already satisfied, so treat it as success and let the UI drop
  // it locally instead of blocking the whole batch delete.
  if (r.status === 404) return true;
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteCommission failed: ${r.status}`);
  }
  return true;
}

export async function createCommission(c) {
  const payload = {
    carrier: c.carrier || 'Unknown',
    amount: Number(String(c.amount ?? 0).replace(/[^0-9.\-]/g, '')) || 0,
    commissionDate: commissionDateFromFront(c),
    type: COMM_TYPE_FRONT_TO_BACK[(c.saleType || 'new').toLowerCase()] || 'NEW_BUSINESS',
    status: COMM_STATUS_FRONT_TO_BACK[(c.status || 'pending').toLowerCase()] || 'PENDING',
    notes: c.notes || null,
    clientId: isUuid(c.clientId) ? c.clientId : undefined,
    policyId: isUuid(c.policyId) ? c.policyId : undefined,
  };
  const r = await apiFetch('/api/v1/commissions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createCommission failed: ${r.status}`);
  }
  const json = await r.json();
  return mapCommissionFromApi(json.data);
}

// Update a single commission — mainly to (re)assign or clear its client
// (pass clientId, or clientId:null to unassign). Returns the mapped row so the
// UI can replace it in place. Correction persists across refresh.
export async function updateCommission(id, patch) {
  const r = await apiFetch(`/api/v1/commissions/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateCommission failed: ${r.status}`);
  }
  const json = await r.json();
  return mapCommissionFromApi(json.data);
}

// Persist a whole upload batch the user already reviewed/confirmed. The
// backend stores each row faithfully (no re-matching) and echoes back the
// created rows with real UUIDs so delete / batch-delete work afterward.
export async function importCommissionBatch(records, { confirmarDuplicado = false } = {}) {
  const r = await apiFetch('/api/v1/commissions/import-batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ records, confirmarDuplicado }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    // ARCHIVO YA SUBIDO: no es un fallo, es una pregunta. El error lleva los
    // numeros de la subida anterior para que la pantalla pueda enseñarlos y
    // dejar decidir — una aseguradora puede mandar el mismo nombre cada mes,
    // asi que esto NO se puede rechazar de plano.
    if (r.status === 409 && err.codigo === 'SUBIDA_REPETIDA') {
      const e = new Error(err.error || 'Este archivo ya se subió antes');
      e.codigo = 'SUBIDA_REPETIDA';
      e.duplicados = err.duplicados || [];
      throw e;
    }
    throw new Error(err.error || `importCommissionBatch failed: ${r.status}`);
  }
  const json = await r.json();
  return (json.data || []).map(mapCommissionFromApi);
}

// ── Appointments / Calendar Events ───────────────────────────────────────────
//
// Frontend uses {date, time} separate fields and a `type` enum for the
// visual category (appointment, renewal, call, deadline, birthday).
// Backend uses {startTime, endTime} as DateTime + a free-form string `type`.
// We store the frontend type in the backend's type field verbatim so the
// visual category survives a round-trip.

function combineDateTimeToISO(date, time) {
  if (!date) return new Date().toISOString();
  const t = time && /^\d{2}:\d{2}$/.test(time) ? time : '09:00';
  return new Date(`${date}T${t}:00`).toISOString();
}

export function mapAppointmentFromApi(a) {
  const start = a.startTime ? new Date(a.startTime) : null;
  return {
    id: a.id,
    // Build the date from LOCAL getters (matching `time` below). Using
    // toISOString() returns the UTC date, so an evening appointment in Florida
    // (UTC-4/-5) landed on the next day's calendar cell
    // (feedback_local_day_not_utc).
    date: start
      ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
      : '',
    time: start
      ? `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`
      : '',
    title: a.title || '',
    // Cuánto dura (Lissette 2026-10-17: "no me deja colocar cuánto tiempo será
    // la reunión"). Antes no viajaba y cada edición la volvía a dejar en 1 h.
    durationMinutes: (() => {
      const fin = a.endTime ? new Date(a.endTime) : null;
      const m = start && fin ? Math.round((fin.getTime() - start.getTime()) / 60000) : 0;
      return m > 0 ? m : 60;
    })(),
    type: a.type || 'appointment',
    clientId: a.clientId || '',
    notes: a.description || '',
    location: a.location || '',
    status: a.status || 'scheduled',
    // Auto-generated metadata (renewal job populates these). Frontend can
    // use them to badge auto-events and link back to the source policy.
    source: a.source || null,
    // External personal-calendar mirror events (source='external_cal') come
    // back with masked=true for viewers not allowed to see the real title —
    // the frontend then shows a localized "Busy" block. They are read-only
    // (no edit/delete) because they're a one-way mirror of Google/Outlook.
    masked: a.masked === true,
    // ¿Puede ESTA persona editarla? Desde E-421 toda la agencia ve todas las
    // citas, pero cambiarlas sigue siendo del grupo de quien la atiende.
    editable: a.editable !== false,
    policyId: a.policyId || null,
    policy: a.policy || null,
    // Para qué líneas y para qué agentes cierra la hora (Lissette 2026-10-11).
    // Vacías = todas las líneas / solo quien la creó, como siempre.
    bloqueaLineas: Array.isArray(a.bloqueaLineas) ? a.bloqueaLineas : [],
    bloqueaAgentes: Array.isArray(a.bloqueaAgentes) ? a.bloqueaAgentes : [],
    // Quién la atiende (dueño de la cita). Se puede pasar a otra persona desde
    // "👤 Atiende" en el Calendario (Lissette 2026-10-17).
    agentId: a.agentId || a.agent?.id || null,
    agentName: a.agent?.displayName || null,
  };
}

function mapAppointmentToApi(e) {
  const startISO = combineDateTimeToISO(e.date, e.time);
  // La duración que eligió (durationMinutes); sin ella, 1 hora como siempre.
  const minutos = Math.max(5, Math.min(24 * 60, Number(e.durationMinutes) || 60));
  const endISO = e.endTime
    ? new Date(e.endTime).toISOString()
    : new Date(new Date(startISO).getTime() + minutos * 60 * 1000).toISOString();
  return {
    title: e.title || 'Untitled',
    description: e.notes || null,
    startTime: startISO,
    endTime: endISO,
    location: e.location || null,
    type: e.type || 'appointment',
    clientId: isUuid(e.clientId) ? e.clientId : undefined,
    // Solo cuando el formulario las trae: ausentes no se tocan en el servidor.
    ...(Array.isArray(e.bloqueaLineas) ? { bloqueaLineas: e.bloqueaLineas } : {}),
    ...(Array.isArray(e.bloqueaAgentes) ? { bloqueaAgentes: e.bloqueaAgentes } : {}),
    // Solo cuenta al editar: si cambia, el servidor pasa la cita a esa persona.
    ...(e.agentId ? { agentId: e.agentId } : {}),
  };
}

export async function listAppointments() {
  const r = await apiFetch('/api/v1/calendar');
  if (!r.ok) throw new Error(`listAppointments failed: ${r.status}`);
  const json = await r.json();
  return {
    events: (json.data || []).map(mapAppointmentFromApi),
  };
}

export async function createAppointment(event) {
  const r = await apiFetch('/api/v1/calendar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapAppointmentToApi(event)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createAppointment failed: ${r.status}`);
  }
  const json = await r.json();
  return mapAppointmentFromApi(json.data);
}

export async function updateAppointment(id, patch) {
  const r = await apiFetch(`/api/v1/calendar/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapAppointmentToApi(patch)),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateAppointment failed: ${r.status}`);
  }
  const json = await r.json();
  return mapAppointmentFromApi(json.data);
}

export async function deleteAppointment(id) {
  const r = await apiFetch(`/api/v1/calendar/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteAppointment failed: ${r.status}`);
  }
  return true;
}

// Manually trigger the renewal-reminder job. Returns { created, skipped, errors }.
// Useful right after entering policies so the calendar populates without
// waiting for the next 24h tick.
export async function generateRenewalReminders() {
  const r = await apiFetch('/api/v1/calendar/generate-renewals', { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `generateRenewalReminders failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data || { created: 0, skipped: 0, errors: 0 };
}

// ── External personal calendar (Google / Outlook) — one-way feed ─────────────
// The agent connects their OWN secret iCal URL; the backend mirrors events into
// the calendar as "Busy" blocks so seats don't double-book. The raw URL is a
// credential — the status endpoint never returns it.

// Returns { connected, provider, syncedAt, error }.
export async function getExternalCalStatus() {
  const r = await apiFetch('/api/v1/calendar/external/status');
  if (!r.ok) throw new Error(`getExternalCalStatus failed: ${r.status}`);
  const json = await r.json();
  return json.data || { connected: false, provider: null, syncedAt: null, error: null };
}

// Connect (or replace) the feed. Returns { connected, provider, syncedAt, error, eventsImported }.
export async function connectExternalCal(url, provider) {
  const r = await apiFetch('/api/v1/calendar/external', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, provider }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `connectExternalCal failed: ${r.status}`);
  return json.data;
}

// Trigger an immediate re-sync. Returns { syncedAt, error, eventsImported }.
export async function syncExternalCal() {
  const r = await apiFetch('/api/v1/calendar/external/sync', { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `syncExternalCal failed: ${r.status}`);
  return json.data;
}

// Disconnect: clears the credential + removes mirrored events.
export async function disconnectExternalCal() {
  const r = await apiFetch('/api/v1/calendar/external', { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `disconnectExternalCal failed: ${r.status}`);
  }
  return true;
}

// ── Users (Team) ─────────────────────────────────────────────────────────────

const ROLE_BACK_TO_FRONT = {
  PLATFORM_OWNER: 'agency_owner',
  AGENCY_OWNER: 'agency_owner',
  AGENCY_ADMIN: 'agency_admin',
  AGENT: 'agent',
  SOLO_AGENT: 'solo_agent',
  SEAT: 'assistant', // Frontend keeps the legacy "assistant" role string for backward compat with all components.
};

const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#ef4444', '#14b8a6'];

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || (name?.slice(0, 2).toUpperCase() ?? 'U');
}

function mapUserFromApi(u, idx = 0) {
  return {
    id: u.id,
    name: u.displayName || u.email,
    email: u.email,
    role: ROLE_BACK_TO_FRONT[u.role] || 'agent',
    npn: u.npn || '',
    // Correo de contacto del agente: un seat lo necesita para pintar la firma
    // de su agente supervisor, igual que ya hace con el NPN y las redes.
    contactEmail: u.contactEmail || '',
    // Segundo correo y fijo de oficina (Lissette 2026-08-19). Viajan igual que
    // el primero para que un asiento pueda pintar la firma de su agente.
    contactEmail2: u.contactEmail2 || '',
    officePhone: u.officePhone || '',
    // WhatsApp y fax (Lissette 2026-08-29), con las mismas reglas.
    whatsappNumber: u.whatsappNumber || '',
    fax: u.fax || '',
    // Professional identity carried in the roster so a seat can PRE-LOAD its
    // supervising agent's values in My Profile (#4). Empty for members whose
    // profile hasn't set them.
    licenseStates: u.licenseStates || '',
    companyName: u.companyName || '',
    companyLogo: u.companyLogo || '',
    // La forma de ese logo (2026-09-23): un asiento pinta el logo de su agente
    // con la forma que eligió su agente. Ver crm-app/formaDelLogo.js.
    companyLogoShape: u.companyLogoShape || 'rectangular',
    website: u.website || '',
    // Social profiles ride along for the same reason: a seat renders its
    // supervising agent's icons on the card and in the signature preview.
    instagramUrl: u.instagramUrl || '',
    facebookUrl: u.facebookUrl || '',
    linkedinUrl: u.linkedinUrl || '',
    tiktokUrl: u.tiktokUrl || '',
    mailingStreet: u.mailingStreet || '',
    mailingCity: u.mailingCity || '',
    mailingState: u.mailingState || '',
    mailingZip: u.mailingZip || '',
    initials: initialsFromName(u.displayName || u.email),
    color: COLORS[idx % COLORS.length],
    pin: '',
    parentAgentId: u.supervisor?.id || u.supervisorId || null,
    parentAgentName: u.supervisor?.displayName || null,
    agencyId: null,
    phone: u.phone || '',
    isActive: u.isActive,
    seatLabel: u.seatLabel || null,
    seatPermissions: u.seatPermissions && typeof u.seatPermissions === 'object' ? u.seatPermissions : null,
    seatScope: u.seatScope || null,
    avatarUrl: u.avatarUrl || null,
    profilePhoto: u.profilePhoto || null,
  };
}

export async function listUsers() {
  const r = await apiFetch('/api/v1/team/members');
  if (!r.ok) throw new Error(`listUsers failed: ${r.status}`);
  const json = await r.json();
  return {
    users: (json.data || []).map((u, i) => mapUserFromApi(u, i)),
  };
}

// Owner-only per-agent production report (Paso 3). Returns aggregated numbers
// only: { rows: [{ userId, name, role, clientsActive, policiesActive,
// annualPremium, commissionMonth, lastActivityAt }], totals, monthStart }.
export async function getAgentProduction() {
  const r = await apiFetch('/api/v1/team/production');
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `getAgentProduction failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Owner-only agency-wide analytics (Agency/Enterprise plan). Accepts filters
// { from, to, agentId, policyType, carrier } and returns aggregated numbers
// only (kpis, policyMix, topCarriers, leadSources, keyIndicators, filters).
export async function getAgencyAnalytics(filters = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const r = await apiFetch(`/api/v1/team/analytics${suffix}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    const err = new Error(e.error || `getAgencyAnalytics failed: ${r.status}`);
    err.code = e.code;
    throw err;
  }
  return (await r.json()).data;
}

// Aggregate summary of a saved client report — COUNTS ONLY, never client rows.
// This is what lets a client report ride in a dashboard and in the scheduled
// email: the report itself is full of PHI, the summary is not.
export async function getClientReportSummary(reportId, groupBy = 'status') {
  const qs = new URLSearchParams({ groupBy });
  const r = await apiFetch(`/api/v1/reports/custom/${encodeURIComponent(reportId)}/summary?${qs}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `getClientReportSummary failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Server-side aggregate of the sales report. The on-screen version is computed
// in the browser; this is the same question asked of the database, so it can
// ride in a dashboard and in the scheduled email. Aggregates only.
export async function getSalesSummary({ dimension = 'analyst', measure = 'count', from, to, type, status } = {}) {
  const qs = new URLSearchParams({ dimension, measure });
  for (const [k, v] of Object.entries({ from, to, type, status })) {
    if (v) qs.set(k, v);
  }
  const r = await apiFetch(`/api/v1/reports/sales-summary?${qs}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `getSalesSummary failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// One block of the Métricas screen, computed by the server. The screen adds it
// all up in the browser; this asks the database the same questions so a block
// can sit in a panel and be mailed on a schedule. Returns { title, columns,
// rows, chartKey?, note? } — the note says what the block does NOT cover.
export async function getMetricsSummary({ block = 'kpis', from, to } = {}) {
  const qs = new URLSearchParams({ block });
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const r = await apiFetch(`/api/v1/reports/metrics-summary?${qs}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `getMetricsSummary failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── Team dashboards ─────────────────────────────────────────────────────────
// A dashboard is several widgets over the team metrics sharing ONE set of
// filters. It stores only the recipe (which metric, which chart, which
// columns) — opening it re-runs the same /reports/team queries, so a panel can
// never show a number that disagrees with the report it was built from.
export async function listTeamDashboards() {
  const r = await apiFetch('/api/v1/reports/dashboards');
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `listTeamDashboards failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function createTeamDashboard(payload) {
  const r = await apiFetch('/api/v1/reports/dashboards', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `createTeamDashboard failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function updateTeamDashboard(id, payload) {
  const r = await apiFetch(`/api/v1/reports/dashboards/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `updateTeamDashboard failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function deleteTeamDashboard(id) {
  const r = await apiFetch(`/api/v1/reports/dashboards/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `deleteTeamDashboard failed: ${r.status}`);
  }
  return true;
}

// Active members ("vidas aseguradas") by policy type. A client is a household,
// a member is an insured life — a family of four is 1 client and 4 members, and
// the four may be on health while only two are on dental. Counts come from the
// per-policy insured selection, so the same person legitimately appears under
// two types: `byType[].members` sums to `totals.memberPolicies`, NOT to
// `totals.uniqueMembers`. Policies where nobody was ever marked insured are
// reported separately in `undefinedPolicies` instead of being guessed at.
// Filters: { status = 'ACTIVE', type }. Aggregate counts only — no names.
export async function getMembersReport(filters = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const r = await apiFetch(`/api/v1/reports/members${suffix}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `getMembersReport failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── Team Invitations ────────────────────────────────────────────────────────

const INVITE_ROLE_FRONT_TO_BACK = {
  agent: 'AGENT',
  solo_agent: 'SOLO_AGENT',
  agency_admin: 'AGENCY_ADMIN',
  assistant: 'SEAT', // legacy "assistant" string in the frontend → SEAT enum on the backend
  seat: 'SEAT',
};

// Send a new invite. Returns { id, email, role, inviteUrl, emailSent, expiresAt }.
// promoteToAdmin: Solo "add one admin" add-on — invitee joins as an AGENT and
// is auto-promoted to a full-access admin (AGENCY_OWNER) on accept. The backend
// only honors it for a SOLO_AGENT inviter on a SOLO plan, capped at one.
export async function sendTeamInvite({ email, role, promoteToAdmin = false }) {
  const backendRole = INVITE_ROLE_FRONT_TO_BACK[(role || 'assistant').toLowerCase()] || 'SEAT';
  const r = await apiFetch('/api/v1/team/invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role: backendRole, promoteToAdmin: !!promoteToAdmin }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    // EL `code` VIAJA. La pantalla ofrece "compra un asiento" cuando el
    // servidor dice SEAT_LIMIT_REACHED, no comparando el texto del mensaje.
    const err = new Error(json.error || `sendTeamInvite failed: ${r.status}`);
    err.code = json.code || null;
    err.limite = json.limite ?? null;
    // Si esta en prueba, el asiento no se cobra ahora sino al activar el plan.
    // Lo decide el servidor; la pantalla solo cambia lo que dice el cartel.
    err.enPrueba = json.enPrueba === true;
    throw err;
  }
  return json.data;
}

export async function listTeamInvitations() {
  const r = await apiFetch('/api/v1/team/invitations');
  if (!r.ok) throw new Error(`listTeamInvitations failed: ${r.status}`);
  const json = await r.json();
  return { invitations: json.data || [] };
}

export async function revokeTeamInvitation(id) {
  const r = await apiFetch(`/api/v1/team/invitations/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `revokeTeamInvitation failed: ${r.status}`);
  }
  return true;
}

export async function resendTeamInvitation(id) {
  const r = await apiFetch(`/api/v1/team/invitations/${id}/resend`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `resendTeamInvitation failed: ${r.status}`);
  return json.data;
}

// Public lookup of invitation metadata by token (no auth needed)
export async function lookupInvitation(token) {
  const r = await fetch(`${API_BASE}/auth/invitation/${encodeURIComponent(token)}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `Invitation lookup failed: ${r.status}`);
  return json.data;
}

// Accept invitation — caller must be authenticated as the invited email
export async function acceptInvitation(token) {
  const r = await apiFetch(`/auth/invitation/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    // EL `code` VIAJA (E-309). La pantalla de /join explica en español por qué
    // no se puede aceptar —"tu cuenta es la dueña de una agencia"— y para eso
    // necesita el motivo, no el texto en inglés de la API.
    const err = new Error(json.error || `acceptInvitation failed: ${r.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json.data;
}

// Self-serve registration after a fresh Microsoft sign-in.
// `/auth/register` is authenticated — the MSAL token is what proves the user
// owns the email/oid being attached to a new agency. Returns the created
// { user, agency } payload.
// `referralCode` viaja con el registro cuando la persona llegó por el enlace de
// otro agente (?ref=CODE). Un código malo NO puede tumbar el alta: el backend
// lo anota si sirve y lo registra en el log si no — la cuenta se crea igual.
export async function registerAccount({ agencyName, plan, language, inviteCode, referralCode }) {
  const r = await apiFetch('/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agencyName, plan, language, inviteCode, referralCode }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    // Surface the backend code so the form can render a specific message
    // for INVITE_CODE_REQUIRED / INVITE_CODE_INVALID / etc.
    const err = new Error(json.error || `Registration failed: ${r.status}`);
    if (json.code) err.code = json.code;
    throw err;
  }
  return json.data;
}

// ── Billing ─────────────────────────────────────────────────────────────────
//
// All endpoints assume an authenticated agent. /products is read-only — the
// other two start a Stripe-hosted session and return a URL the frontend
// redirects to.

// GET /billing/products → { interval, current: {...}, plans: [...], addons: [...] }
// `current.daysLeft` is server-computed off `trialEndsAt`; the UI can use it
// directly without re-parsing dates.
//
// `interval` selects the catalogue: 'month' (default) or 'year' for the
// 15%-off annual prices. Founder agencies always get their monthly Founder
// price back — Founder pricing doesn't stack with the annual discount — and
// each product then carries annualUnavailable:true so the UI can explain why.
export async function listBillingProducts(interval) {
  const qs = interval === 'year' ? '?interval=year' : '';
  const r = await apiFetch(`/api/v1/billing/products${qs}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listBillingProducts failed: ${r.status}`);
  return json.data;
}

// POST /billing/checkout — exchange a Stripe `priceId` for a Checkout URL.
// The backend whitelists allowed priceIds against env vars, so we can pass
// whatever the /products endpoint returned. On success the caller should
// `window.location = data.url` to send the user to Stripe Checkout.
export async function createBillingCheckout(priceId) {
  const r = await apiFetch('/api/v1/billing/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ priceId }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createBillingCheckout failed: ${r.status}`);
  return json.data; // { url }
}

// POST /billing/seats — AGREGAR UN ASIENTO. Es la salida del tope: el servidor
// bloquea al llegar al limite y esto es lo que deja pasarlo.
//
// DOS CAMINOS, y el que toca lo decide el SERVIDOR, no esta pantalla:
//   - Con plan activo  -> se cobra en el acto, prorrateado hasta el proximo
//                         recibo, dentro de la suscripcion que ya tiene.
//   - En prueba        -> se agrega sin cobrar nada ahora y se le cobra al
//                         activar el plan, porque el checkout cuenta los
//                         asientos que la agencia tiene de verdad. Viene
//                         marcado con `enPrueba: true`.
//
// Devuelve `{ maxAssistants, cobradoCentavos, enPrueba }` para que la pantalla
// diga lo que de verdad paso y refleje el tope nuevo sin recargar.
export async function comprarAsiento() {
  const r = await apiFetch('/api/v1/billing/seats', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `comprarAsiento failed: ${r.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json.data;
}

// POST /billing/portal — opens the Stripe-hosted Customer Portal where the
// agent updates payment method, cancels, or changes plan. Requires an
// existing Stripe customer (created on first checkout) — 404 otherwise.
export async function createBillingPortal() {
  const r = await apiFetch('/api/v1/billing/portal', { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createBillingPortal failed: ${r.status}`);
  return json.data; // { url }
}

// ── Documents ────────────────────────────────────────────────────────────────
//
// Read-only for now. Upload migration requires building a SAS-token issuance
// endpoint on the backend so the frontend can PUT files directly to Blob
// Storage, then POST metadata to /documents/upload. Out of scope for this
// session — see deferred_tasks.md.

const _MIME_BY_EXT = {
  pdf: 'application/pdf',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', bmp: 'image/bmp', heic: 'image/heic', heif: 'image/heif',
  csv: 'text/csv', txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export function mapDocumentFromApi(d) {
  const sizeBytes = d.sizeBytes || 0;
  const sizeStr =
    sizeBytes > 1024 * 1024
      ? `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`
      : `${Math.round(sizeBytes / 1024)} KB`;
  return {
    id: d.id,
    clientId: d.clientId || null,
    clientName: d.client ? `${d.client.firstName} ${d.client.lastName}`.trim() : '',
    name: d.name,
    type: d.category || d.type || 'other',
    // Real MIME per stored type. It used to be `application/${d.type}`, so a
    // photo came back as "application/jpg" and every `startsWith('image/')`
    // check missed it — a renamed document (no extension left in the name) then
    // showed the generic file icon instead of a thumbnail.
    mimeType: _MIME_BY_EXT[String(d.type || '').toLowerCase()]
      || (d.type ? `application/${d.type}` : 'application/octet-stream'),
    // Keep the FULL ISO timestamp (not date-only). PR #367 (Lissette
    // 2026-05-27) fixed several write sites but missed this read-side
    // mapper. The per-tab badge counter compares against a full-ISO
    // mount-time stamp, so a date-only string parses as midnight UTC
    // and the upload appears "older than mount" → 0 badge.
    uploadedAt: d.createdAt || '',
    uploadedBy: d.uploadedBy?.seatLabel || d.uploadedBy?.displayName || '',
    size: sizeStr,
    blobUrl: d.blobUrl,
    welcomeKit: !!d.welcomeKit,
    // An eligibility-slot upload tags lineCategory "elig:<alertId>" so the
    // requested-document slot re-matches the saved file after a refresh (else the
    // slot reappears empty and the file looks lost). Turn the marker back into
    // eligAlertId/source here; hide it from the plain lineCategory so it never
    // shows as a policy line-category label.
    ...(typeof d.lineCategory === 'string' && d.lineCategory.startsWith('elig:')
      ? { eligAlertId: d.lineCategory.slice(5), source: 'eligibility', lineCategory: null }
      : { lineCategory: d.lineCategory || null }),
    // Multi-kit membership (policy-type ids + "__all__" + "portal"). When the
    // backend has no `kits` array yet (doc never edited with the new picker),
    // derive it from the legacy welcomeKit/lineCategory pair.
    kits: Array.isArray(d.kits)
      ? d.kits
      : (d.welcomeKit ? (d.lineCategory ? [d.lineCategory] : ['__all__']) : []),
    // Documents-tab folder filing: which folder (and policy-type tab) this doc
    // is in. Both null ⇒ loose in "General". Separate from lineCategory/kit.
    folder: d.folder || null,
    folderType: d.folderType || null,
    // Link back to the document request this upload fulfilled (set when the
    // client uploads through a request's portal). Lets the SPA refresh that
    // request's status live when a document.created event arrives.
    documentRequestId: d.documentRequestId || null,
    required: false,
    data: null, // not embedding the file bytes — they live in Azure Blob
  };
}

export async function listDocuments({ clientId, documentRequestId } = {}) {
  const qs = new URLSearchParams();
  if (clientId) qs.set('clientId', clientId);
  if (documentRequestId) qs.set('documentRequestId', documentRequestId);
  const path = qs.toString() ? `/api/v1/documents?${qs.toString()}` : '/api/v1/documents';
  const r = await apiFetch(path);
  if (!r.ok) throw new Error(`listDocuments failed: ${r.status}`);
  const json = await r.json();
  return {
    documents: (json.data || []).map(mapDocumentFromApi),
  };
}

// M1: list the files a client uploaded through a doc-request's public portal.
// Replaces the legacy localStorage cache (uploadsLocal.js), which only ever
// saw demo uploads made in the same browser — real portal uploads land as
// backend Document rows tagged with documentRequestId. The blobs live in a
// private container, so callers mint a short-lived view URL per file via
// getDocumentReadUrl when they actually need to display/download it.
// HEIC is NOT here on purpose: no desktop browser can render it, so treating it
// as an image only produced a blank <img>. It gets its own flag and its own
// "how do I open this?" tile instead.
const _IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const _HEIC_EXTS = ['heic', 'heif'];
export async function listUploadsForRequest(requestId) {
  if (!requestId) return [];
  const { documents } = await listDocuments({ documentRequestId: requestId });
  return documents.map((d) => {
    const ext = (d.name || '').split('.').pop().toLowerCase();
    return {
      id: d.id,
      name: d.name,
      ext,
      isImage: _IMAGE_EXTS.includes(ext),
      isHeic: _HEIC_EXTS.includes(ext),
      sizeStr: d.size,
      uploadedAt: d.uploadedAt,
      uploadedBy: d.uploadedBy,
    };
  });
}

// Upload any File object straight to Azure Blob via a short-lived SAS URL and
// return the permanent (unsigned) blobUrl. No size limit through our API (the
// bytes go browser → blob). Used by Team Training to store large videos/files.
// Pass onProgress(0..1) to drive a progress bar.
export async function uploadFileToBlob(file, onProgress) {
  const sasRes = await apiFetch('/api/v1/documents/sas', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  });
  if (!sasRes.ok) { const e = await sasRes.json().catch(() => ({})); throw new Error(e.error || `sas failed: ${sasRes.status}`); }
  const { data: sas } = await sasRes.json();
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sas.uploadUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (ev) => { if (onProgress && ev.lengthComputable) onProgress(ev.loaded / ev.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`blob upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('blob upload failed (network)'));
    xhr.send(file);
  });
  return sas.blobUrl;
}

// Upload a File object: ask backend for a SAS URL, PUT directly to Blob,
// then save the metadata. The bytes never transit through our API.
export async function uploadDocument(file, { clientId, category, lineCategory, folder, folderType } = {}) {
  // 1) Request SAS
  const sasRes = await apiFetch('/api/v1/documents/sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  });
  // Cada paso marca en el error cuál fue y el estado HTTP, para que la
  // pantalla diga POR QUÉ (crm-app/motivoDeSubidaFallida.js, E-416) en vez de
  // "1 archivo(s) fallaron".
  const errorDeSubida = (payload, fallback, status, etapa) => {
    const e = makeApiError(payload, fallback);
    e.status = status;
    e.etapa = etapa;
    return e;
  };
  if (!sasRes.ok) {
    const err = await sasRes.json().catch(() => ({}));
    throw errorDeSubida(err, `sas failed: ${sasRes.status}`, sasRes.status, sasRes.status === 403 ? 'permiso' : 'sas');
  }
  const { data: sas } = await sasRes.json();

  // 2) PUT bytes to Azure Blob (no auth header — SAS is in the query string)
  let putRes;
  try {
    putRes = await fetch(sas.uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });
  } catch (netErr) {
    // "Failed to fetch": la red no dejó salir el archivo hacia Azure.
    throw errorDeSubida(null, `blob upload failed: ${netErr?.message || 'network'}`, 0, 'envio');
  }
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '');
    throw errorDeSubida(null, `blob upload failed: ${putRes.status} ${text.slice(0, 200)}`, putRes.status, 'envio');
  }

  // 3) Persist metadata
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const metaRes = await apiFetch('/api/v1/documents/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: file.name,
      type: ext || 'bin',
      category: category || 'other',
      lineCategory: lineCategory || null,
      blobUrl: sas.blobUrl,
      sizeBytes: file.size,
      clientId: clientId || null,
      // Optional: drop the upload straight into a Documents-tab folder instead
      // of loose in "General" (folderType = policy-type id or "custom:<name>").
      folder: folder || null,
      folderType: folderType || null,
    }),
  });
  if (!metaRes.ok) {
    const err = await metaRes.json().catch(() => ({}));
    throw errorDeSubida(err, `metadata save failed: ${metaRes.status}`, metaRes.status, metaRes.status === 403 ? 'permiso' : 'guardar');
  }
  const { data } = await metaRes.json();
  return mapDocumentFromApi(data);
}

// Get a short-lived read URL to view/download. Pass { download: true } to get
// a URL whose Content-Disposition forces the browser to save the file (under
// its real name) instead of rendering it inline.
export async function getDocumentReadUrl(documentId, { download = false } = {}) {
  const r = await apiFetch('/api/v1/documents/read-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ documentId, download }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `read-sas failed: ${r.status}`);
  }
  const { data } = await r.json();
  return data.url;
}

// Rename a stored document (display name only; the blob path is untouched).
export async function renameDocument(id, name) {
  const r = await apiFetch(`/api/v1/documents/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `renameDocument failed: ${r.status}`);
  }
  const { data } = await r.json();
  return mapDocumentFromApi(data);
}

// Set/clear a document's line-of-business category (a policy-type id, or null).
// Set a document's multi-kit membership (array of kit ids). The backend derives
// welcomeKit + lineCategory from it for the renewal-kit send.
export async function setDocumentKits(id, kits) {
  const r = await apiFetch(`/api/v1/documents/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kits: Array.isArray(kits) ? kits : [] }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setDocumentKits failed: ${r.status}`);
  }
  const { data } = await r.json();
  return mapDocumentFromApi(data);
}

export async function setDocumentCategory(id, lineCategory) {
  const r = await apiFetch(`/api/v1/documents/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lineCategory: lineCategory || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setDocumentCategory failed: ${r.status}`);
  }
  const { data } = await r.json();
  return mapDocumentFromApi(data);
}

// Move a document into a folder (folderType = policy-type id, folder = folder
// name), or back to "General" by passing both null. Metadata-only PATCH.
export async function moveDocument(id, { folderType = null, folder = null } = {}) {
  const r = await apiFetch(`/api/v1/documents/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folderType: folderType || null, folder: folder || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `moveDocument failed: ${r.status}`);
  }
  const { data } = await r.json();
  return mapDocumentFromApi(data);
}

// Read a client's Documents-tab folder registry: { [policyTypeId]: string[] }.
export async function listDocFolders(clientId) {
  const r = await apiFetch(`/api/v1/documents/folders?clientId=${encodeURIComponent(clientId)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `listDocFolders failed: ${r.status}`);
  }
  const { data } = await r.json();
  return (data && data.folders) || {};
}

// Replace a client's folder registry. `folders` is { [policyTypeId]: string[] }.
export async function saveDocFolders(clientId, folders) {
  const r = await apiFetch('/api/v1/documents/folders', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId, folders: folders || {} }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `saveDocFolders failed: ${r.status}`);
  }
  const { data } = await r.json();
  return (data && data.folders) || {};
}

export async function deleteDocument(id) {
  const r = await apiFetch(`/api/v1/documents/${id}`, { method: 'DELETE' });
  // Idempotent delete: a 404 means the row is already gone (a stale/phantom
  // entry, or an id that never existed in the backend — e.g. an optimistic doc
  // whose upload never persisted). The desired end state is "not there", so
  // treat it as success and let the caller drop it locally instead of blocking
  // the user with "Document not found" (Lissette 2026-07-13 ticket).
  if (r.status === 404) return true;
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteDocument failed: ${r.status}`);
  }
  return true;
}

// Mark/unmark a document so it rides along (as a secure download link) in
// the agency's Welcome Kit email sent when a renewal moves to DONE.
export async function setDocumentWelcomeKit(id, on) {
  const r = await apiFetch(`/api/v1/documents/${id}/welcome-kit`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ welcomeKit: !!on }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setDocumentWelcomeKit failed: ${r.status}`);
  }
  const { data } = await r.json();
  return data;
}

// ── Notes (per client) ───────────────────────────────────────────────────────

// Note attachments (photos/files) come back as [{ name, type, size, blobUrl,
// url }] where `url` is a short-lived signed read URL. Map to the `files` shape
// the notes UI renders (fileSrc picks `url`); keep the bare `blobUrl` so an
// edit can re-send the reference without leaking/expiring a SAS query string.
function mapNoteAttachments(list) {
  return (Array.isArray(list) ? list : []).map((a) => ({
    name: a.name || '',
    type: a.type || '',
    size: a.size || 0,
    blobUrl: a.blobUrl || '',
    url: a.url || a.blobUrl || '',
  }));
}

// El camino de vuelta: la referencia desnuda que se guarda en el servidor.
// Manda `blobUrl` y NUNCA la `url` firmada — esa lleva un SAS con caducidad, y
// guardarla dejaría enlaces muertos en la base de datos.
function toAttachmentRef(a) {
  return {
    name: a.name || '',
    type: a.type || '',
    size: a.size || 0,
    blobUrl: a.blobUrl || a.url || '',
  };
}

export function mapNoteFromApi(n) {
  const files = mapNoteAttachments(n.attachments);
  const mapped = {
    id: n.id,
    clientId: n.clientId,
    text: n.content,
    isPinned: !!n.isPinned,
    category: n.category || null,
    files,
    attachments: files, // alias; both point at the mapped list
    authorId: n.authorId,
    authorName: n.author?.displayName || '',
    // Keep the FULL ISO timestamp. Truncating to the date ('.split("T")[0]')
    // dropped the time, so the note header rendered `new Date("2026-07-08")` =
    // midnight UTC = 8:00 PM the day before in Florida (UTC-4). Preserve the
    // instant so the local time shows correctly. (feedback_local_day_not_utc)
    createdAt: n.createdAt || '',
    // Notas escritas en la etapa de PROSPECTO. Se muestran aquí para tener el
    // historial completo, pero se editan donde se escribieron (la pizarra de
    // Prospectos): no hay una segunda copia que pueda separarse de la primera.
    source: n.source || null,
    readOnly: !!n.readOnly,
  };
  // Cuántos comentarios tiene el hilo, para pintar "💬 3" sin traer los
  // cuerpos. Solo lo manda la LISTA de notas; las respuestas de crear/editar
  // una nota no lo llevan.
  //
  // Por eso la clave se omite cuando no viene, en vez de caer a 0: el
  // manejador de 'note.updated' fusiona con `{ ...x, ...n }`, así que un 0 por
  // defecto borraría el contador de la pantalla cada vez que alguien editara
  // el texto de una nota. Omitiéndola, el valor anterior sobrevive.
  if (typeof n.commentCount === 'number') mapped.commentCount = n.commentCount;
  return mapped;
}

// Un comentario del hilo de una nota. Mismo trato que la nota para los
// adjuntos y para la fecha (ISO completo, nunca truncado al día).
export function mapNoteCommentFromApi(x) {
  const files = mapNoteAttachments(x.attachments);
  return {
    id: x.id,
    noteId: x.noteId,
    text: x.content || '',
    files,
    attachments: files,
    authorId: x.authorId,
    authorName: x.author?.displayName || '',
    createdAt: x.createdAt || '',
    updatedAt: x.updatedAt || '',
  };
}

export async function listNotes({ clientId } = {}) {
  const qs = new URLSearchParams();
  if (clientId) qs.set('clientId', clientId);
  const path = qs.toString() ? `/api/v1/notes?${qs.toString()}` : '/api/v1/notes';
  const r = await apiFetch(path);
  if (!r.ok) throw new Error(`listNotes failed: ${r.status}`);
  const json = await r.json();
  return { notes: (json.data || []).map(mapNoteFromApi) };
}

// Solo las MARCAS de las notas de la agencia (cliente, categoria y fecha), sin
// texto ni fotos: es lo unico que necesita el aviso de "hay algo nuevo" de la
// lista de Clientes. Pedir las notas completas de toda la agencia dejaba el
// sistema lento para todos (2026-09-23, ver notes.routes.ts). Estas filas NO
// se mezclan con las notas de verdad: no tienen texto, y editar una borraria
// lo que no se ve.
export async function listNoteStamps() {
  const r = await apiFetch('/api/v1/notes?ligero=1');
  if (!r.ok) throw new Error(`listNoteStamps failed: ${r.status}`);
  const json = await r.json();
  return (json.data || []).map((n) => ({
    id: n.id,
    clientId: n.clientId,
    category: n.category || null,
    createdAt: n.createdAt || '',
  }));
}

// Upload a note photo/file straight to Azure Blob (2-step SAS flow, same as
// uploadDocument) but WITHOUT creating a Document row — note attachments live
// on the note itself. Returns a bare blob reference to store on the note.
export async function uploadNoteAttachment(file) {
  const sasRes = await apiFetch('/api/v1/notes/attachment-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  });
  if (!sasRes.ok) {
    const err = await sasRes.json().catch(() => ({}));
    throw new Error(err.error || `attachment sas failed: ${sasRes.status}`);
  }
  const { data: sas } = await sasRes.json();
  const putRes = await fetch(sas.uploadUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '');
    throw new Error(`blob upload failed: ${putRes.status} ${text.slice(0, 200)}`);
  }
  return { name: file.name, type: file.type || '', size: file.size || 0, blobUrl: sas.blobUrl };
}

export async function createNote({ clientId, text, isPinned, category, attachments }) {
  const body = { clientId, content: text, isPinned: !!isPinned };
  if (category) body.category = category;
  if (Array.isArray(attachments) && attachments.length) body.attachments = attachments;
  const r = await apiFetch('/api/v1/notes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createNote failed: ${r.status}`);
  }
  const json = await r.json();
  return mapNoteFromApi(json.data);
}

export async function updateNote(id, { text, isPinned, attachments } = {}) {
  const body = {};
  if (text !== undefined) body.content = text;
  if (isPinned !== undefined) body.isPinned = !!isPinned;
  // Full replacement of the attachment list. Send bare blobUrls only (never a
  // signed `url`), so the backend stores a clean, non-expiring reference.
  if (attachments !== undefined) {
    body.attachments = (Array.isArray(attachments) ? attachments : []).map(toAttachmentRef);
  }
  const r = await apiFetch(`/api/v1/notes/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateNote failed: ${r.status}`);
  }
  const json = await r.json();
  return mapNoteFromApi(json.data);
}

export async function deleteNote(id) {
  const r = await apiFetch(`/api/v1/notes/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteNote failed: ${r.status}`);
  }
  return true;
}

// ── Comentarios de una nota ──────────────────────────────────────────────────
//
// El hilo NO viaja en listNotes: esa llamada trae TODAS las notas de la agencia
// al arrancar, y meter los cuerpos ahí engordaría la carga inicial. La lista
// solo trae `commentCount`; el hilo se pide al abrirlo.

export async function listNoteComments(noteId) {
  const r = await apiFetch(`/api/v1/notes/${noteId}/comments`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `listNoteComments failed: ${r.status}`);
  }
  const json = await r.json();
  return (json.data || []).map(mapNoteCommentFromApi);
}

export async function addNoteComment(noteId, { text, attachments } = {}) {
  const body = { content: text || '' };
  if (Array.isArray(attachments) && attachments.length) {
    body.attachments = attachments.map(toAttachmentRef);
  }
  const r = await apiFetch(`/api/v1/notes/${noteId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `addNoteComment failed: ${r.status}`);
  }
  const json = await r.json();
  return mapNoteCommentFromApi(json.data);
}

export async function updateNoteComment(commentId, { text, attachments } = {}) {
  const body = {};
  if (text !== undefined) body.content = text;
  if (attachments !== undefined) {
    body.attachments = (Array.isArray(attachments) ? attachments : []).map(toAttachmentRef);
  }
  const r = await apiFetch(`/api/v1/notes/comments/${commentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateNoteComment failed: ${r.status}`);
  }
  const json = await r.json();
  return mapNoteCommentFromApi(json.data);
}

export async function deleteNoteComment(commentId) {
  const r = await apiFetch(`/api/v1/notes/comments/${commentId}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteNoteComment failed: ${r.status}`);
  }
  return true;
}

// ── Signatures ───────────────────────────────────────────────────────────────

export function mapSignatureFromApi(s) {
  return {
    id: s.id,
    clientId: s.clientId,
    clientName: s.client ? `${s.client.firstName} ${s.client.lastName}`.trim() : '',
    documentName: s.documentName,
    signatureImageUrl: s.signatureImageUrl,
    signatureUrl: s.signatureUrl || null, // pre-signed read URL for the signature PNG
    signedDocUrl: s.signedDocUrl || null, // pre-signed read URL for the merged signed PDF
    status: s.status || (s.signatureImageUrl ? 'SIGNED' : 'PENDING'),
    portalToken: s.portalToken || null,
    portalUrl: s.portalUrl || null,
    expiresAt: s.expiresAt || null,
    sentAt: s.sentAt || null,
    // When the client first opened the signing link (null until then). Drives
    // the "Vista" state between Enviado and Firmada.
    viewedAt: s.viewedAt || null,
    signedAt: s.signedAt ? (typeof s.signedAt === 'string' ? s.signedAt.split('T')[0] : new Date(s.signedAt).toISOString().split('T')[0]) : '',
    // El instante completo: `signedAt` de arriba es el día UTC (de antes, lo usan
    // otras pantallas); quien necesite el día LOCAL de la firma usa este.
    signedAtIso: s.signedAt ? new Date(s.signedAt).toISOString() : '',
    clientEmail: s.clientEmail || null,
    ipAddress: s.ipAddress || '',
    createdAt: s.createdAt,
    // Preguntas que respondio el cliente al firmar, y sus respuestas ya
    // resueltas por el servidor (Wendy Cercio, 2026-08-24).
    //
    // Esto es una LISTA BLANCA: lo que no se nombre aqui se pierde sin error,
    // por muy bien que lo mande el servidor. Es la trampa que ya se comio los
    // cuerpos de las plantillas en 2026-05-26.
    questions: Array.isArray(s.questions) ? s.questions : [],
    answers: Array.isArray(s.answers) ? s.answers : [],
  };
}

export async function listSignatures({ clientId } = {}) {
  const qs = new URLSearchParams();
  if (clientId) qs.set('clientId', clientId);
  const path = qs.toString() ? `/api/v1/signatures?${qs.toString()}` : '/api/v1/signatures';
  const r = await apiFetch(path);
  if (!r.ok) throw new Error(`listSignatures failed: ${r.status}`);
  const json = await r.json();
  return { signatures: (json.data || []).map(mapSignatureFromApi) };
}

// Re-send a pending/expired signature request: same portal link, refreshed
// expiry when needed, email re-delivered. Signed requests are rejected (400).
export async function resendSignature(id, { recipientEmail } = {}) {
  const r = await apiFetch(`/api/v1/signatures/${id}/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(recipientEmail ? { recipientEmail } : {}),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.detail || json.error || `resendSignature failed: ${r.status}`);
  return {
    ...mapSignatureFromApi(json.data || {}),
    emailSent: !!(json.data && json.data.emailSent),
    // Igual que en el alta: si no se nombra, el motivo no llega a la pantalla.
    emailError: (json.data && json.data.emailError) || null,
    portalUrl: (json.data && json.data.portalUrl) || null,
  };
}

export async function createSignature({ clientId, documentName, signatureImageUrl }) {
  const r = await apiFetch('/api/v1/signatures', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId, documentName, signatureImageUrl: signatureImageUrl || '' }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createSignature failed: ${r.status}`);
  }
  const json = await r.json();
  return mapSignatureFromApi(json.data);
}

// ── Activity Log ─────────────────────────────────────────────────────────────

function mapActivityFromApi(a) {
  return {
    id: a.id,
    action: a.action,
    entityType: a.entityType,
    entityId: a.entityId,
    details: a.details,
    userId: a.userId,
    userName: a.user?.displayName || '',
    userRole: a.user?.role || '',
    createdAt: a.createdAt,
  };
}

export async function listActivity({ today = false, limit = 100, entityType, entityId, from, to } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (today) qs.set('today', '1');
  // from/to are ISO instants (the caller converts the user's local day
  // boundaries to UTC) so the dashboard feed can load any past day straight
  // from the server instead of filtering the cached ~100 most-recent rows.
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (entityType && entityId) {
    qs.set('entityType', entityType);
    qs.set('entityId', entityId);
  }
  const r = await apiFetch(`/api/v1/activity?${qs.toString()}`);
  if (!r.ok) throw new Error(`listActivity failed: ${r.status}`);
  const json = await r.json();
  return { activity: (json.data || []).map(mapActivityFromApi) };
}

// Convenience wrapper for the "full history" tab on a single entity's detail
// view. Pulls up to 1000 ActivityLog rows scoped to the (entityType, entityId)
// pair — enough to cover years of edits without paginating.
export async function listEntityHistory(entityType, entityId, { limit = 1000 } = {}) {
  if (!entityType || !entityId) return { activity: [] };
  return listActivity({ entityType, entityId, limit });
}

// ── Task notes thread ────────────────────────────────────────────────────────
// Notes are append-only ActivityLog rows (action 'task.note') served by the
// tasks routes so everyone with access to the task reads the WHOLE thread —
// the /activity role filter would hide the owner's notes from a seat.

export async function listTaskNotes(taskId) {
  const r = await apiFetch(`/api/v1/tasks/${taskId}/notes`);
  if (!r.ok) throw new Error(`listTaskNotes failed: ${r.status}`);
  const json = await r.json();
  return (json.data || []).map(mapActivityFromApi);
}

// Per-task {events, notes, lastAt} map in one call — feeds the counters on
// the table's "Notas" / "Actividad" columns without N per-row fetches.
export async function getTasksActivitySummary() {
  const r = await apiFetch('/api/v1/tasks/activity-summary');
  if (!r.ok) throw new Error(`getTasksActivitySummary failed: ${r.status}`);
  const json = await r.json();
  return json.data || {};
}

export async function addTaskNote(taskId, text, images) {
  const r = await apiFetch(`/api/v1/tasks/${taskId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // images: raw blob refs from uploadNoteAttachment — the backend validates
    // the tenant prefix and signs them for display.
    body: JSON.stringify({ text, ...(Array.isArray(images) && images.length ? { images } : {}) }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `addTaskNote failed: ${r.status}`);
  }
  const json = await r.json();
  return mapActivityFromApi(json.data);
}

// Corregir o retirar una nota de tarea. En el servidor NO se edita la fila:
// activity_logs es append-only por un trigger de la base de datos (es rastro de
// auditoria), asi que se guarda una fila nueva que apunta a la anterior y el
// GET las pliega. Desde aqui se ve como un PATCH normal.
export async function editTaskNote(taskId, noteId, text, images) {
  const r = await apiFetch(`/api/v1/tasks/${taskId}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, ...(Array.isArray(images) && images.length ? { images } : {}) }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `editTaskNote failed: ${r.status}`);
  }
  return true;
}

export async function deleteTaskNote(taskId, noteId) {
  const r = await apiFetch(`/api/v1/tasks/${taskId}/notes/${noteId}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteTaskNote failed: ${r.status}`);
  }
  return true;
}

// Team actions — agency-wide log of "soft" actions a teammate already
// did (greeting_sent, signature_requested, etc.) so the dashboard's
// "has anyone done X already" guard survives device switches and cache
// clears. POST is idempotent on (agencyId, key).
export async function listTeamActions({ limit = 500 } = {}) {
  const r = await apiFetch(`/api/v1/team-actions?limit=${limit}`);
  if (!r.ok) throw new Error(`listTeamActions failed: ${r.status}`);
  const json = await r.json();
  return { actions: json.data || [] };
}

// Las marcas de TODOS los cumpleaños que caen entre `desde` y `hasta`
// (YYYY-MM-DD), por antiguas que sean: la lista de arriba solo trae las últimas
// N acciones, y un cumpleaños programado con meses de antelación se quedaba
// fuera — el aviso decía "sin programar" (Johanna Arosemena, 2026-10-30, E-399).
export async function listMarcasDeCumpleanos({ desde, hasta }) {
  const r = await apiFetch(`/api/v1/team-actions/cumpleanos?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`);
  if (!r.ok) throw new Error(`listMarcasDeCumpleanos failed: ${r.status}`);
  const json = await r.json();
  return { actions: json.data || [] };
}

export async function recordTeamAction({ type, key, clientId, details }) {
  const r = await apiFetch(`/api/v1/team-actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, key, clientId, details }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `recordTeamAction failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Undo a greeting marker so the card goes back to "pendiente". Only
// `greeting_sent` keys are accepted by the backend — the same table holds
// signature/document/renewal history that must not be erasable.
export async function undoTeamAction(key) {
  const r = await apiFetch(`/api/v1/team-actions?key=${encodeURIComponent(key)}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `undoTeamAction failed: ${r.status}`);
  }
  return (await r.json());
}

// ── Mass Email Campaigns ─────────────────────────────────────────────────────

// `channel` = 'email' | 'sms'. En una campana de SMS, `subject` es el NOMBRE de
// la campana (lo que se ve en el Historial), `htmlBody` es el texto pelado del
// mensaje, y cada destinatario trae `phone` en vez de —o ademas de— `email`.
// El canal se DECLARA: el servidor no lo deduce de si hay telefono.
export async function createCampaign({ subject, htmlBody, recipients, acknowledgePhi = false, channel = 'email' }) {
  const r = await apiFetch('/api/v1/campaigns', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, htmlBody, recipients, acknowledgePhi, channel }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw makeApiError(err, `createCampaign failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

export async function sendCampaign(id) {
  const r = await apiFetch(`/api/v1/campaigns/${id}/send`, { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `sendCampaign failed: ${r.status}`);
  }
  const json = await r.json();
  // { sent, failed, skippedSuppressed, alreadyDone, total, pausedByQuota,
  //   quotaKind, pausedForNow, remaining }
  //
  // Los dos "pausada" NO son lo mismo (E-304):
  //   - pausedForNow: a ESTA llamada se le acabo el tiempo (la puerta de
  //     enlace corta a los 4 min y entre correo y correo hay que dejar un
  //     hueco). Se vuelve a llamar EN EL ACTO y sigue; lo hace solo
  //     `enviarCampanaEntera` en MassEmailView.
  //   - pausedByQuota: el buzon del agente dijo que no. `quotaKind` dice si
  //     fue el cupo del dia ('daily-quota' → manana) o que iba saturado
  //     ('throttled' → unos minutos).
  //
  // En los dos casos los `remaining` quedaron SIN TOCAR, asi que volver a
  // llamar continua exactamente ahi y nadie recibe el correo dos veces.
  return json.data;
}

/**
 * Programa una campana YA CREADA (borrador) para una fecha futura.
 *
 * El camino es el mismo que el de enviar ahora — crear la campana con sus
 * destinatarios y luego dispararla — solo que aqui el gatillo lo aprieta la
 * cola del servidor a su hora. Eso es lo que hace que una campana programada
 * salga en el Historial, agrupada, y mida sus aperturas y sus clicks igual que
 * una enviada al momento (Lissette 2026-09-03, queja 45).
 */
export async function scheduleCampaign(id, runAt) {
  const r = await apiFetch(`/api/v1/campaigns/${id}/schedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ runAt }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw makeApiError(err, `scheduleCampaign failed: ${r.status}`);
  }
  const json = await r.json();
  // { queued, skippedSuppressed, runAt, campaignId }
  return json.data;
}

export async function listCampaigns() {
  const r = await apiFetch('/api/v1/campaigns');
  if (!r.ok) throw new Error(`listCampaigns failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function getCampaign(id) {
  const r = await apiFetch(`/api/v1/campaigns/${id}`);
  if (!r.ok) throw new Error(`getCampaign failed: ${r.status}`);
  const json = await r.json();
  return json.data;
}

export async function deleteCampaign(id) {
  const r = await apiFetch(`/api/v1/campaigns/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteCampaign failed: ${r.status}`);
  }
  return true;
}

// ── Borradores de Correo Masivo (Lissette 2026-09-24) ────────────────────────
// Un borrador no manda nada: guarda el formulario tal cual (asunto, cuerpo,
// canal y en `payload` los marcados, los correos a mano y los filtros).
export async function listCampaignDrafts() {
  const r = await apiFetch('/api/v1/campaigns/drafts');
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `listCampaignDrafts failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data || [];
}

/** Guarda el borrador: lo crea si no trae `id`, lo sobrescribe si lo trae. */
export async function saveCampaignDraft({ id = null, channel = 'email', subject = '', htmlBody = '', payload = {} }) {
  const r = await apiFetch(id ? `/api/v1/campaigns/drafts/${id}` : '/api/v1/campaigns/drafts', {
    method: id ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel, subject, htmlBody, payload }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const e = makeApiError(err, `saveCampaignDraft failed: ${r.status}`);
    // 404 = el borrador ya no existe (lo borraron en otra pestaña o se mandó):
    // la pantalla lo guarda entonces como uno nuevo en vez de perderlo.
    e.status = r.status;
    throw e;
  }
  const json = await r.json();
  return json.data;
}

export async function deleteCampaignDraft(id) {
  const r = await apiFetch(`/api/v1/campaigns/drafts/${id}`, { method: 'DELETE' });
  // 404 = ya no estaba (otra persona lo borro, o se mando desde otra pestaña):
  // el resultado que se queria ya se cumple.
  if (!r.ok && r.status !== 404) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteCampaignDraft failed: ${r.status}`);
  }
  return true;
}

// ── Platform Admin (PLATFORM_OWNER only) ─────────────────────────────────────

export async function listAgencies() {
  const r = await apiFetch('/api/v1/admin/agencies');
  if (!r.ok) throw new Error(`listAgencies failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function getAgencyStats(id) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/stats`);
  if (!r.ok) throw new Error(`getAgencyStats failed: ${r.status}`);
  const json = await r.json();
  return json.data;
}

// BAA click-through status across every agency (who has accepted the current
// Business Associate Agreement version, and who is pending/outdated).
// Returns { currentVersion, clickwrapEnabled, summary, agencies:[...] }.
export async function getBaaAcceptances() {
  const r = await apiFetch('/api/v1/admin/baa-acceptances');
  if (!r.ok) throw new Error(`getBaaAcceptances failed: ${r.status}`);
  const json = await r.json();
  return json.data;
}

export async function listAgencyClients(id, { limit = 20, offset = 0 } = {}) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/clients?limit=${limit}&offset=${offset}`);
  if (!r.ok) throw new Error(`listAgencyClients failed: ${r.status}`);
  return r.json();
}

// Platform-only — audited reveal of customer financial metrics. Called BEFORE
// un-masking the commission totals in Platform Admin; writes a
// 'platform.financials_revealed' row to each affected agency's audit log.
//   scope: 'platform_overview' (overview card) | 'agency' (+ agencyId, drill-down)
export async function revealPlatformFinancials({ scope = 'platform_overview', agencyId } = {}) {
  const r = await apiFetch('/api/v1/admin/financials-reveal', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scope, agencyId }),
  });
  if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || `revealPlatformFinancials failed: ${r.status}`); }
  return true;
}

// Campañas del founder (Deskte -> sus agentes). Reemplazan al viejo
// platformBroadcast, que mandaba de un tiro, sin historial ni segmentos, y
// filtraba por un rol inexistente en el backend.

export async function listPlatformCampaigns() {
  const r = await apiFetch('/api/v1/admin/campaigns');
  if (!r.ok) throw new Error(`listPlatformCampaigns failed: ${r.status}`);
  return (await r.json()).data || [];
}

export async function getPlatformCampaign(id) {
  const r = await apiFetch(`/api/v1/admin/campaigns/${id}`);
  if (!r.ok) throw new Error(`getPlatformCampaign failed: ${r.status}`);
  return (await r.json()).data;
}

// Dry-run del segmento: { agencyCount, recipientCount, unsubscribed,
//  willReceive, segmentLabel, sample[] }
export async function previewPlatformAudience(segment) {
  const r = await apiFetch('/api/v1/admin/campaigns/audience', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ segment }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `previewPlatformAudience failed: ${r.status}`);
  return json.data;
}

export async function createPlatformCampaign({ subject, htmlBody, segment, scheduledAt = null }) {
  const r = await apiFetch('/api/v1/admin/campaigns', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, htmlBody, segment, scheduledAt }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createPlatformCampaign failed: ${r.status}`);
  return json.data;
}

export async function updatePlatformCampaign(id, patch) {
  const r = await apiFetch(`/api/v1/admin/campaigns/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updatePlatformCampaign failed: ${r.status}`);
  return json.data;
}

export async function sendPlatformCampaignTest(id, to) {
  const r = await apiFetch(`/api/v1/admin/campaigns/${id}/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `sendPlatformCampaignTest failed: ${r.status}`);
  return json.data;
}

export async function sendPlatformCampaignNow(id) {
  const r = await apiFetch(`/api/v1/admin/campaigns/${id}/send`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `sendPlatformCampaignNow failed: ${r.status}`);
  return json.data;
}

export async function cancelPlatformCampaign(id) {
  const r = await apiFetch(`/api/v1/admin/campaigns/${id}/cancel`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `cancelPlatformCampaign failed: ${r.status}`);
  return json.data;
}

export async function deletePlatformCampaign(id) {
  const r = await apiFetch(`/api/v1/admin/campaigns/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deletePlatformCampaign failed: ${r.status}`);
  }
  return true;
}

// Sube una imagen para el cuerpo de una campaña del founder y devuelve la URL
// PERMANENTE que va en el <img>. Mismo patrón de dos pasos que
// uploadEmailImage (SAS de escritura → PUT), pero bajo el prefijo `platform/`:
// el founder no tiene agencia. La URL no lleva SAS, así que la imagen sigue
// viéndose cuando alguien abra el correo semanas después.
export async function uploadPlatformCampaignImage(file) {
  if (!file) throw new Error('No file provided');
  if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
    throw new Error('Solo se aceptan imágenes PNG, JPEG, WebP o GIF.');
  }
  if (file.size > 2 * 1024 * 1024) throw new Error('La imagen pesa más de 2 MB.');

  const sasRes = await apiFetch('/api/v1/admin/campaigns/image-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, contentType: file.type }),
  });
  const sasJson = await sasRes.json().catch(() => ({}));
  if (!sasRes.ok) throw new Error(sasJson.error || `image SAS failed: ${sasRes.status}`);
  const { uploadUrl, displayUrl } = sasJson.data || {};
  if (!uploadUrl || !displayUrl) throw new Error('Bad SAS response');

  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload to storage failed: ${put.status}`);
  return displayUrl;
}

export async function listPlatformOptOuts() {
  const r = await apiFetch('/api/v1/admin/campaigns/optouts');
  if (!r.ok) throw new Error(`listPlatformOptOuts failed: ${r.status}`);
  return (await r.json()).data || [];
}

// Platform-only — cancel an agency's Stripe subscription. `immediate:true`
// cancels right away with prorate; default is cancel-at-period-end. Body
// also accepts a `reason` string (≤500 chars) that lands in the audit log.
export async function cancelAgencySubscription(id, { immediate = false, reason = null } = {}) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/cancel-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ immediate, reason }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.detail || json.error || `cancelAgencySubscription failed: ${r.status}`);
  return json.data;
}

// Platform-only — invite codes for Private Beta gate.
// listInviteCodes returns { codes: [...], privateBetaEnabled: bool }.
export async function listInviteCodes() {
  const r = await apiFetch('/api/v1/admin/invite-codes');
  if (!r.ok) throw new Error(`listInviteCodes failed: ${r.status}`);
  const json = await r.json();
  return {
    codes: json.data || [],
    privateBetaEnabled: !!json.privateBetaEnabled,
  };
}

// createInviteCodes mints N codes at once (used for a batch handout).
// Returns the created rows.
export async function createInviteCodes({ note, maxUses = 1, expiresInDays, count = 1, customCode } = {}) {
  const r = await apiFetch('/api/v1/admin/invite-codes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, maxUses, expiresInDays, count, customCode }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createInviteCodes failed: ${r.status}`);
  return json.data.codes;
}

// revokeInviteCode flips a code to status="revoked" so it stops accepting
// new redemptions; existing redemption audit history is preserved.
export async function revokeInviteCode(id) {
  const r = await apiFetch(`/api/v1/admin/invite-codes/${id}/revoke`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `revokeInviteCode failed: ${r.status}`);
  return json.data;
}

// ── Demo agency (platform-only) ─────────────────────────────────────────────
// A synthetic tenant used for sales presentations, so no real client book is
// ever on screen. The backend refuses to touch anything that isn't provably
// the demo agency, and answers 409 with the reason when a guard trips — which
// is a message worth showing verbatim, so it is surfaced as the Error.

export async function getDemoAgency() {
  const r = await apiFetch('/api/v1/admin/demo-agency');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getDemoAgency failed: ${r.status}`);
  return json.data;
}

// Creates the demo agency, or resets its data in place when it already exists
// (the agency and its users survive a reset). Slow-ish by design — it writes
// the whole book — so callers should show a busy state.
export async function seedDemoAgency(agencyName) {
  const r = await apiFetch('/api/v1/admin/demo-agency/seed', {
    method: 'POST',
    body: JSON.stringify(agencyName ? { agencyName } : {}),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `seedDemoAgency failed: ${r.status}`);
  return json.data;
}

export async function deleteDemoAgency() {
  const r = await apiFetch('/api/v1/admin/demo-agency', { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `deleteDemoAgency failed: ${r.status}`);
  return json.data;
}

// Platform-only — change an agency's plan. Two modes:
//   - billing:false → local-only override (Stripe untouched). Use for
//     free/promo/Founder gifts.
//   - billing:true + priceId → drive a Stripe subscription price swap
//     with proration. Local plan + limits update either way.
export async function changeAgencyPlan(id, { plan, billing = false, priceId = null, reason = null } = {}) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/change-plan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan, billing, priceId, reason }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.detail || json.error || `changeAgencyPlan failed: ${r.status}`);
  return json.data;
}

// Platform-only — pulls the cross-tenant ActivityLog for an agency.
// `filters` accepts: { limit, action, actionPrefix, userId, since }.
// Returns { agency: { id, name }, total, logs: [{ id, action, entityType,
//   entityId, details, createdAt, user: { id, displayName, email, role } }] }.
export async function listAgencyAuditLog(id, filters = {}) {
  const qs = new URLSearchParams();
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.action) qs.set('action', filters.action);
  if (filters.actionPrefix) qs.set('actionPrefix', filters.actionPrefix);
  if (filters.userId) qs.set('userId', filters.userId);
  if (filters.since) qs.set('since', filters.since);
  const qstr = qs.toString();
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/audit${qstr ? '?' + qstr : ''}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `listAgencyAuditLog failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Platform KILL SWITCH for in-app calling. The agency declares its own BAA
// (setOwnVoiceBaa below) because it holds the provider account and the
// contract; what the platform owner keeps is the power to force one OFF.
// Blocking is stored apart from the attestation, so un-blocking restores what
// the agency declared instead of erasing it.
export async function setAgencyVoiceBaaBlocked(id, blocked) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/voice-baa`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocked }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setAgencyVoiceBaaBlocked failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// POST /admin/agencies/:id/limits — AJUSTAR A MANO LOS TOPES DE UNA AGENCIA.
//
// `limites` lleva SOLO los campos que se tocaron: lo que no va, no se toca.
// Mandar los cinco siempre convertiría "no edité eso" en "confirmo ese valor".
//
// Lanza un Error con `.code`. El que importa es **POR_DEBAJO_DEL_PLAN** (409):
// el número pedido está por debajo de lo que el plan ya incluye, así que
// guardarlo no cambiaría el tope real. Viene con `.porDebajoDelPlan` —la lista
// de campos y su suelo— para poder enseñarlo y volver a llamar con
// `confirmar: true`. No es un fallo: es un ajuste que no ajustaría, y el
// servidor prefiere pararlo a guardarlo en silencio.
export async function ajustarTopesDeAgencia(id, limites, { motivo, confirmar } = {}) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/limits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...limites,
      reason: motivo || null,
      confirmarPorDebajoDelPlan: confirmar === true,
    }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `ajustarTopesDeAgencia failed: ${r.status}`);
    err.code = json.code || null;
    err.porDebajoDelPlan = json.porDebajoDelPlan || [];
    throw err;
  }
  return json.data; // { guardado, delPlan, efectivo, porDebajoDelPlan }
}

// LOS INTERRUPTORES DE LOS TOPES, desde Platform Admin. Antes esto era una
// variable de entorno en Azure (Lissette, 2026-09-22: *"no veo como hacerlo"*).
//
// Devuelve { asientos, clientes } ya RESUELTOS — lo que de verdad se aplica,
// no lo que se acaba de pulsar. Tarda hasta 30 s en verse en todas las
// replicas del servidor, que es el precio de no tener que reiniciar nada.
export async function leerTopesDeCobro() {
  const r = await apiFetch('/api/v1/admin/topes');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `leerTopesDeCobro failed: ${r.status}`);
  return json.data;
}

export async function cambiarTopeDeCobro(cual, valor, motivo) {
  const r = await apiFetch('/api/v1/admin/topes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cual, valor: valor === true, reason: motivo || null }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `cambiarTopeDeCobro failed: ${r.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json.data;
}

export async function suspendAgency(id, reason) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/suspend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `suspendAgency failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function unsuspendAgency(id) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/unsuspend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `unsuspendAgency failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// AGENCIA EN PAUSA (Lissette 2026-10-22). Nadie de la agencia entra y sus
// envíos automáticos a clientes no salen. `hasta` = 'YYYY-MM-DD' o null (sin
// fecha). `pausarCobro` pausa también el cobro en Stripe hasta esa fecha.
// Ver deskte-api/src/utils/agenciaEnPausa.ts.
export async function pauseAgency(id, { hasta = null, pausarCobro = true, reason = null } = {}) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/pause`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hasta: hasta || null, pausarCobro: pausarCobro !== false, reason: reason || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `pauseAgency failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Quita la pausa antes de tiempo y reanuda el cobro en Stripe. Si Stripe falla
// la agencia entra igual y `avisoCobro` dice qué hacer a mano.
export async function resumeAgency(id) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `resumeAgency failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Dry run of deleteAgency(): the full inventory of what a delete would remove,
// plus every reason it must not happen. Changes nothing. `canDelete` is the
// server's answer — the UI must not re-derive it from the counts.
export async function getAgencyPurgePreview(id) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/purge-preview`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getAgencyPurgePreview failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// PERMANENTLY delete an EMPTY agency (no undo). `confirmName` must equal the
// agency's name exactly or the server refuses with CONFIRM_MISMATCH. Anything
// holding real data, a Founder, a comped agency or a paying customer is
// refused server-side and stays a terminal operation.
export async function deleteAgency(id, confirmName) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmName }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteAgency failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Mark an agency platform-internal (free + unlimited) or hand it back to
// normal billing. Refuses with code HAS_SUBSCRIPTION if Stripe is still
// billing them — cancel the subscription first.
export async function setAgencyInternal(id, internal, reason) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/internal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ internal, reason: reason || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setAgencyInternal failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Put an agency in / out of the Founder Program. Gates which price it is
// quoted ($35 vs $45) and whether /checkout will accept a Founder price at all.
// Does not alter an existing Stripe subscription.
export async function setAgencyFounder(id, founder, reason) {
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/founder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ founder, reason: reason || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setAgencyFounder failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function startBrowseAsAgency(id, reason) {
  // sinSuplantar: va como la dueña de la plataforma aunque otra pestaña haya
  // dejado un "Ver como" en localStorage (ver apiFetch en AuthGate.jsx).
  const r = await apiFetch(`/api/v1/admin/agencies/${id}/browse-session`, {
    sinSuplantar: true,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `startBrowseAsAgency failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function startViewAsUser(userId, reason) {
  const r = await apiFetch(`/api/v1/admin/users/${userId}/view-session`, {
    sinSuplantar: true,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || null }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `startViewAsUser failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function listAllUsers() {
  const r = await apiFetch('/api/v1/admin/users');
  if (!r.ok) throw new Error(`listAllUsers failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

// CAMBIAR EL CORREO DE UNA CUENTA (Lissette 2026-10-11). Solo la plataforma.
// Cambia el correo en Deskte (y en Stripe si es la dueña). El correo con el
// que la persona ENTRA vive en Microsoft y se cambia allí.
export async function changeAccountEmail(userId, email, reason) {
  const r = await apiFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/change-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email || '', reason: reason || '' }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `changeAccountEmail failed: ${r.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json.data;
}

// DEVOLVERLE LA CUENTA A SU DUEÑA (E-309). Solo funciona sobre una cuenta
// atrapada: agencia sin dueña activa, o un asiento que se supervisa a sí mismo.
// El servidor vuelve a comprobarlo; el `atrapada` que viene en la lista solo
// decide si se enseña el botón.
export async function restoreAccountOwner(userId, reason) {
  const r = await apiFetch(`/api/v1/admin/users/${userId}/restore-owner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || '' }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `restoreAccountOwner failed: ${r.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json.data;
}

export async function getPlatformMetrics() {
  const r = await apiFetch('/api/v1/admin/metrics');
  if (!r.ok) throw new Error(`getPlatformMetrics failed: ${r.status}`);
  const json = await r.json();
  return json.data;
}

// Pestaña "Salud del sistema" (PLATFORM_OWNER). Servidor, base y Redis ahora
// mismo + la última hora de la instancia que contesta. Ver
// deskte-api/src/utils/saludDelSistema.ts.
export async function getSaludDelSistema() {
  const r = await apiFetch('/api/v1/admin/salud');
  if (!r.ok) throw new Error(`getSaludDelSistema failed: ${r.status}`);
  const json = await r.json();
  return json.data;
}

// ── Founder CRM — las agencias como CLIENTES de Deskte ──────────────────────
// (PLATFORM_OWNER only). Pestaña "Clientes" de Platform Admin: ficha por
// cuenta, sus seats, etapa comercial, notas y próximo seguimiento.

export async function listPlatformCustomers({ includeInternal = false } = {}) {
  const r = await apiFetch(`/api/v1/admin/customers${includeInternal ? '?includeInternal=1' : ''}`);
  if (!r.ok) throw new Error(`listPlatformCustomers failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function getPlatformCustomer(agencyId) {
  const r = await apiFetch(`/api/v1/admin/customers/${agencyId}`);
  if (!r.ok) throw new Error(`getPlatformCustomer failed: ${r.status}`);
  const json = await r.json();
  return json.data;
}

// patch: { stage?, nextFollowUpAt? (ISO o null), followUpNote? }
export async function updatePlatformCustomer(agencyId, patch) {
  const r = await apiFetch(`/api/v1/admin/customers/${agencyId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updatePlatformCustomer failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function addPlatformCustomerNote(agencyId, body) {
  const r = await apiFetch(`/api/v1/admin/customers/${agencyId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `addPlatformCustomerNote failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function updatePlatformCustomerNote(agencyId, noteId, body) {
  const r = await apiFetch(`/api/v1/admin/customers/${agencyId}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updatePlatformCustomerNote failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function deletePlatformCustomerNote(agencyId, noteId) {
  const r = await apiFetch(`/api/v1/admin/customers/${agencyId}/notes/${noteId}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deletePlatformCustomerNote failed: ${r.status}`);
  }
  return true;
}

// ── Cobro de la plataforma — lo que Deskte le factura a SUS agencias ────────
// (PLATFORM_OWNER only). Backend: platform-billing.routes.ts.

// { stripe: { secretKey, webhookSecret, prices[], missingForSignup[],
//   signupCollectsCard }, accounts: { total, paying, unpaid, unpaidFounders } }
export async function getPlatformBillingStatus() {
  const r = await apiFetch('/api/v1/admin/billing/status');
  if (!r.ok) throw new Error(`getPlatformBillingStatus failed: ${r.status}`);
  return (await r.json()).data;
}

// Qué se le facturaría a una agencia (precio Founder o público según su marca)
// y qué facturas de Deskte ya tiene.
export async function getPlatformCustomerBilling(agencyId) {
  const r = await apiFetch(`/api/v1/admin/billing/customers/${agencyId}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getPlatformCustomerBilling failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── Programa de referidos ───────────────────────────────────────────────────
// Backend: referrals.routes.ts (el agente) y platform-referrals.routes.ts (la
// founder). El premio NUNCA se acredita solo: se aprueba desde Platform Admin.

// Lo del agente: su código, su enlace, sus referidos y lo que lleva ganado.
// El código se crea en el servidor la primera vez que se llama.
export async function getMyReferrals() {
  const r = await apiFetch('/api/v1/referrals/me');
  if (!r.ok) throw new Error(`getMyReferrals failed: ${r.status}`);
  return (await r.json()).data;
}

export async function listReferrals() {
  const r = await apiFetch('/api/v1/admin/referrals');
  if (!r.ok) throw new Error(`listReferrals failed: ${r.status}`);
  const json = await r.json();
  return { rows: json.data || [], summary: json.summary || {} };
}

export async function getReferralSettings() {
  const r = await apiFetch('/api/v1/admin/referrals/settings');
  if (!r.ok) throw new Error(`getReferralSettings failed: ${r.status}`);
  return (await r.json()).data;
}

// patch: { enabled?, rewardType?, rewardValue?, qualifyAfterPaidInvoices? }
// Solo afecta a lo que venga: los premios ya ganados llevan copiadas sus
// condiciones del día que se ganaron.
export async function saveReferralSettings(patch) {
  const r = await apiFetch('/api/v1/admin/referrals/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `saveReferralSettings failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Aprueba el premio y deja el crédito en la cuenta de Stripe de quien refirió.
export async function approveReferral(id) {
  const r = await apiFetch(`/api/v1/admin/referrals/${id}/approve`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `approveReferral failed: ${r.status}`);
  return json.data;
}

export async function rejectReferral(id, reason) {
  const r = await apiFetch(`/api/v1/admin/referrals/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `rejectReferral failed: ${r.status}`);
  return json.data;
}

// Emite la factura en Stripe y se la manda por correo al dueño.
// payload: { lines?, daysUntilDue?, memo?, force? }
//
// El 409 (INVOICE_ALREADY_OPEN) no es un fallo: es "esta agencia ya tiene una
// factura sin pagar". Se propaga con .code y .existing para poder ofrecer
// mandarla igual en vez de tragarse el aviso.
export async function issuePlatformInvoice(agencyId, payload = {}) {
  const r = await apiFetch(`/api/v1/admin/billing/customers/${agencyId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `issuePlatformInvoice failed: ${r.status}`);
    err.code = json.code || null;
    err.existing = json.existing || null;
    throw err;
  }
  return json.data;
}

// Crea el enlace de pago automatico de una agencia que ya esta dentro: una
// pantalla de Stripe que guarda la tarjeta y deja la suscripcion cobrandose
// sola, al precio que le toque (Founder o publico).
//
// interval 'month' | 'year'. Un Founder SI puede pagar por anio: doce meses de
// SU precio, sin el 15% encima (Lissette, 2026-09-07).
//
// A diferencia de la factura, esto NO cobra hoy nada por si solo: es el
// enlace que hay que mandarle al dueno para que lo complete el.
export async function createSubscriptionLink(agencyId, interval = 'month') {
  const r = await apiFetch(`/api/v1/admin/billing/customers/${agencyId}/subscription-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ interval }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `createSubscriptionLink failed: ${r.status}`);
    err.code = json.code || null;
    err.missing = json.missing || null;
    throw err;
  }
  return json.data;
}

// VOLVER A PREGUNTARLE A STRIPE por la suscripcion de esta agencia.
//
// La copia que guarda Deskte SOLO se escribe cuando llega un webhook, y un
// webhook que no llega no se recupera solo: la agencia se queda congelada en el
// ultimo estado que si llego. Paso de verdad (2026-09-13): una ficha decia
// `incomplete` con un periodo terminado cuatro dias atras, imposible en Stripe,
// y la duena ya habia pagado.
//
// No inventa nada: lee de Stripe y escribe lo que diga.
export async function resyncSubscription(agencyId) {
  const r = await apiFetch(`/api/v1/admin/billing/customers/${agencyId}/resync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `resyncSubscription failed: ${r.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json.data;
}

// Manda ESE MISMO enlace al correo del dueno de la agencia, con las dos
// opciones dentro —mensual y anual— para que elija la persona.
//
// Se manda desde el servidor y no desde aqui a proposito: los enlaces los crea
// el propio backend, asi que nadie puede hacer que Deskte mande un correo con
// una direccion cualquiera dentro.
export async function emailSubscriptionLink(agencyId) {
  const r = await apiFetch(`/api/v1/admin/billing/customers/${agencyId}/subscription-link/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `emailSubscriptionLink failed: ${r.status}`);
    err.code = json.code || null;
    err.missing = json.missing || null;
    // Cuando el correo no sale pero el enlace SI se creo, viene dentro: la
    // pantalla lo puede seguir enseñando para copiarlo a mano.
    err.data = json.data || null;
    throw err;
  }
  return json.data;
}

// El texto del correo del link de pago: la presentacion y la despedida que
// escribe Lissette. Los precios, los botones y la fecha limite NO se tocan
// desde aqui a proposito.
export async function getSubscriptionLinkMessage() {
  const r = await apiFetch('/api/v1/admin/billing/link-message');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getSubscriptionLinkMessage failed: ${r.status}`);
  return json.data;
}

export async function saveSubscriptionLinkMessage(payload) {
  const r = await apiFetch('/api/v1/admin/billing/link-message', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `saveSubscriptionLinkMessage failed: ${r.status}`);
  return json.data;
}

// Registra a mano un referido de antes de que existiera el programa: la
// agencia que trajo y la que fue traida. Nace PENDIENTE — el premio sigue
// necesitando la aprobacion de siempre.
export async function createManualReferral(payload) {
  const r = await apiFetch('/api/v1/admin/referrals/manual', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.error || `createManualReferral failed: ${r.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json.data;
}

// ── Quotes ──────────────────────────────────────────────────────────────────

export async function listQuotes(statusOrOpts) {
  // Back-compat: a plain string is still treated as the status filter.
  const opts = typeof statusOrOpts === 'string' ? { status: statusOrOpts } : (statusOrOpts || {});
  const qs = new URLSearchParams();
  if (opts.status) qs.set('status', opts.status);
  if (opts.clientId) qs.set('clientId', opts.clientId); // #9 quote history per client
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const r = await apiFetch(`/api/v1/quotes${suffix}`);
  if (!r.ok) throw new Error(`listQuotes failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function getQuote(id) {
  const r = await apiFetch(`/api/v1/quotes/${id}`);
  if (!r.ok) throw new Error(`getQuote failed: ${r.status}`);
  return (await r.json()).data;
}
export async function createQuote(payload) {
  const r = await apiFetch('/api/v1/quotes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `createQuote failed: ${r.status}`));
  }
  return (await r.json()).data;
}
export async function updateQuote(id, payload) {
  const r = await apiFetch(`/api/v1/quotes/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `updateQuote failed: ${r.status}`));
  }
  return (await r.json()).data;
}
// Quote auto-fill: upload one or more PDFs and/or images (screenshots, photos);
// the backend extracts text (PDFs) or reads the image (vision) and uses Azure
// OpenAI to return structured plan options for review. Send as multipart — do
// NOT set Content-Type so the browser adds the boundary. Throws an Error whose
// `.code` carries the backend's machine reason ('no_text', 'too_large',
// 'not_configured'…) so the UI can show a soft hint.
export async function extractQuoteFromDocument(files, kind = 'health') {
  const fd = new FormData();
  for (const f of Array.from(files || [])) {
    // Entries may be raw File objects (PDFs) or { blob, name } (re-encoded images).
    if (f && f.blob) fd.append('files', f.blob, f.name || 'image.jpg');
    else if (f) fd.append('files', f);
  }
  // 'life' routes to the WinFlex/life-illustration reader; default 'health'.
  if (kind && kind !== 'health') fd.append('kind', kind);
  const r = await apiFetch('/api/v1/quotes/extract-from-document', { method: 'POST', body: fd });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const e = new Error(err.reason || err.error || `extract failed: ${r.status}`);
    e.code = err.error || `http_${r.status}`;
    throw e;
  }
  return (await r.json()).data;
}

// ── Marketplace cotizador (CMS Marketplace API — live ACA plans) ──
// Backend: deskte-api/src/routes/marketplace.routes.ts. These consume the
// backend's already-normalized shape, so the UI stays decoupled from CMS quirks.

/** Whether the marketplace quoter is configured (api key set) on the server. */
export async function getMarketplaceStatus() {
  const r = await apiFetch('/api/v1/marketplace/status');
  if (!r.ok) return { configured: false };
  return await r.json(); // { configured }
}

/** Resolve a ZIP to its county/FIPS options (a ZIP can span several counties). */
export async function getMarketplaceCounties(zip, year) {
  const qs = new URLSearchParams({ zip: String(zip || '') });
  if (year) qs.set('year', String(year));
  const r = await apiFetch(`/api/v1/marketplace/counties?${qs.toString()}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const e = new Error(err.error || `counties failed: ${r.status}`);
    e.code = err.error || `http_${r.status}`;
    throw e;
  }
  return (await r.json()).counties || [];
}

/**
 * Run a live marketplace quote.
 * payload: { income, people:[{ age|dob, gender?, usesTobacco?, aptcEligible? }],
 *            place:{ countyfips, state, zipcode }, year?, market?, applySubsidy? }
 * Returns: { eligibility:{ aptc, csr, isMedicaidChip }, plans:[...], total, year }
 */
// ¿Cada plan cubre los medicamentos del cliente? (fase 2 de los medicamentos)
// Devuelve { planId: { rxcui: 'cubre'|'generico'|'no_cubre'|'sin_dato' } }.
export async function marketplaceDrugCoverage({ rxcuis, planIds, year }) {
  const r = await apiFetch('/api/v1/marketplace/drugs-covered', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rxcuis, planIds, year }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(j.error || `drug coverage failed: ${r.status}`);
    e.code = j.error || `http_${r.status}`;
    // Lo que contestó CMS (E-419): la pantalla lo enseña para que el próximo
    // "no contestó" diga por qué.
    e.cmsStatus = j.status || null;
    throw e;
  }
  return j.coverage || {};
}

// Buscar un médico en el directorio del Mercado (nombre o especialidad + ZIP).
// Nunca lanza: devuelve { providers, error } y la pantalla deja escribir a mano.
export async function buscarMedicosMercado({ q, zip, tipo }) {
  const qs = new URLSearchParams({ q: q || '', zip: zip || '' });
  if (tipo) qs.set('tipo', tipo);
  try {
    const r = await apiFetch(`/api/v1/marketplace/providers?${qs.toString()}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) return { providers: [], error: j.error || `http_${r.status}` };
    return { providers: Array.isArray(j.providers) ? j.providers : [], error: null };
  } catch (e) {
    return { providers: [], error: e.message || 'network' };
  }
}

// ¿Cada médico (NPI) está en la red de cada plan? { planId: { npi: 'en_red'|'fuera'|'sin_dato' } }
export async function marketplaceProviderCoverage({ npis, planIds, year }) {
  const r = await apiFetch('/api/v1/marketplace/providers-covered', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ npis, planIds, year }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(j.error || `provider coverage failed: ${r.status}`);
    e.code = j.error || `http_${r.status}`;
    // Lo que contestó CMS (E-419): la pantalla lo enseña para que el próximo
    // "no contestó" diga por qué.
    e.cmsStatus = j.status || null;
    throw e;
  }
  return j.coverage || {};
}

export async function marketplaceQuote(payload) {
  const r = await apiFetch('/api/v1/marketplace/quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const e = new Error(err.error || `marketplace quote failed: ${r.status}`);
    e.code = err.error || `http_${r.status}`;
    throw e;
  }
  return await r.json();
}

export async function sendQuote(id) {
  const r = await apiFetch(`/api/v1/quotes/${id}/send`, { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `sendQuote failed: ${r.status}`);
  }
  // Returns: { ok, emailSent, landingUrl, publicToken }
  return await r.json();
}
export async function convertQuoteToPolicy(id, itemId) {
  const body = itemId ? { itemId } : {};
  const r = await apiFetch(`/api/v1/quotes/${id}/convert-to-policy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `convertQuoteToPolicy failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteQuote(id) {
  const r = await apiFetch(`/api/v1/quotes/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteQuote failed: ${r.status}`);
  return true;
}

// ── Cotizador guiado: catálogo de productos (docs/PLAN-cotizador-guiado.md) ──
// El producto viaja tal cual lo arma cotizadorCatalogo.js#productoVacio: el
// catálogo es configuración de la agencia, no PHI, y no hay campos cifrados.
// Error con la razón que da el servidor ("Solo quien administra la agencia…"),
// no un "failed: 403" que no le dice nada a quien lo lee.
async function errorDelCatalogo(r, que) {
  const err = await r.json().catch(() => ({}));
  const e = new Error(formatApiError(err, `${que} failed: ${r.status}`));
  // La asesoría distingue "otro guardó en medio" y "ya está cerrada" de un fallo.
  e.status = r.status;
  e.conflicto = !!err.conflicto;
  e.cerrada = !!err.cerrada;
  return e;
}
// Devuelve { productos, permisos }. `permisos` lo calcula el servidor con las
// mismas reglas que aplica: la pantalla enseña los botones según eso.
export async function listCotizadorProductos() {
  const r = await apiFetch('/api/v1/cotizador/productos');
  if (!r.ok) throw await errorDelCatalogo(r, 'listCotizadorProductos');
  const j = await r.json();
  return { productos: j.data || [], permisos: j.permisos || null };
}
export async function createCotizadorProducto(payload) {
  const r = await apiFetch('/api/v1/cotizador/productos', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `createCotizadorProducto failed: ${r.status}`));
  }
  return (await r.json()).data;
}
export async function updateCotizadorProducto(id, payload) {
  const r = await apiFetch(`/api/v1/cotizador/productos/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `updateCotizadorProducto failed: ${r.status}`));
  }
  return (await r.json()).data;
}
// Brochures del catálogo: se suben al almacenamiento privado y se abren con
// sesión (no hay enlace público). `base`: la plataforma subiendo para la base.
export async function subirBrochuresCotizador(files, { base = false } = {}) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  const r = await apiFetch(`/api/v1/cotizador/brochures${base ? '?base=1' : ''}`, { method: 'POST', body: fd });
  if (!r.ok) throw await errorDelCatalogo(r, 'subirBrochuresCotizador');
  const j = await r.json();
  // `otros`: archivos que no eran PDF y no se subieron — la pantalla lo dice.
  return { subidos: j.data || [], otros: j.otros || 0 };
}
export async function cargarBrochuresBaseCotizador(files) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  const r = await apiFetch('/api/v1/cotizador/base/brochures', { method: 'POST', body: fd });
  if (!r.ok) throw await errorDelCatalogo(r, 'cargarBrochuresBaseCotizador');
  return (await r.json()).data;
}
export async function abrirBrochureCotizador(blob) {
  const ruta = String(blob).split('/').map(encodeURIComponent).join('/');
  const r = await apiFetch(`/api/v1/cotizador/brochures/${ruta}`);
  if (!r.ok) throw await errorDelCatalogo(r, 'abrirBrochureCotizador');
  return URL.createObjectURL(await r.blob());
}
// ── Cotizador guiado: entrevistas (Fase 2) ──
// `datos` lleva el hogar, el estatus migratorio, la salud y los ingresos: el
// servidor lo guarda CIFRADO y lo devuelve descifrado solo a quien puede verlo.
// La lista trae solo nombre y chips (`resumen`); la asesoría entera se pide al
// abrirla. `puedeEditar` y `truncada` los decide el servidor.
// El panel (Fase 5): solo números. Quién ve qué lo decide el servidor; `null`
// si el usuario no es de una agencia.
/**
 * Buscador de hospitales con asistencia financiera (datos públicos CMS + IRS,
 * iguales para todas las agencias). Nada del cliente viaja: el % del nivel de
 * pobreza se calcula en la pantalla.
 */
export async function getHospitalesCondados(estado) {
  const r = await apiFetch(`/api/v1/hospitales/condados?estado=${encodeURIComponent(estado)}`);
  if (!r.ok) throw await errorDelCatalogo(r, 'getHospitalesCondados');
  return await r.json(); // { condados: [{ condado, hospitales }], generado }
}
export async function getHospitales(estado, condado) {
  const qs = new URLSearchParams({ estado });
  if (condado) qs.set('condado', condado);
  const r = await apiFetch(`/api/v1/hospitales?${qs.toString()}`);
  if (!r.ok) throw await errorDelCatalogo(r, 'getHospitales');
  return await r.json(); // { hospitales: [...con nota], generado, puedeAnotar }
}
/** Nota de la agencia sobre un hospital. Texto vacío = borrarla (devuelve null). */
export async function putHospitalNota(hospitalId, texto) {
  const r = await apiFetch(`/api/v1/hospitales/${encodeURIComponent(hospitalId)}/nota`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ texto }),
  });
  if (!r.ok) throw await errorDelCatalogo(r, 'putHospitalNota');
  return (await r.json()).nota ?? null;
}
export async function getCotizadorPanel() {
  const r = await apiFetch('/api/v1/cotizador/panel');
  if (!r.ok) throw await errorDelCatalogo(r, 'getCotizadorPanel');
  return (await r.json()).data ?? null;
}
export async function listCotizadorCasos() {
  const r = await apiFetch('/api/v1/cotizador/casos');
  if (!r.ok) throw await errorDelCatalogo(r, 'listCotizadorCasos');
  const j = await r.json();
  return { casos: j.data || [], puedeEditar: j.puedeEditar !== false, truncada: !!j.truncada, permisos: j.permisos || null };
}
export async function getCotizadorCaso(id) {
  const r = await apiFetch(`/api/v1/cotizador/casos/${id}`);
  if (!r.ok) throw await errorDelCatalogo(r, 'getCotizadorCaso');
  return (await r.json()).data;
}
export async function createCotizadorCaso(payload) {
  const r = await apiFetch('/api/v1/cotizador/casos', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) throw await errorDelCatalogo(r, 'createCotizadorCaso');
  return (await r.json()).data;
}
export async function updateCotizadorCaso(id, payload) {
  const r = await apiFetch(`/api/v1/cotizador/casos/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) throw await errorDelCatalogo(r, 'updateCotizadorCaso');
  return (await r.json()).data;
}
export async function deleteCotizadorCaso(id) {
  const r = await apiFetch(`/api/v1/cotizador/casos/${id}`, { method: 'DELETE' });
  if (!r.ok) throw await errorDelCatalogo(r, 'deleteCotizadorCaso');
  return true;
}
// Preferencias de la agencia por nivel de la recomendación: { [nivelId]: productoId }.
// `puedeCambiar` lo decide el servidor con la misma regla con que guarda.
export async function getCotizadorPreferencias() {
  const r = await apiFetch('/api/v1/cotizador/preferencias');
  if (!r.ok) throw await errorDelCatalogo(r, 'getCotizadorPreferencias');
  const j = await r.json();
  return { preferencias: j.data || {}, puedeCambiar: !!j.puedeCambiar };
}
export async function saveCotizadorPreferencias(preferencias) {
  const r = await apiFetch('/api/v1/cotizador/preferencias', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ preferencias }) });
  if (!r.ok) throw await errorDelCatalogo(r, 'saveCotizadorPreferencias');
  return (await r.json()).data || {};
}
// "No lo vendo": oculta un producto (sobre todo uno base de Deskte) sin borrarlo.
export async function ocultarCotizadorProducto(id, oculto) {
  const r = await apiFetch(`/api/v1/cotizador/productos/${id}/ocultar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ oculto: !!oculto }) });
  if (!r.ok) throw await errorDelCatalogo(r, 'ocultarCotizadorProducto');
  return (await r.json()).data;
}
export async function deleteCotizadorProducto(id) {
  const r = await apiFetch(`/api/v1/cotizador/productos/${id}`, { method: 'DELETE' });
  if (!r.ok) throw await errorDelCatalogo(r, 'deleteCotizadorProducto');
  return true;
}

// ── Signature Requests (sends a portal link to the client to sign) ───────

// Two-step: agent uploads the doc to be signed FIRST, then creates the request
// with documentBlob set. We do it this way (instead of multipart) so the file
// never transits through the API server — it goes straight to Azure Blob.
export async function uploadSignatureDoc(file) {
  const sasRes = await apiFetch('/api/v1/signatures/sas-doc', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  });
  if (!sasRes.ok) {
    const err = await sasRes.json().catch(() => ({}));
    throw new Error(err.error || `sas-doc failed: ${sasRes.status}`);
  }
  const { data } = await sasRes.json();

  const putRes = await fetch(data.uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/pdf',
    },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Blob upload failed: ${putRes.status}`);

  return { blobName: data.blobName, mime: file.type || 'application/octet-stream' };
}

export async function createSignatureRequest(payload) {
  const r = await apiFetch('/api/v1/signatures', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    // Preserve backend's machine-readable fields (code: 'PHI_DETECTED',
    // findings, hint) so SignaturesView can branch on err.code and render
    // PhiWarningModal — same pattern createCampaign uses for mass email.
    const err = await r.json().catch(() => ({}));
    throw makeApiError(err, `createSignatureRequest failed: ${r.status}`);
  }
  const json = await r.json();
  return {
    ...mapSignatureFromApi(json.data),
    portalUrl: json.data.portalUrl,
    emailSent: json.data.emailSent,
    // POR QUÉ no salió. Esta lista es explícita: sin nombrarlo aquí, el motivo
    // llega del servidor y se pierde en esta línea, y la pantalla vuelve a no
    // tener más remedio que inventarse una causa.
    emailError: json.data.emailError || null,
  };
}

export async function deleteSignature(id) {
  const r = await apiFetch(`/api/v1/signatures/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteSignature failed: ${r.status}`);
  return true;
}

// Public — used by /sign/:token landing
export async function viewSignaturePublic(token) {
  const r = await fetch(`${API_BASE}/signature-view/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `viewSignaturePublic failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function submitSignaturePublic(token, pngBlob, responses, extras = {}) {
  // Sube una imagen (la firma o las iniciales) con su propio permiso de un solo uso.
  const subir = async (blob) => {
    const sasRes = await fetch(`${API_BASE}/signature-view/${encodeURIComponent(token)}/sas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!sasRes.ok) {
      const err = await sasRes.json().catch(() => ({}));
      throw new Error(err.error || `sas failed: ${sasRes.status}`);
    }
    const { data: sasData } = await sasRes.json();
    const putRes = await fetch(sasData.uploadUrl, {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'image/png' },
      body: blob,
    });
    if (!putRes.ok) throw new Error(`Blob upload failed: ${putRes.status}`);
    return sasData.blobName;
  };
  const data = { blobName: await subir(pngBlob) };
  // Las iniciales y el nombre, si el documento los pide (Lissette 2026-10-23).
  const initialsBlobName = extras.initialsBlob ? await subir(extras.initialsBlob) : undefined;

  const completeRes = await fetch(`${API_BASE}/signature-view/${encodeURIComponent(token)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `responses`: lo que el cliente contesto a las preguntas del documento
    // (Wendy Cercio, 2026-08-24). El servidor las valida contra las preguntas
    // GUARDADAS, no contra estas, asi que mandar de mas no sirve de nada.
    body: JSON.stringify({
      blobName: data.blobName,
      responses: responses || undefined,
      initialsBlobName,
      signerName: extras.signerName || undefined,
    }),
  });
  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({}));
    // El servidor devuelve `missing` con los enunciados sin contestar, para
    // que la pantalla pueda decir CUALES faltan en vez de "no se pudo".
    throw makeApiError(err, err.detail || err.error || `complete failed: ${completeRes.status}`);
  }
  // Returns: { id, status, signedAt, signedDocUrl }
  return (await completeRes.json()).data;
}

// ── Document Requests ────────────────────────────────────────────────────

function mapDocRequestFromApi(r) {
  return {
    id: r.id,
    title: r.title || '',
    description: r.description || '',
    documentsNeeded: r.documentsNeeded || [],
    status: r.status || 'pending',
    portalToken: r.portalToken,
    portalUrl: r.portalUrl,
    expiresAt: r.expiresAt,
    sentAt: r.sentAt,
    completedAt: r.completedAt,
    clientEmail: r.clientEmail,
    clientId: r.clientId || r.client?.id,
    clientName: r.client ? `${r.client.firstName} ${r.client.lastName}` : '',
    createdAt: r.createdAt,
  };
}

export async function listDocRequests(status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const r = await apiFetch(`/api/v1/documents/requests${qs}`);
  if (!r.ok) throw new Error(`listDocRequests failed: ${r.status}`);
  return ((await r.json()).data || []).map(mapDocRequestFromApi);
}
export async function createDocRequest(payload) {
  const r = await apiFetch('/api/v1/documents/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    // `makeApiError` y no `new Error(err.error)`: el servidor rechaza esto por
    // cuatro motivos distintos (PHI_DETECTED, no_supervisor, agent_no_mailbox,
    // cliente fuera de alcance) y cada uno se resuelve de otra forma. Tirar el
    // codigo dejaba a la pantalla pegando el identificador interno en la cara
    // de la agente — y, con PHI, sin manera de confirmar y seguir.
    const err = await r.json().catch(() => ({}));
    throw makeApiError(err, `createDocRequest failed: ${r.status}`);
  }
  const json = await r.json();
  return { ...mapDocRequestFromApi(json.data), portalUrl: json.data.portalUrl, emailSent: json.data.emailSent };
}
export async function deleteDocRequest(id) {
  const r = await apiFetch(`/api/v1/documents/requests/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteDocRequest failed: ${r.status}`);
  return true;
}

// Public — used by /upload/:token landing page (no auth)
// ── Document shares (enviar documentos a un correo externo) ──────────────────
// Several of a client's documents behind ONE public link, emailed to a third
// party (typically a carrier). The link expires and can be revoked.

export async function createDocumentShare({ clientId, documentIds, recipientEmail, recipientName, message, expiresInDays, language }) {
  const r = await apiFetch('/api/v1/documents/shares', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      clientId,
      documentIds,
      recipientEmail,
      ...(recipientName ? { recipientName } : {}),
      ...(message ? { message } : {}),
      ...(expiresInDays ? { expiresInDays } : {}),
      ...(language ? { language } : {}),
    }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(json, `createDocumentShare failed: ${r.status}`));
  return json.data;
}

export async function listDocumentShares(clientId) {
  const r = await apiFetch(`/api/v1/documents/shares?clientId=${encodeURIComponent(clientId)}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listDocumentShares failed: ${r.status}`);
  return json.data || [];
}

export async function revokeDocumentShare(id) {
  const r = await apiFetch(`/api/v1/documents/shares/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `revokeDocumentShare failed: ${r.status}`);
  return json.data;
}

// Public side — the recipient's page. No auth: the token is the credential.
export async function viewDocSharePublic(token) {
  const r = await fetch(`${API_BASE}/doc-share/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `viewDocSharePublic failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// One short-lived (15 min) download URL for a single file of the share.
export async function getDocShareFileUrl(token, documentId) {
  const r = await fetch(`${API_BASE}/doc-share/${encodeURIComponent(token)}/file`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ documentId }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getDocShareFileUrl failed: ${r.status}`);
  return json.data;
}

export async function viewDocPortalPublic(token) {
  const r = await fetch(`${API_BASE}/doc-portal/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `viewDocPortalPublic failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function uploadToDocPortalPublic(token, file) {
  const sasRes = await fetch(`${API_BASE}/doc-portal/${encodeURIComponent(token)}/sas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  });
  if (!sasRes.ok) {
    const err = await sasRes.json().catch(() => ({}));
    throw new Error(err.error || `sas failed: ${sasRes.status}`);
  }
  const { data } = await sasRes.json();

  const putRes = await fetch(data.uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Blob upload failed: ${putRes.status}`);

  const completeRes = await fetch(`${API_BASE}/doc-portal/${encodeURIComponent(token)}/upload-complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      blobName: data.blobName,
      fileName: file.name,
      fileType: (file.name.split('.').pop() || '').toLowerCase(),
      sizeBytes: file.size,
    }),
  });
  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({}));
    throw new Error(err.error || `upload-complete failed: ${completeRes.status}`);
  }
  return (await completeRes.json()).data;
}

// Quote attachments — request a write SAS, PUT the file directly to Azure Blob,
// then call /quotes/:id/attach to persist metadata.
export async function uploadQuoteAttachment(quoteId, file) {
  const sasRes = await apiFetch('/api/v1/quotes/sas-attach', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  });
  if (!sasRes.ok) {
    const err = await sasRes.json().catch(() => ({}));
    throw new Error(err.error || `sas-attach failed: ${sasRes.status}`);
  }
  const { data } = await sasRes.json();

  const putRes = await fetch(data.uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Blob upload failed: ${putRes.status}`);

  const attachRes = await apiFetch(`/api/v1/quotes/${quoteId}/attach`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      blobName: data.blobName,
      fileName: file.name,
      fileType: file.type || null,
      fileSize: file.size ?? null,
    }),
  });
  if (!attachRes.ok) {
    const err = await attachRes.json().catch(() => ({}));
    throw new Error(err.error || `attach metadata failed: ${attachRes.status}`);
  }
  return true;
}
// Sin `blob` borra TODOS los adjuntos, que es lo que hacia esta llamada cuando
// solo cabia uno. Con `blob` borra ese y deja los demas.
export async function deleteQuoteAttachment(quoteId, blob = null) {
  const q = blob ? `?blob=${encodeURIComponent(blob)}` : '';
  const r = await apiFetch(`/api/v1/quotes/${quoteId}/attach${q}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteQuoteAttachment failed: ${r.status}`);
  return true;
}

// Convert a Lead to a Client. Backend also auto-creates any policies linked to
// accepted quotes for this lead. Returns { client, policiesCreated, policies }.
export async function convertLeadToClient(leadId) {
  const r = await apiFetch(`/api/v1/leads/${leadId}/convert`, { method: 'PUT' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `convertLeadToClient failed: ${r.status}`);
  }
  const json = await r.json();
  return {
    client: json.data,
    policiesCreated: json.policiesCreated || 0,
    // Map raw backend policies into the camel-case shape the frontend uses
    // everywhere else, so caller can splice them straight into local state.
    policies: Array.isArray(json.policies) ? json.policies.map(mapPolicyFromApi) : [],
  };
}

// Public landing — no auth required (uses fetch directly, not apiFetch).
export async function viewQuotePublic(token) {
  const r = await fetch(`${API_BASE}/quote-view/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `viewQuotePublic failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function acceptQuoteOption(token, itemId) {
  const r = await fetch(`${API_BASE}/quote-view/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `acceptQuoteOption failed: ${r.status}`);
  }
  return true;
}
export async function declineQuote(token) {
  const r = await fetch(`${API_BASE}/quote-view/${encodeURIComponent(token)}/decline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `declineQuote failed: ${r.status}`);
  }
  return true;
}

// Convert a quote to a draft invoice. Idempotent — re-calling returns the
// existing invoice (with `alreadyExisted: true` in the wrapper).
export async function convertQuoteToInvoice(quoteId, opts = {}) {
  const r = await apiFetch(`/api/v1/quotes/${quoteId}/convert-to-invoice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `convertQuoteToInvoice failed: ${r.status}`);
  }
  const json = await r.json();
  return { invoice: json.data, alreadyExisted: !!json.alreadyExisted };
}

// ── Invoices ────────────────────────────────────────────────────────────────

export async function listInvoices(status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const r = await apiFetch(`/api/v1/invoices${qs}`);
  if (!r.ok) throw new Error(`listInvoices failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function getInvoice(id) {
  const r = await apiFetch(`/api/v1/invoices/${id}`);
  if (!r.ok) throw new Error(`getInvoice failed: ${r.status}`);
  return (await r.json()).data;
}
export async function createInvoice(payload) {
  const r = await apiFetch('/api/v1/invoices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `createInvoice failed: ${r.status}`));
  }
  return (await r.json()).data;
}
export async function updateInvoice(id, payload) {
  const r = await apiFetch(`/api/v1/invoices/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(formatApiError(err, `updateInvoice failed: ${r.status}`));
  }
  return (await r.json()).data;
}
export async function sendInvoice(id) {
  const r = await apiFetch(`/api/v1/invoices/${id}/send`, { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `sendInvoice failed: ${r.status}`);
  }
  // Returns { ok, emailSent, landingUrl, publicToken }
  return await r.json();
}
export async function payInvoiceCheckout(id) {
  // Agent-side: open Stripe Checkout for an invoice (e.g. to test or send link manually).
  const r = await apiFetch(`/api/v1/invoices/${id}/pay`, { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `payInvoiceCheckout failed: ${r.status}`);
  }
  return (await r.json()).url;
}
export async function deleteInvoice(id) {
  const r = await apiFetch(`/api/v1/invoices/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteInvoice failed: ${r.status}`);
  return true;
}

// Public PDF URL — anyone with the token can fetch (no auth header needed).
// Used in the recipient-facing invoice landing page.
export function invoicePdfPublicUrl(token) {
  return `/invoice-view/${encodeURIComponent(token)}/pdf`;
}

// Agent-side PDF preview: fetches with auth, returns a blob URL the caller
// can open via window.open(url) or attach to an <iframe>.
// IMPORTANT: revoke the URL after use to free memory: URL.revokeObjectURL(url).
export async function downloadInvoicePdfAuthed(id) {
  const r = await apiFetch(`/api/v1/invoices/${id}/pdf`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `downloadInvoicePdfAuthed failed: ${r.status}`);
  }
  const blob = await r.blob();
  return URL.createObjectURL(blob);
}

// Agent-side QUOTE PDF (with per-option coverage detail). Same pattern as
// downloadInvoicePdfAuthed: returns a blob URL — revoke after use.
export async function downloadQuotePdfAuthed(id) {
  const r = await apiFetch(`/api/v1/quotes/${id}/pdf`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `downloadQuotePdfAuthed failed: ${r.status}`);
  }
  const blob = await r.blob();
  return URL.createObjectURL(blob);
}

// ── Public invoice (recipient-facing, no auth) ─────────────────────────
// Uses raw fetch (not apiFetch) — no auth header.
export async function viewInvoicePublic(token) {
  const r = await fetch(`${API_BASE}/invoice-view/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `viewInvoicePublic failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function payInvoicePublic(token) {
  const r = await fetch(`${API_BASE}/invoice-view/${encodeURIComponent(token)}/pay`, { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `payInvoicePublic failed: ${r.status}`);
  }
  return (await r.json()).url;
}

// ── User Email Integration (OAuth Gmail / Microsoft 365) ───────────────────

export async function getEmailIntegrationStatus() {
  const r = await apiFetch('/api/v1/email/status');
  if (!r.ok) throw new Error(`getEmailIntegrationStatus failed: ${r.status}`);
  return r.json();
}
export async function connectGoogleEmail() {
  const r = await apiFetch('/api/v1/email/google/connect', { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `connectGoogleEmail failed: ${r.status}`);
  }
  return r.json();
}
// authority: 'consumers' (personal Outlook/Hotmail/Live, default) or
// 'organizations' (corporate Microsoft 365 work account). The backend
// uses the matching login endpoint — they are NOT interchangeable.
export async function connectMicrosoftEmail(authority = 'consumers') {
  const r = await apiFetch('/api/v1/email/microsoft/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ authority }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `connectMicrosoftEmail failed: ${r.status}`);
  }
  return r.json();
}
export async function disconnectEmail() {
  const r = await apiFetch('/api/v1/email', { method: 'DELETE' });
  if (!r.ok) throw new Error(`disconnectEmail failed: ${r.status}`);
  return r.json();
}

// ── Two-way calendar sync (Option B) — Deskte appointments → Google/Outlook ──
// Same OAuth popup + postMessage('deskte-oauth-result', kind:'calendar') flow
// as email, but a separate connection with calendar write scope.

export async function getCalendarSyncStatus() {
  const r = await apiFetch('/api/v1/calendar-sync/status');
  if (!r.ok) throw new Error(`getCalendarSyncStatus failed: ${r.status}`);
  return r.json();
}
export async function connectGoogleCalendarSync() {
  const r = await apiFetch('/api/v1/calendar-sync/google/connect', { method: 'POST' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || j.error || `connectGoogleCalendarSync failed: ${r.status}`);
  return j;
}
export async function connectMicrosoftCalendarSync(authority = 'consumers') {
  const r = await apiFetch('/api/v1/calendar-sync/microsoft/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ authority }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || j.error || `connectMicrosoftCalendarSync failed: ${r.status}`);
  return j;
}
// enabled=false pauses outbound push without dropping the connection.
export async function toggleCalendarSync(enabled) {
  const r = await apiFetch('/api/v1/calendar-sync/toggle', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled: !!enabled }),
  });
  if (!r.ok) throw new Error(`toggleCalendarSync failed: ${r.status}`);
  return r.json();
}
// Backfill: push upcoming manual appointments not yet mirrored.
export async function syncCalendarPush() {
  const r = await apiFetch('/api/v1/calendar-sync/sync', { method: 'POST' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `syncCalendarPush failed: ${r.status}`);
  return j.data || j;
}
export async function disconnectCalendarSync() {
  const r = await apiFetch('/api/v1/calendar-sync', { method: 'DELETE' });
  if (!r.ok) throw new Error(`disconnectCalendarSync failed: ${r.status}`);
  return r.json();
}

// ── WhatsApp browser-extension token ───────────────────────────────────────
// Status only — never returns the raw token (the backend stores only a hash).
export async function getWhatsappExtensionToken() {
  const r = await apiFetch('/api/v1/integrations/whatsapp-token');
  if (!r.ok) throw new Error(`getWhatsappExtensionToken failed: ${r.status}`);
  return (await r.json()).data;
}
// Generate / rotate. Returns { token, lastFour } — the raw token is shown ONCE.
export async function generateWhatsappExtensionToken() {
  const r = await apiFetch('/api/v1/integrations/whatsapp-token', { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `generateWhatsappExtensionToken failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function revokeWhatsappExtensionToken() {
  const r = await apiFetch('/api/v1/integrations/whatsapp-token', { method: 'DELETE' });
  if (!r.ok) throw new Error(`revokeWhatsappExtensionToken failed: ${r.status}`);
  return r.json();
}

// ── BYO SMS / voice provider connections (SignalWire / Twilio / RingCentral) ──
// Phase 2: real SMS send via the agent's connected Twilio account.
// Throws if no Twilio integration is connected (error.message === 'no-integration').
export async function sendClientSms(clientId, { message, mediaUrls }) {
  const r = await apiFetch('/api/v1/phone/send-sms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // mediaUrls: raw blob refs (temp-uploads) — the backend validates the
    // tenant prefix and signs them for the provider (MMS).
    body: JSON.stringify({ clientId, message, ...(Array.isArray(mediaUrls) && mediaUrls.length ? { mediaUrls } : {}) }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || err.message || `sendClientSms failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Two-way SMS conversation with a client (inbound replies + outbound sends).
export async function getClientSmsThread(clientId) {
  const r = await apiFetch(`/api/v1/phone/messages?clientId=${encodeURIComponent(clientId)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || err.message || `getClientSmsThread failed: ${r.status}`);
  }
  return (await r.json()).data.messages || [];
}

export async function getPhoneIntegrations() {
  const r = await apiFetch('/api/v1/phone');
  if (!r.ok) throw new Error(`getPhoneIntegrations failed: ${r.status}`);
  // { integrations, voiceBaaProviders, canCallInApp,
  //   smsA2pProviders, smsA2pAttestations, smsA2pBlocked, a2pProviders,
  //   canSendBulkSms, bulkSmsBlockedReason }
  //
  // `bulkSmsBlockedReason` es 'sin-linea' | 'sin-10dlc' | 'bloqueado', y hay
  // que ensenarlo: los tres tienen arreglos distintos y un boton gris sin
  // motivo obliga a adivinar cual (regla de oro 83).
  return (await r.json()).data;
}

// La agencia declara el registro A2P 10DLC de su linea. Sin el, las operadoras
// de EE.UU. FILTRAN los envios masivos sin devolver error.
export async function attestSmsA2p(provider, attested) {
  const r = await apiFetch('/api/v1/phone/a2p', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, attested }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || !json.ok) throw new Error(json.error || `attestSmsA2p failed: ${r.status}`);
  return json.data;
}

// Place a call THROUGH the connected provider (the client's phone rings from
// the agency's number), instead of handing a tel: link to whatever app the
// agent's computer happens to have registered. Refused server-side unless the
// agency has a signed BAA for that provider — the caller must handle the
// documented reasons rather than showing a raw error.
// Returns { callId, provider }; throws Error with `.reason` set on failure.
export async function placeInAppCall(clientId) {
  const r = await apiFetch('/api/v1/phone/call', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || !json.ok) {
    const err = new Error(json.message || json.error || `placeInAppCall failed: ${r.status}`);
    err.reason = json.error || 'unknown'; // not-enabled | no-integration | provider-unsupported | no-callback | no-phone
    throw err;
  }
  return json.data;
}
// What the provider actually did with a call Deskte placed. MightyCall
// accepts a call with 200 and can then abandon it silently, so the UI polls
// this instead of showing "Calling…" forever. Returns null for "no news" —
// never render that as a failure.
export async function getCallStatus(callId) {
  const r = await apiFetch(`/api/v1/phone/call/${encodeURIComponent(callId)}/status`);
  if (!r.ok) return null;
  return (await r.json().catch(() => ({}))).data ?? null;
}

// payload: { provider, phoneNumber, accountId, authToken, spaceUrl? }
export async function connectPhoneProvider(payload) {
  const r = await apiFetch('/api/v1/phone/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `connectPhoneProvider failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function disconnectPhoneProvider(provider) {
  const r = await apiFetch(`/api/v1/phone/${encodeURIComponent(provider)}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`disconnectPhoneProvider failed: ${r.status}`);
  return r.json();
}
// Choose which connected provider handles each channel. Pass a provider id to
// assign, null to unassign, or omit a channel to leave it unchanged.
// e.g. setPhoneChannels({ sms: 'TWILIO', voice: 'RINGCENTRAL' }).
export async function setPhoneChannels({ sms, voice } = {}) {
  const body = {};
  if (sms !== undefined) body.sms = sms;
  if (voice !== undefined) body.voice = voice;
  const r = await apiFetch('/api/v1/phone/channels', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setPhoneChannels failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// The agency declares its OWN signed BAA for a provider. In the BYO model the
// agency contracts with the provider directly, so it is the only party that
// can truthfully attest — and the declaration is recorded with who and when,
// which is the evidence trail. Agency owner / solo agent only.
export async function setOwnVoiceBaa(provider, attested) {
  const r = await apiFetch('/api/v1/phone/baa', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, attested }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setOwnVoiceBaa failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Auto-link on reply (Twilio): turn ON (points the number's inbound webhook at
// us via the Twilio API) / OFF. Surfaces the backend's friendly message.
export async function configurePhoneInbound(provider = 'TWILIO') {
  const r = await apiFetch(`/api/v1/phone/configure-inbound`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ provider }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || json.ok === false) throw new Error(json.message || json.error || `configure-inbound failed: ${r.status}`);
  return json.data;
}
export async function disablePhoneInbound(provider = 'TWILIO') {
  const r = await apiFetch(`/api/v1/phone/disable-inbound`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ provider }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || json.ok === false) throw new Error(json.message || json.error || `disable-inbound failed: ${r.status}`);
  return json.data;
}
// One-click "turn on receiving" for Quo — creates the SMS + call + summary
// webhooks on the agent's Quo workspace via their stored API key.
export async function configureQuoWebhooks() {
  const r = await apiFetch(`/api/v1/phone/quo/configure-webhooks`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || json.ok === false) throw new Error(json.message || json.error || `configure-quo-webhooks failed: ${r.status}`);
  return json.data; // { inboundWebhookUrl, sms, calls, summaries }
}

// GoTo Connect is the only provider that cannot work from pasted credentials
// alone: its API takes an OAuth token, so after saving the Client ID/Secret the
// agent has to say yes on GoTo's own screen. This returns the URL to open for
// that — the popup comes back to /oauth-callback like the mailbox flows.
// Returns { authUrl, redirectUri }.
export async function startGotoAuthorization() {
  const r = await apiFetch('/api/v1/phone/goto/authorize-url', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || json.ok === false) throw new Error(json.message || json.error || `goto-authorize-url failed: ${r.status}`);
  return json.data;
}

// The GoTo line calls go out from. Separate from connectPhoneProvider on
// purpose: that one asks for the Client Secret again and drops the
// authorization, so correcting a line there would cost a whole re-authorize.
// Pass '' to clear it and let the next authorization look it up again.
export async function setGotoLine(lineId) {
  const r = await apiFetch('/api/v1/phone/goto/line', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lineId: lineId || '' }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || json.ok === false) throw new Error(json.message || json.error || `goto-line failed: ${r.status}`);
  return json.data;
}

// ── Booking Link ───────────────────────────────────────────────────────────
// Protected: config + reservations of a Booking Link. `para` = de quién es el
// enlace (Lissette 2026-10-17: la dueña y los seats pueden cambiar el de
// cualquier agente de la agencia). Sin `para`, el propio.
const conPara = (ruta, para) => (para ? `${ruta}?for=${encodeURIComponent(para)}` : ruta);
export async function getBookingEditables() {
  const r = await apiFetch('/api/v1/booking/editables');
  if (!r.ok) throw new Error(`getBookingEditables failed: ${r.status}`);
  return (await r.json()).data || [];
}
// VARIOS ENLACES POR PERSONA (Lissette 2026-10-19): `enlace` = cuál de los
// enlaces de esa persona (Salud, Medicare…). Sin él, el primero.
const rutaDelEnlace = (ruta, para, enlace) => {
  const q = new URLSearchParams();
  if (para) q.set('for', para);
  if (enlace) q.set('enlace', enlace);
  const t = q.toString();
  return t ? `${ruta}?${t}` : ruta;
};
export async function createBookingLink(nombre, para) {
  const r = await apiFetch(conPara('/api/v1/booking/config', para), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nombre: nombre || undefined }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createBookingLink failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteBookingLink(id, para) {
  const r = await apiFetch(conPara(`/api/v1/booking/config/${encodeURIComponent(id)}`, para), { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteBookingLink failed: ${r.status}`);
  }
  return true;
}
export async function getBookingConfig(para, enlace) {
  const r = await apiFetch(rutaDelEnlace('/api/v1/booking/config', para, enlace));
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getBookingConfig failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function saveBookingConfig(payload, para, enlace) {
  const r = await apiFetch(rutaDelEnlace('/api/v1/booking/config', para, enlace), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `saveBookingConfig failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function getBookingReservations(para) {
  const r = await apiFetch(conPara('/api/v1/booking/bookings', para));
  if (!r.ok) throw new Error(`getBookingReservations failed: ${r.status}`);
  return (await r.json()).data.bookings;
}

// Public (no auth): the /book/:slug landing page.
export async function getPublicBooking(slug) {
  const r = await fetch(`${API_BASE}/booking/${encodeURIComponent(slug)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getPublicBooking failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Public client resource portal — no auth. Returns { client, resources }.
export async function getPublicResourcePortal(token) {
  const r = await fetch(`${API_BASE}/resource-portal/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getPublicResourcePortal failed: ${r.status}`);
  }
  return (await r.json()).data;
}

/**
 * Una firma FRESCA de un recurso del portal PUBLICO, pedida al tocar el enlace.
 *
 * Lissette 2026-09-02, queja 13 (ticket de Gabriel De Silva): "Ver Video
 * redirige a Azure y sale un error XML de autenticacion".
 *
 * La lista del portal se firma UNA vez al abrir la pagina, con dos horas de
 * vida. Pasadas esas dos horas -- una pestana abierta toda la tarde, o un
 * enlace que el cliente abre por la manana y toca por la noche -- cada clic
 * llevaba a la pagina de error XML de Azure.
 *
 * El CRM ya resolvia esto con BlobLink, pero BlobLink re-firma contra
 * /api/v1/blob-url/refresh, que EXIGE SESION. Aqui no hay sesion: el visitante
 * es un cliente con un enlace. Por eso el permiso viaja en el token del portal
 * y no en una cabecera de autenticacion.
 *
 * Ante cualquier fallo devuelve la URL que ya tenia: peor que un enlace viejo
 * es no tener enlace.
 */
export async function freshPublicResourceFileUrl(token, res) {
  const actual = res?.fileUrl || res?.fileData || '';
  if (!token || !res?.id || !isDeskteBlobUrl(res.fileUrl)) return actual;
  try {
    const r = await fetch(
      `${API_BASE}/resource-portal/${encodeURIComponent(token)}/file-url/${encodeURIComponent(res.id)}`,
    );
    const json = await r.json().catch(() => ({}));
    if (!r.ok || !json?.data?.url) return actual;
    return json.data.url;
  } catch {
    return actual;
  }
}

export async function getPublicBookingAvailability(slug, linea) {
  // `linea` (salud/medicare/vida): las horas libres PARA esa línea — una hora
  // bloqueada solo para Salud sigue libre para Medicare (Lissette 2026-10-11).
  const q = linea ? `?linea=${encodeURIComponent(linea)}` : '';
  const r = await fetch(`${API_BASE}/booking/${encodeURIComponent(slug)}/availability${q}`);
  if (!r.ok) throw new Error(`getPublicBookingAvailability failed: ${r.status}`);
  return (await r.json()).data;
}
// Captcha de los formularios públicos: { siteKey } o { siteKey: null } si está
// apagado (auditoría de seguridad 2026-09-27). Sin sesión: es para visitantes.
export async function getCaptchaConfig() {
  const r = await fetch(`${API_BASE}/captcha/config`);
  if (!r.ok) throw new Error(`getCaptchaConfig failed: ${r.status}`);
  return (await r.json()).data;
}
const cabeceraCaptcha = (token) => (token ? { 'X-Captcha-Token': token } : {});

export async function reservePublicBooking(slug, payload, captchaToken) {
  const r = await fetch(`${API_BASE}/booking/${encodeURIComponent(slug)}/reserve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...cabeceraCaptcha(captchaToken) },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `reservePublicBooking failed: ${r.status}`);
  return json.data;
}

// 410 = el enlace existió y ya no vale (venció o lo revocaron). Se marca en el
// error para que la página pueda decir "pídele otro a tu agente" en vez del
// mensaje genérico de enlace roto: son dos situaciones distintas y sólo una
// tiene solución del lado del cliente.
function marcarEnlaceMuerto(err, status) {
  if (status === 410) err.linkGone = true;
  return err;
}

// Public (no auth): the /intake/:token enrollment form shown after a client
// accepts a plan. GET returns prefill + accepted-plan summary; submit creates
// the client + policy server-side.
export async function getPublicIntake(token) {
  const r = await fetch(`${API_BASE}/intake/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw marcarEnlaceMuerto(new Error(err.error || `getPublicIntake failed: ${r.status}`), r.status);
  }
  return (await r.json()).data;
}
export async function submitPublicIntake(token, payload) {
  const r = await fetch(`${API_BASE}/intake/${encodeURIComponent(token)}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `submitPublicIntake failed: ${r.status}`);
  return json.data;
}


// ── Assistant supervisor + permissions (granted by their supervisor) ───────

export async function updateAssistantSupervisor(userId, supervisorId) {
  const r = await apiFetch(`/api/v1/team/members/${encodeURIComponent(userId)}/supervisor`, {
    method: 'PATCH',
    body: JSON.stringify({ supervisorId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateAssistantSupervisor failed: ${r.status}`);
  }
  return r.json();
}

// Promote an agent to co-owner, or set a co-owner back to agent.
// `role` is the BACKEND enum string: "AGENCY_OWNER" or "AGENT".
// Owner-only on the backend; only AGENT<->AGENCY_OWNER transitions allowed.
export async function updateMemberRole(userId, role) {
  const r = await apiFetch(`/api/v1/team/members/${encodeURIComponent(userId)}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateMemberRole failed: ${r.status}`);
  }
  return r.json();
}


// Soft-delete (deactivate) a team member. The backend marks them inactive
// and sets deletedAt; subsequent listUsers() calls won't return them.
export async function deleteTeamMember(userId) {
  const r = await apiFetch(`/api/v1/team/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteTeamMember failed: ${r.status}`);
  }
  return r.json();
}

// Update the seat profile (custom display label + granular per-module permissions).
// Both fields optional; seatPermissions is a partial patch — only the keys you
// pass are updated, the rest stay as-is.
export async function updateSeatProfile(userId, { seatLabel, seatPermissions, seatScope } = {}) {
  const body = {};
  if (seatLabel !== undefined) body.seatLabel = seatLabel;
  if (seatPermissions && typeof seatPermissions === 'object') body.seatPermissions = seatPermissions;
  if (seatScope !== undefined) body.seatScope = seatScope;
  const r = await apiFetch(`/api/v1/team/members/${encodeURIComponent(userId)}/seat`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateSeatProfile failed: ${r.status}`);
  }
  return r.json();
}

// Turn the agency's "private books" mode on/off (owner-only on the backend).
// When on, owners + agency-scoped seats see only the owners' shared pool and
// each agent's book stays private. Returns { ok, data: { id, agentBooksPrivate } }.
export async function setAgentBooksPrivate(enabled) {
  const r = await apiFetch('/api/v1/agencies/me/agent-books-private', {
    method: 'PATCH',
    body: JSON.stringify({ enabled: !!enabled }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `setAgentBooksPrivate failed: ${r.status}`);
  }
  return r.json();
}

// ── Lead Forms (public capture forms shared by an agent) ───────────────────

export async function listForms() {
  const r = await apiFetch('/api/v1/forms');
  if (!r.ok) throw new Error(`listForms failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function getForm(id) {
  const r = await apiFetch(`/api/v1/forms/${id}`);
  if (!r.ok) throw new Error(`getForm failed: ${r.status}`);
  return (await r.json()).data;
}
export async function createForm(payload) {
  const r = await apiFetch('/api/v1/forms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createForm failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateForm(id, payload) {
  const r = await apiFetch(`/api/v1/forms/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateForm failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteForm(id) {
  const r = await apiFetch(`/api/v1/forms/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteForm failed: ${r.status}`);
  return true;
}
export async function listFormSubmissions(id) {
  const r = await apiFetch(`/api/v1/forms/${id}/submissions`);
  if (!r.ok) throw new Error(`listFormSubmissions failed: ${r.status}`);
  return (await r.json()).data || [];
}

// ── Applications (templates + sending) ──────────────────────────────
export async function listApplicationTemplates() {
  const r = await apiFetch('/api/v1/applications/templates');
  if (!r.ok) throw new Error(`listApplicationTemplates failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createApplicationTemplate(payload) {
  const r = await apiFetch('/api/v1/applications/templates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `createApplicationTemplate failed: ${r.status}`); }
  return (await r.json()).data;
}
export async function updateApplicationTemplate(id, payload) {
  const r = await apiFetch(`/api/v1/applications/templates/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `updateApplicationTemplate failed: ${r.status}`); }
  return (await r.json()).data;
}
export async function deleteApplicationTemplate(id) {
  const r = await apiFetch(`/api/v1/applications/templates/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteApplicationTemplate failed: ${r.status}`);
  return true;
}
export async function listApplications(clientId) {
  const q = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  const r = await apiFetch(`/api/v1/applications${q}`);
  if (!r.ok) throw new Error(`listApplications failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function sendApplication(payload) {
  const r = await apiFetch('/api/v1/applications/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `sendApplication failed: ${r.status}`); }
  return (await r.json()).data;
}
// Reenviar el enlace de una solicitud: acuña uno NUEVO y mata el anterior.
// Es la contraparte de que el enlace dure 72 horas — sin esto, "se me venció"
// obligaría a montar la solicitud otra vez desde cero.
export async function resendApplication(id) {
  const r = await apiFetch(`/api/v1/applications/${encodeURIComponent(id)}/resend`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `resendApplication failed: ${r.status}`);
  return json.data;
}

// Matar el enlace de una solicitud ahora. La caducidad de 72 h no sirve para
// el caso que pide un botón: se acaba de mandar a la dirección equivocada.
export async function revokeApplication(id) {
  const r = await apiFetch(`/api/v1/applications/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `revokeApplication failed: ${r.status}`);
  return json.data;
}

export async function deleteApplication(id) {
  const r = await apiFetch(`/api/v1/applications/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `deleteApplication failed: ${r.status}`); }
  return true;
}
// Raw answers the client submitted (audit copy), to review the application as filled.
export async function getApplicationSubmission(id) {
  const r = await apiFetch(`/api/v1/applications/${encodeURIComponent(id)}/submission`);
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `getApplicationSubmission failed: ${r.status}`); }
  return (await r.json()).data;
}
// PUBLIC application form — no auth.
export async function getPublicApplication(token) {
  const r = await fetch(`${API_BASE}/application/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw marcarEnlaceMuerto(new Error(e.error || `getPublicApplication failed: ${r.status}`), r.status);
  }
  return (await r.json()).data;
}
export async function submitPublicApplication(token, payload) {
  const r = await fetch(`${API_BASE}/application/${encodeURIComponent(token)}/submit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `submitPublicApplication failed: ${r.status}`);
  return json.data;
}

// PUBLIC — no auth, no apiFetch wrapper.
export async function getPublicForm(slug) {
  const r = await fetch(`${API_BASE}/forms/${encodeURIComponent(slug)}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `getPublicForm failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function submitPublicForm(slug, data, captchaToken) {
  const r = await fetch(`${API_BASE}/forms/${encodeURIComponent(slug)}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...cabeceraCaptcha(captchaToken) },
    body: JSON.stringify({ data }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `submitPublicForm failed: ${r.status}`);
  return json;
}

// ── Process Templates (Action Templates) ──────────────────────────────────

export async function listTemplates({ category, policyType } = {}) {
  const qs = new URLSearchParams();
  if (category) qs.set('category', category);
  if (policyType) qs.set('policyType', policyType);
  const r = await apiFetch(`/api/v1/templates${qs.toString() ? `?${qs.toString()}` : ''}`);
  if (!r.ok) throw new Error(`listTemplates failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function getTemplate(id) {
  const r = await apiFetch(`/api/v1/templates/${id}`);
  if (!r.ok) throw new Error(`getTemplate failed: ${r.status}`);
  return (await r.json()).data;
}
export async function createTemplate(payload) {
  const r = await apiFetch('/api/v1/templates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createTemplate failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateTemplate(id, payload) {
  const r = await apiFetch(`/api/v1/templates/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateTemplate failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteTemplate(id) {
  const r = await apiFetch(`/api/v1/templates/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteTemplate failed: ${r.status}`);
  return true;
}
export async function applyTemplate(id, { clientId, triggerDate, assigneeId } = {}) {
  const r = await apiFetch(`/api/v1/templates/${id}/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId, triggerDate, assigneeId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `applyTemplate failed: ${r.status}`);
  }
  return (await r.json()).data; // { tasks: [...], templateName }
}

// ── Client Portal — admin (manage tokens) ────────────────────────────────

export async function listPortalTokens(clientId) {
  const r = await apiFetch(`/api/v1/portal/clients/${clientId}/tokens`);
  if (!r.ok) throw new Error(`listPortalTokens failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createPortalToken(clientId, payload = {}) {
  const r = await apiFetch(`/api/v1/portal/clients/${clientId}/tokens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createPortalToken failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function revokePortalToken(tokenId) {
  const r = await apiFetch(`/api/v1/portal/tokens/${tokenId}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`revokePortalToken failed: ${r.status}`);
  return true;
}

// ── Client Portal — public landing (no auth, token-protected) ────────────

// Los 4 últimos del SSN, en cabecera y nunca en la URL (portal y renovación).
const cabeceraDeCodigo = (codigo) => (codigo ? { 'X-Codigo-Verificacion': String(codigo) } : {});

// El candado del portal (Lissette 2026-10-19), el mismo del enlace de
// renovación: cada llamada lleva los 4 últimos del SSN en una CABECERA. Una
// respuesta con `data.locked` no es un error: se devuelve para pintar el candado.
async function publicPortalGet(token, path, codigo) {
  const r = await fetch(`${API_BASE}/portal/${encodeURIComponent(token)}${path}`, {
    headers: cabeceraDeCodigo(codigo),
  });
  const json = await r.json().catch(() => ({}));
  if (json?.data?.locked) return { ...json.data, error: json.error };
  if (!r.ok) throw new Error(json.error || `Portal request failed: ${r.status}`);
  return json.data;
}
export const getPortalOverview   = (t, codigo) => publicPortalGet(t, '/overview', codigo);
export const getPortalPolicies   = (t, codigo) => publicPortalGet(t, '/policies', codigo);
export const getPortalDocuments  = (t, codigo) => publicPortalGet(t, '/documents', codigo);
export const getPortalSignatures = (t, codigo) => publicPortalGet(t, '/signatures', codigo);
export const getPortalQuotes     = (t, codigo) => publicPortalGet(t, '/quotes', codigo);
export const getPortalInvoices   = (t, codigo) => publicPortalGet(t, '/invoices', codigo);

// ── Renewal Review — admin (manage links) ────────────────────────────────
// At renewal time the agent generates an encrypted link where the client
// confirms their personal data ("Todo correcto" / "Necesito un ajuste").

export async function listRenewalReviews(clientId) {
  const r = await apiFetch(`/api/v1/portal/clients/${clientId}/renewal-reviews`);
  if (!r.ok) throw new Error(`listRenewalReviews failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createRenewalReview(clientId, payload = {}) {
  const r = await apiFetch(`/api/v1/portal/clients/${clientId}/renewal-reviews`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload), // { fields:[], policyId?, label?, expiresInDays? }
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createRenewalReview failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function revokeRenewalReview(id) {
  const r = await apiFetch(`/api/v1/portal/renewal-reviews/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`revokeRenewalReview failed: ${r.status}`);
  return true;
}

// ── Renewal Review — public landing (no auth, token-protected) ───────────

// El candado del enlace (Lissette 2026-09-25 y 2026-10-01): el servidor pide
// SOLO los 4 ultimos del SSN; sin SSN no abre. El codigo viaja en una
// CABECERA, nunca en la URL. Mientras falte o este mal, el servidor contesta
// 401/403/429 con `data.locked` — eso NO es un error de enlace: se
// devuelve tal cual para que la pagina pinte el candado.

export async function getRenewalReview(token, codigo) {
  const r = await fetch(`${API_BASE}/portal/review/${encodeURIComponent(token)}`, {
    headers: cabeceraDeCodigo(codigo),
  });
  const json = await r.json().catch(() => ({}));
  if (json?.data?.locked) return { ...json.data, error: json.error };
  if (!r.ok) throw new Error(json.error || `Review request failed: ${r.status}`);
  return json.data;
}
export async function respondRenewalReview(token, payload, codigo) {
  const r = await fetch(`${API_BASE}/portal/review/${encodeURIComponent(token)}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...cabeceraDeCodigo(codigo) },
    // { response: 'correct'|'adjust', comment?, answers? }
    // `answers` = las preguntas Sí/No del enlace, p. ej. { idme: 'yes' }.
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `Respond failed: ${r.status}`);
  return json.data;
}

// ── Renewal Review — Renewals 2-step flow (aviso → link) ─────────────────

// Ensure an active review exists for (client, policy); returns { review, url }.
export async function ensureRenewalReview(clientId, payload = {}) {
  const r = await apiFetch(`/api/v1/portal/clients/${clientId}/renewal-reviews/ensure`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload), // { policyId?, fields? }
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `ensureRenewalReview failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Send the teaser ("notice") or the link SMS for a review. Surfaces the
// backend's friendly message (no-phone / no-integration / twilio error).
export async function sendRenewalReviewSms(reviewId, payload) {
  const r = await apiFetch(`/api/v1/portal/renewal-reviews/${reviewId}/sms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload), // { kind: 'notice'|'link', message }
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    // EL CODIGO VIAJA CON EL ERROR, no solo el texto. La ruta contesta
    // `{ error: 'no-integration' | 'no-phone' | 'suppressed', message }`, y ese
    // codigo es lo que deja traducir el motivo al idioma de la pantalla
    // (crm-app/motivoDelSmsDeRenovacion.js). Con solo el `message` en ingles, el
    // envio en lote no tenia nada que ensenar salvo un numero.
    const err = new Error(json.message || json.error || `sendRenewalReviewSms failed: ${r.status}`);
    err.code = typeof json.error === 'string' ? json.error : '';
    err.status = r.status;
    throw err;
  }
  return json.data;
}
// El link de renovación por CORREO, para las agencias sin línea de SMS
// (Lissette 2026-10-05). Mismo contrato de errores que el SMS: el código viaja
// en `err.code` para que motivoDelSms lo diga en el idioma de la pantalla.
export async function sendRenewalReviewEmail(reviewId, payload) {
  const r = await apiFetch(`/api/v1/portal/renewal-reviews/${reviewId}/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload), // { subject, message }
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(json.message || json.error || `sendRenewalReviewEmail failed: ${r.status}`);
    err.code = typeof json.error === 'string' ? json.error : '';
    err.status = r.status;
    throw err;
  }
  return json.data;
}
// Latest review per policy id (for the Renewals status chip).
export async function renewalReviewsByPolicies(policyIds) {
  const r = await apiFetch(`/api/v1/portal/renewal-reviews/by-policies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policyIds }),
  });
  if (!r.ok) throw new Error(`renewalReviewsByPolicies failed: ${r.status}`);
  return (await r.json()).data || [];
}

// ── Recurring Tasks ─────────────────────────────────────────────────────

export async function listRecurringTasks(clientId) {
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  const r = await apiFetch(`/api/v1/recurring-tasks${qs}`);
  if (!r.ok) throw new Error(`listRecurringTasks failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createRecurringTask(payload) {
  const r = await apiFetch('/api/v1/recurring-tasks', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createRecurringTask failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateRecurringTask(id, payload) {
  const r = await apiFetch(`/api/v1/recurring-tasks/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateRecurringTask failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteRecurringTask(id) {
  const r = await apiFetch(`/api/v1/recurring-tasks/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteRecurringTask failed: ${r.status}`);
  return true;
}
export async function pauseRecurringTask(id) {
  const r = await apiFetch(`/api/v1/recurring-tasks/${id}/pause`, { method: 'POST' });
  if (!r.ok) throw new Error(`pauseRecurringTask failed: ${r.status}`);
  return (await r.json()).data;
}
export async function resumeRecurringTask(id) {
  const r = await apiFetch(`/api/v1/recurring-tasks/${id}/resume`, { method: 'POST' });
  if (!r.ok) throw new Error(`resumeRecurringTask failed: ${r.status}`);
  return (await r.json()).data;
}
export async function runRecurringTaskNow(id) {
  const r = await apiFetch(`/api/v1/recurring-tasks/${id}/run-now`, { method: 'POST' });
  if (!r.ok) throw new Error(`runRecurringTaskNow failed: ${r.status}`);
  return (await r.json()).data;
}

// ── Recurring Invoices ─────────────────────────────────────────────────

export async function listRecurringInvoices(clientId) {
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  const r = await apiFetch(`/api/v1/recurring-invoices${qs}`);
  if (!r.ok) throw new Error(`listRecurringInvoices failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createRecurringInvoice(payload) {
  const r = await apiFetch('/api/v1/recurring-invoices', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createRecurringInvoice failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateRecurringInvoice(id, payload) {
  const r = await apiFetch(`/api/v1/recurring-invoices/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateRecurringInvoice failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteRecurringInvoice(id) {
  const r = await apiFetch(`/api/v1/recurring-invoices/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteRecurringInvoice failed: ${r.status}`);
  return true;
}
export async function pauseRecurringInvoice(id) {
  const r = await apiFetch(`/api/v1/recurring-invoices/${id}/pause`, { method: 'POST' });
  if (!r.ok) throw new Error(`pauseRecurringInvoice failed: ${r.status}`);
  return (await r.json()).data;
}
export async function resumeRecurringInvoice(id) {
  const r = await apiFetch(`/api/v1/recurring-invoices/${id}/resume`, { method: 'POST' });
  if (!r.ok) throw new Error(`resumeRecurringInvoice failed: ${r.status}`);
  return (await r.json()).data;
}
export async function runRecurringInvoiceNow(id) {
  const r = await apiFetch(`/api/v1/recurring-invoices/${id}/run-now`, { method: 'POST' });
  if (!r.ok) throw new Error(`runRecurringInvoiceNow failed: ${r.status}`);
  return await r.json();
}

// ── AI Assistant ───────────────────────────────────────────────────────

export async function aiDraftEmail(payload) {
  const r = await apiFetch('/api/v1/ai/draft-email', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiDraftEmail failed: ${r.status}`);
  }
  return await r.json();
}
export async function aiSummarize(payload) {
  const r = await apiFetch('/api/v1/ai/summarize', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw makeApiError(err, `aiSummarize failed: ${r.status}`);
  }
  return await r.json();
}
export async function aiSuggestNextAction(payload) {
  const r = await apiFetch('/api/v1/ai/suggest-next-action', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiSuggestNextAction failed: ${r.status}`);
  }
  return await r.json();
}
// Translate a document/consent template between ES and EN, preserving
// {placeholder} variables. Runs on Azure OpenAI (covered by the Azure BAA).
// payload: { text, fromLang?: 'es'|'en', toLang: 'es'|'en', title? }
export async function aiTranslateTemplate(payload) {
  const r = await apiFetch('/api/v1/ai/translate', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    // Con `makeApiError` y no un Error pelado: la respuesta trae `code`
    // (TEXTO_DEMASIADO_LARGO / IA_SIN_RESPUESTA) y los números, y la pantalla
    // decide por el código en vez de adivinar por el texto del mensaje.
    const err = await r.json().catch(() => ({}));
    throw makeApiError(err, `aiTranslateTemplate failed: ${r.status}`);
  }
  return await r.json(); // { translated }
}
// Convert a pasted / uploaded document into a reusable template: the AI detects
// names, dates, addresses, income, signatures… and swaps them for {{placeholders}}.
// Runs on Azure OpenAI (Azure BAA). payload: { text, lang?: 'es'|'en', title? }
export async function aiDocToTemplate(payload) {
  const r = await apiFetch('/api/v1/ai/doc-to-template', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw makeApiError(err, `aiDocToTemplate failed: ${r.status}`);
  }
  return await r.json(); // { body }
}
// Turn an uploaded application form (PDF/Word text) into an application TEMPLATE
// structure { name, policyType, intro, sections, customFields }. Agent reviews
// before saving. payload: { text, lang: 'es'|'en', title }
export async function aiApplicationFromDoc(payload) {
  const r = await apiFetch('/api/v1/ai/application-from-doc', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiApplicationFromDoc failed: ${r.status}`);
  }
  return (await r.json()).data; // { name, policyType, intro, sections, customFields }
}
// MyGuard AI chat — grounded on the agency's live data (Azure OpenAI).
// payload: { message, history?: [{role:'user'|'ai', text}] } → { reply }
export async function aiChat(payload) {
  const r = await apiFetch('/api/v1/ai/chat', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiChat failed: ${r.status}`);
  }
  return await r.json(); // { reply }
}
// MyGuard AI with actions — same as aiChat but the assistant may return
// PROPOSALS to create/update a client (applied only after the agent confirms,
// via the normal client APIs). payload: { message, history?, context?:{openClient:{id,name}} }
// → { reply, proposals: [{ id, action, clientId?, clientName?, fields, summary }] }
export async function aiAgent(payload) {
  const r = await apiFetch('/api/v1/ai/agent', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiAgent failed: ${r.status}`);
  }
  return await r.json(); // { reply, proposals }
}
// Apply a confirmed BULK policy change. The server re-resolves the filter
// (agency-scoped) and applies the same change to every match. payload:
// { filter:{carrier?,status?,type?}, fields } → { updated: number }
export async function aiBulkPolicyUpdate(payload) {
  const r = await apiFetch('/api/v1/ai/bulk-policy-update', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiBulkPolicyUpdate failed: ${r.status}`);
  }
  return await r.json(); // { updated, snapshot }
}
// Undo a bulk policy change by restoring the snapshot returned by
// aiBulkPolicyUpdate. payload: { snapshot: [{id, prev}] } → { restored: number }
export async function aiUndoBulkPolicy(payload) {
  const r = await apiFetch('/api/v1/ai/undo-bulk-policy', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiUndoBulkPolicy failed: ${r.status}`);
  }
  return await r.json(); // { restored }
}
// Bulk CLIENT update via AI (confirm-first). payload: { filter, fields } →
// { updated, snapshot }. Fields: preferredLang/status/source only (no PHI).
export async function aiBulkClientUpdate(payload) {
  const r = await apiFetch('/api/v1/ai/bulk-client-update', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiBulkClientUpdate failed: ${r.status}`);
  }
  return await r.json(); // { updated, snapshot }
}
// Undo a bulk client change. payload: { snapshot: [{id, prev}] } → { restored }
export async function aiUndoBulkClient(payload) {
  const r = await apiFetch('/api/v1/ai/undo-bulk-client', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiUndoBulkClient failed: ${r.status}`);
  }
  return await r.json(); // { restored }
}
// Repair names a broken import split wrongly (the middle name ended up inside
// the surname). ONE page per call — loop while `nextCursor` is not null.
//
// Loop on the cursor, NOT on "how many suspects are left": most suspects are
// correctly-stored two-surname names that the server rightly refuses to touch,
// so a count-based loop would spin forever.
export async function repairClientNamesPage({ cursor = null, pageSize = 50 } = {}) {
  const r = await apiFetch('/api/v1/clients/repair-names', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cursor, pageSize }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `repairClientNamesPage failed: ${r.status}`);
  }
  return await r.json(); // { nextCursor, scanned, repaired, clientsUpdated, modelFailures }
}

// How many people still carry the fingerprint of the broken import. A headline
// only — NOT a progress denominator (see above).
export async function getNameRepairPending() {
  const r = await apiFetch('/api/v1/clients/repair-names/pending');
  if (!r.ok) throw new Error(`getNameRepairPending failed: ${r.status}`);
  return await r.json(); // { total, suspects }
}

// Recover city / state / ZIP from clients whose whole address landed in one
// box. ONE page per call — loop while `nextCursor` is not null, same shape as
// the name repair above.
//
// Why it exists (Javier Volcanes, 2026-08-28): the Clients state dropdown is
// built from the DISTINCT values of the `state` column. His book came in from
// monday with the state written INSIDE the address, so the column was empty
// and the dropdown could only offer the handful of records typed by hand
// afterwards — "only TX shows up". The ficha still displayed a state because
// it joins street + city + state + ZIP and the street already carried them.
//
// No AI here: it is text splitting, deterministic and repeatable. Running it
// twice is harmless — it only fills EMPTY columns and stops at any conflict.
export async function repairClientAddressesPage({ cursor = null, pageSize = 200 } = {}) {
  const r = await apiFetch('/api/v1/clients/repair-addresses', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cursor, pageSize }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `repairClientAddressesPage failed: ${r.status}`);
  }
  // { nextCursor, scanned, repaired, statesRecovered, citiesRecovered, conflicts }
  return await r.json();
}

// How many clients have no state at all. This is the number that answers
// "why does the filter only offer me one state?" before touching anything.
export async function getAddressRepairPending() {
  const r = await apiFetch('/api/v1/clients/repair-addresses/pending');
  if (!r.ok) throw new Error(`getAddressRepairPending failed: ${r.status}`);
  return await r.json(); // { total, withoutState }
}

// Subir a la casilla de CONYUGE al dependiente que el propio CRM dice que es la
// pareja. UNA pagina por llamada — se llama en bucle mientras `nextCursor` no
// sea null, igual que las dos reparaciones de arriba.
//
// De donde sale (Lissette, 2026-09-15, la cartera de la agencia de Denisse):
// *"varios spouse o partner se fueron como spouse pero al espacio de dependiente
// y no de spouse como tal"*. Los importadores tenian cinco listas de palabras de
// parentesco distintas y ninguna conocia "partner".
//
// Corre en el servidor porque el SSN y la fecha de nacimiento del dependiente
// estan cifrados y el navegador no los recibe: alli el texto cifrado pasa de
// columna a columna sin descifrarse. Volver a lanzarla es inofensivo: una ficha
// que ya tiene conyuge se salta.
export async function repairClientSpousePage({ cursor = null, pageSize = 200 } = {}) {
  const r = await apiFetch('/api/v1/clients/repair-spouse', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cursor, pageSize }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    // `detail` antes que `error`: el codigo corto ('no_agency') no le dice nada
    // a quien lo lee en pantalla, y el detalle ademas explica que hacer.
    throw new Error(err.detail || err.error || `repairClientSpousePage failed: ${r.status}`);
  }
  return await r.json(); // { nextCursor, scanned, repaired, polizasAjustadas }
}

// En cuantas fichas sigue la pareja metida entre los dependientes.
//
// El motivo del fallo SE LEE del cuerpo, no se tira. Preguntando desde una
// cuenta sin agencia —el PLATFORM_OWNER— el servidor contesta 400 con el
// detalle de que hay que entrar en la agencia primero; con un
// `failed: 400` a secas, el panel se quedaba mudo (Lissette, 2026-09-15).
export async function getSpouseRepairPending() {
  const r = await apiFetch('/api/v1/clients/repair-spouse/pending');
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || err.error || `getSpouseRepairPending failed: ${r.status}`);
  }
  return await r.json(); // { total, pendientes, conConyugeYa }
}

// Split a batch of person names into first/last (Hispanic order-aware) for the
// book-of-business importer. payload: { names: string[] }
//   → { names: [{firstName, lastName, ok}], failed }
//
// CHUNKED HERE ON PURPOSE (incident 2026-08-15). Both importers used to hand
// their whole book to one call. The server capped it at 400 and asked the model
// for an answer that could not fit, so it came back empty and the importer
// quietly stored "first word = name, the rest = surname" — the middle name
// landed inside the surname on ~1.7k clients. The server now caps a request at
// 100 names; sending more would SILENTLY TRUNCATE, so the slicing lives here,
// in the one place both importers go through.
//
// `failed` counts names the model could not split (the server fell back to a
// heuristic). Callers should surface it instead of importing on a guess.
const SPLIT_NAMES_PER_REQUEST = 100;

export async function aiSplitNames(payload) {
  const all = Array.isArray(payload?.names) ? payload.names : [];
  if (!all.length) return { names: [], failed: 0 };

  const names = [];
  let failed = 0;
  for (let i = 0; i < all.length; i += SPLIT_NAMES_PER_REQUEST) {
    const slice = all.slice(i, i + SPLIT_NAMES_PER_REQUEST);
    const r = await apiFetch('/api/v1/ai/split-names', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, names: slice }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `aiSplitNames failed: ${r.status}`);
    }
    const part = await r.json();
    const got = Array.isArray(part?.names) ? part.names : [];
    // Keep the answer aligned with the input even if a slice comes back short:
    // a missing entry must stay a hole, never shift the next person's name.
    for (let k = 0; k < slice.length; k++) names.push(got[k] || null);
    failed += Number(part?.failed) || 0;
  }
  return { names, failed };
}
// AI column mapper for the universal importer. Maps source file headers to
// Deskte field keys. payload: { headers:[], fields:[{key,label,hint}], samples?:[[]] }
// → { mapping: { header: fieldKey|null } }
export async function aiMapColumns(payload) {
  const r = await apiFetch('/api/v1/ai/map-columns', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiMapColumns failed: ${r.status}`);
  }
  return await r.json(); // { mapping }
}
// Live AI dashboard snapshot (counts) for grounding/UI. → { data: {...} }
export async function aiDashboard() {
  const r = await apiFetch('/api/v1/ai/dashboard', { method: 'GET' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `aiDashboard failed: ${r.status}`);
  }
  return await r.json();
}

// ── Workflows ──────────────────────────────────────────────────────────

export async function listWorkflows() {
  const r = await apiFetch('/api/v1/workflows');
  if (!r.ok) throw new Error(`listWorkflows failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createWorkflow(payload) {
  const r = await apiFetch('/api/v1/workflows', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createWorkflow failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateWorkflow(id, payload) {
  const r = await apiFetch(`/api/v1/workflows/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateWorkflow failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteWorkflow(id) {
  const r = await apiFetch(`/api/v1/workflows/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteWorkflow failed: ${r.status}`);
  return true;
}
export async function pauseWorkflow(id) {
  const r = await apiFetch(`/api/v1/workflows/${id}/pause`, { method: 'POST' });
  if (!r.ok) throw new Error(`pauseWorkflow failed: ${r.status}`);
  return (await r.json()).data;
}
export async function resumeWorkflow(id) {
  const r = await apiFetch(`/api/v1/workflows/${id}/resume`, { method: 'POST' });
  if (!r.ok) throw new Error(`resumeWorkflow failed: ${r.status}`);
  return (await r.json()).data;
}
export async function fireWorkflowTest(id) {
  const r = await apiFetch(`/api/v1/workflows/${id}/fire`, { method: 'POST' });
  if (!r.ok) throw new Error(`fireWorkflowTest failed: ${r.status}`);
  return await r.json();
}

// Run a workflow ON DEMAND (the "Manual" trigger) against a chosen client.
export async function runWorkflowOnClient(id, clientId) {
  const r = await apiFetch(`/api/v1/workflows/${id}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId: clientId || null }),
  });
  if (!r.ok) { let e = {}; try { e = await r.json(); } catch {} throw new Error(e.error || `runWorkflow failed: ${r.status}`); }
  return await r.json();
}

// ── Agent Goals ─────────────────────────────────────────────────────────

export async function listGoals() {
  const r = await apiFetch('/api/v1/goals');
  if (!r.ok) throw new Error(`listGoals failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createGoal(payload) {
  const r = await apiFetch('/api/v1/goals', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createGoal failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateGoal(id, payload) {
  const r = await apiFetch(`/api/v1/goals/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateGoal failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteGoal(id) {
  const r = await apiFetch(`/api/v1/goals/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteGoal failed: ${r.status}`);
  return true;
}
export async function getMyGoalsSummary() {
  const r = await apiFetch('/api/v1/goals/me/summary');
  if (!r.ok) throw new Error(`getMyGoalsSummary failed: ${r.status}`);
  return (await r.json()).data || [];
}

// ── Licenses (server-backed; was localStorage-only before 2026-05-19) ────
export async function listLicenses() {
  const r = await apiFetch('/api/v1/licenses');
  if (!r.ok) throw new Error(`listLicenses failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createLicense(payload) {
  const r = await apiFetch('/api/v1/licenses', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createLicense failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateLicense(id, payload) {
  const r = await apiFetch(`/api/v1/licenses/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateLicense failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// RENOVAR una licencia o certificacion en la misma tarjeta: la version vigente
// pasa a `history` y entra la nueva (Lissette 2026-10-20).
export async function renovarLicencia(id, payload) {
  const r = await apiFetch(`/api/v1/licenses/${encodeURIComponent(id)}/renovar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `renovarLicencia failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// El archivo de una version anterior: la lista no lo trae, se pide al abrirlo.
export async function archivoDeVersionDeLicencia(id, n) {
  const r = await apiFetch(`/api/v1/licenses/${encodeURIComponent(id)}/historial/${Number(n)}/archivo`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `archivoDeVersionDeLicencia failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteLicense(id) {
  const r = await apiFetch(`/api/v1/licenses/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteLicense failed: ${r.status}`);
  return true;
}

// ── Expenses (server-backed; was localStorage-only before 2026-05-19) ────
export async function listExpenses() {
  const r = await apiFetch('/api/v1/expenses');
  if (!r.ok) throw new Error(`listExpenses failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createExpense(payload) {
  const r = await apiFetch('/api/v1/expenses', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createExpense failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateExpense(id, payload) {
  const r = await apiFetch(`/api/v1/expenses/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateExpense failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteExpense(id) {
  const r = await apiFetch(`/api/v1/expenses/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteExpense failed: ${r.status}`);
  return true;
}

// ── Payroll & Forms history (simple log of generated paystubs / 1099s) ──────
export async function listPayrollDocs() {
  const r = await apiFetch('/api/v1/payroll');
  if (!r.ok) throw new Error(`listPayrollDocs failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createPayrollDoc(payload) {
  const r = await apiFetch('/api/v1/payroll', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || `createPayrollDoc failed: ${r.status}`); }
  return (await r.json()).data;
}
export async function deletePayrollDoc(id) {
  const r = await apiFetch(`/api/v1/payroll/${id}`, { method: 'DELETE' });
  if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || `deletePayrollDoc failed: ${r.status}`); }
  return true;
}

// ── Tarjeta digital: el enlace publico y el QR (2026-08-27) ─────────────
//
// La publica NO lleva `apiFetch`: `apiFetch` adjunta el token de sesion, y esta
// pagina la abre un cliente que no tiene cuenta. Se llama con `fetch` pelado,
// igual que el resto de paginas publicas.
export async function getPublicAgentCard(slug) {
  const r = await fetch(`${API_BASE}/tarjeta/${encodeURIComponent(slug)}`);
  if (!r.ok) throw new Error('Tarjeta no disponible');
  return (await r.json()).data;
}
export async function getMyAgentCard() {
  const r = await apiFetch('/api/v1/agent-card');
  if (!r.ok) throw new Error(`getMyAgentCard failed: ${r.status}`);
  return (await r.json()).data;
}
export async function saveMyAgentCard(payload) {
  const r = await apiFetch('/api/v1/agent-card', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `saveMyAgentCard failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── Resources library + portal tokens (server-backed 2026-05-19) ────────
export async function listResources() {
  const r = await apiFetch('/api/v1/resources');
  if (!r.ok) throw new Error(`listResources failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createResource(payload) {
  const r = await apiFetch('/api/v1/resources', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createResource failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateResource(id, payload) {
  const r = await apiFetch(`/api/v1/resources/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateResource failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteResource(id) {
  const r = await apiFetch(`/api/v1/resources/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteResource failed: ${r.status}`);
  return true;
}
export async function listResourceTokens() {
  const r = await apiFetch('/api/v1/resources/tokens');
  if (!r.ok) throw new Error(`listResourceTokens failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createResourceTokens(tokens) {
  const r = await apiFetch('/api/v1/resources/tokens', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tokens }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createResourceTokens failed: ${r.status}`);
  }
  return (await r.json()).data || [];
}

// Matar un enlace del portal de recursos ahora mismo. Sin esto, uno mandado a
// la dirección equivocada sigue abriendo hasta que caduque solo.
export async function revokeResourceToken(token) {
  const r = await apiFetch(`/api/v1/resources/tokens/${encodeURIComponent(token)}/revoke`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `revokeResourceToken failed: ${r.status}`);
  return json.data;
}

// ── Carrier appointment grid (server-backed 2026-05-19) ──────────────────
export async function getCarrierAppointments() {
  const r = await apiFetch('/api/v1/carrier-appointments');
  if (!r.ok) throw new Error(`getCarrierAppointments failed: ${r.status}`);
  return (await r.json()).data || { appointments: {}, custom: { categories: {}, carriers: {} } };
}
export async function toggleCarrierAppointment(category, state, carrier) {
  const r = await apiFetch('/api/v1/carrier-appointments/toggle', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ category, state, carrier }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `toggleCarrierAppointment failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateCarrierApptCustom(data) {
  const r = await apiFetch('/api/v1/carrier-appointments/custom', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateCarrierApptCustom failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// ── Agency settings blob (server-backed 2026-05-19) ──────────────────────
export async function getAgencySettings() {
  const r = await apiFetch('/api/v1/agency-settings');
  if (!r.ok) throw new Error(`getAgencySettings failed: ${r.status}`);
  return (await r.json()).data || { docTemplatesMaster: {}, emailTemplates: [], holidayCalendar: [], collaborators: [] };
}
// Customization CATALOG keys a SEAT is allowed to save — shared display lists
// (statuses, types, custom columns, custom-field defs, custom tabs). These route
// to the seat-writable /seat-config endpoint; everything else (email/doc
// templates, HIPAA, AI name, payroll, seat labels, ticket emails) stays on the
// agent-or-above PUT /. Splitting HERE means every current AND future caller of
// updateAgencySettings persists correctly for seats, with no per-call rewiring
// (Lissette 2026-07-01: "todo lo que crea un seat debe guardarse y mantenerse").
const SEAT_EDITABLE_SETTING_KEYS = new Set([
  'policyStatuses', 'policyTypes', 'policyCustomFieldDefs', 'appointmentFieldDefs',
  // Estados propios del PAGO del mes. Van con los de poliza: quien administra
  // Pagos del Mes suele ser un asiento, y si no puede guardarlos aqui, los crea
  // y desaparecen al recargar.
  'paymentStatuses',
  // Marketplaces propios de la agencia: quien crea uno suele ser quien está
  // cargando la póliza, asiento incluido. Si no pudiera guardarlo aquí, lo
  // escribiría y desaparecería al recargar — el mismo tropiezo de los estados
  // de pago (Lissette 2026-08-19).
  'marketplaces',
  // Origenes del lead propios de la agencia: mismo motivo que los marketplaces
  // -- quien carga las polizas suele ser un asiento (Lissette 2026-08-28).
  'leadSources',
  // Motivos de cancelacion propios de la agencia (Lissette 2026-09-19: "que
  // puedan escribir mas y que se vaya quedando en el drop down"). Mismo motivo
  // que los anteriores, y aqui es el mas claro de todos: quien cancela una
  // poliza un martes por la tarde es casi siempre un asiento.
  'cancelReasons',
  // Estatus migratorios propios de la agencia (Lissette 2026-09-16: *"poder
  // escribir estatus migratorio y que se vayan quedando guardados en el drop
  // down"*). Mismo motivo, una vez mas, y aqui todavia mas claro: quien teclea
  // el estatus migratorio de una familia entera es casi siempre un asiento. Si
  // no pudiera guardar el catalogo, escribiria "Parole humanitario", lo veria
  // puesto en la ficha, y al recargar habria desaparecido de la lista para
  // todos — el fallo exacto que esta lista existe para no repetir.
  'immigrationStatuses',
  // Categorias propias de Licencias y Certificados: mismo motivo otra vez
  // (Lissette 2026-09-02, queja 7). Un asiento sube sus propias licencias y
  // certificaciones; si no pudiera guardar la categoria aqui, la crearia y
  // desapareceria al recargar -- que es LITERALMENTE la queja que este cambio
  // viene a arreglar. Dejarla fuera de esta lista habria reproducido el fallo
  // para todos los asientos, y solo para ellos.
  'licenseTypes',
  // Columnas propias de las tablas de Renovaciones y de Prospecto y Referido
  // (Lissette 2026-09-09). Quien trabaja esas dos pantallas todos los dias es
  // justo quien sabe que columna le hace falta, y muchas veces es un asiento.
  // Fuera de esta lista, un asiento crearia la columna y desapareceria al
  // recargar — el mismo tropiezo de los estados de pago y las licencias.
  'tableCustomColumns',
  'taskStatuses', 'taskTypes', 'renewalStatuses',
  'onboardingStatuses',
  'ticketStatuses', 'ticketTypes',
  'followupStatuses', 'followupCustomColumns',
  'vaultCustomColumns', 'companyCustomColumns', 'paymentCustomColumns',
  // El orden de los campos de la ficha de la poliza, de toda la agencia
  // (Lissette 2026-10-03): lo acomoda quien usa la ficha, asiento incluido.
  'policyCardLayout',
  'customTabDefs',
  // Shared TEMPLATE catalogs a seat builds during outreach — suggested docs by
  // policy type, campaign email templates, and the holiday-greeting calendar.
  // Route to the seat-writable /seat-config so a seat's edits persist instead of
  // 403'ing on the agent-only PUT and vanishing on refresh (Lissette 2026-07-04:
  // "en TODO tipo de plantilla el seat puede ver y editar y que quede guardado").
  'docTemplatesMaster', 'emailTemplates', 'holidayCalendar',
  // El mensaje del KIT DE BIENVENIDA de Prospecto y Referido. Es una plantilla
  // mas, y se quedo fuera de esta lista cuando entraron las otras tres el
  // 2026-07-04 — asi que el asiento veia el boton, escribia el mensaje, y al
  // guardar le salia "Insufficient permissions for this action" (Victor Romero,
  // 2026-09-16). Quien manda el kit es justo quien trabaja los prospectos, y en
  // la mayoria de las agencias eso es un asiento.
  'welcomeKitTemplates',
  // El kit de RENOVACION, por el mismo motivo y en la misma pantalla. Se quedo
  // fuera a proposito unas horas antes, con el argumento de que "no tiene
  // pantalla de asiento" — y lo tenia: es el MISMO editor y el MISMO boton que
  // el de arriba, con un desplegable que elige si editas la plantilla por
  // defecto (`renewalKitTemplate`) o la de un tipo (`welcomeKitTemplates`).
  // Dejar una dentro y otra fuera hacia que el mismo boton guardara o fallara
  // segun lo que hubiera elegido en el desplegable — peor que si no funcionara
  // ninguna (Victor Romero, 2026-09-16, el mismo error en Renovaciones).
  'renewalKitTemplate',
  // Que campos pide el ENLACE DE RENOVACION. Lissette 2026-09-08: "me
  // gustaria que todos tengan el permiso de colocar los campos que se pediran
  // en el link, no solo el agente". El boton "Campos de revision" siempre se
  // vio para todo el mundo; lo que fallaba era el guardado, que iba al PUT de
  // agente y le devolvia 403 al asiento. La pantalla decia "No se pudo
  // guardar" sin decir por que, y el enlace seguia pidiendo los campos de
  // antes.
  'renewalReviewFields',
  // El texto de los dos SMS del enlace de renovacion, por el mismo motivo:
  // lo escribe quien manda las renovaciones, y en la mayoria de las agencias
  // eso es un asiento (Lissette 2026-09-08).
  'renewalLinkMessages',
]);
export async function updateAgencySettings(patch) {
  const p = (patch && typeof patch === 'object') ? patch : {};
  const seatPart = {};
  const agentPart = {};
  for (const [k, v] of Object.entries(p)) {
    if (SEAT_EDITABLE_SETTING_KEYS.has(k)) seatPart[k] = v; else agentPart[k] = v;
  }
  let data = null;
  // Seat-editable catalogs first, so a seat's customization always persists even
  // if the same patch also carried an admin-only field it isn't allowed to write.
  if (Object.keys(seatPart).length) {
    const r = await apiFetch('/api/v1/agency-settings/seat-config', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(seatPart),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `updateAgencySettings (seat-config) failed: ${r.status}`);
    }
    data = (await r.json()).data;
  }
  if (Object.keys(agentPart).length) {
    const r = await apiFetch('/api/v1/agency-settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(agentPart),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `updateAgencySettings failed: ${r.status}`);
    }
    data = (await r.json()).data;
  }
  return data;
}
// Shared "collaborating agents" — its own endpoint (no agent-or-above guard) so
// SEATS can edit the agency-wide list too (Lissette 2026-06-19 #4).
// Solo los cambios `{ poner, quitar }`, por id (E-396): mandar la lista entera
// borraba los colaboradores que otra persona había añadido mientras tanto.
export async function updateAgencyCollaborators(cambios) {
  const r = await apiFetch('/api/v1/agency-settings/collaborators', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ poner: cambios?.poner || [], quitar: cambios?.quitar || [] }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateAgencyCollaborators failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Shared client tag colors { tagName(lowercase): "#rrggbb" } — its own endpoint
// (no agent-or-above guard) so SEATS can color tags too. The tag NAME stays a
// plain string on the client; only the color lives here, looked up by name.
// Los cuatro catálogos de etiquetas (y sus colores) se guardan mandando SOLO
// los cambios: `{ anadir, quitar }` o `{ poner, quitar }` (ver
// crm-app/cambiosDeEtiquetas.js). Mandar la lista entera borraba lo que otras
// personas de la agencia habían creado mientras tanto (Pomares, 2026-10-29, E-396).
export async function updateAgencyTagColors(cambios) {
  const r = await apiFetch('/api/v1/agency-settings/tag-colors', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ poner: cambios?.poner || {}, quitar: cambios?.quitar || [] }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateAgencyTagColors failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// The agency's client-tag CATALOG (string[] of names) — its own endpoint (no
// agent-or-above guard) so SEATS can create tags too. This list, not the tags
// currently in use, is what the "reuse a tag" chips render: a tag stays after
// its last client loses it, and only the ✕ (with confirmation) drops it.
// Catálogo y colores de las ETIQUETAS DE PÓLIZA. Rutas propias, separadas de las
// de cliente: una etiqueta de póliza no aparece al etiquetar un cliente.
export async function updateAgencyPolicyTagLibrary(cambios) {
  const r = await apiFetch('/api/v1/agency-settings/policy-tag-library', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ anadir: cambios?.anadir || [], quitar: cambios?.quitar || [] }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateAgencyPolicyTagLibrary failed: ${r.status}`);
  return json.data;
}

export async function updateAgencyPolicyTagColors(cambios) {
  const r = await apiFetch('/api/v1/agency-settings/policy-tag-colors', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ poner: cambios?.poner || {}, quitar: cambios?.quitar || [] }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateAgencyPolicyTagColors failed: ${r.status}`);
  return json.data;
}

// Los plazos de aviso de la AGENCIA. Ruta propia porque el servidor solo deja
// escribirla al dueño (Lissette 2026-08-28: "solo el dueño"); leerla la lee
// cualquiera en getAgencySettings.
export async function updateAgencyAlertLead(alertLead, alertLeadByType) {
  const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
  const r = await apiFetch('/api/v1/agency-settings/alert-lead', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ alertLead: obj(alertLead), alertLeadByType: obj(alertLeadByType) }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateAgencyAlertLead failed: ${r.status}`);
  return json.data;
}

// El ORDEN DEL NOMBRE de la agencia. Ruta propia por lo mismo que los plazos:
// el servidor solo deja escribirla al dueño; leerla la lee cualquiera en
// getAgencySettings. Mandar cualquier cosa que no sea uno de los dos valores lo
// deja SIN FIJAR, que es lo que devuelve a cada persona su preferencia.
export async function updateAgencyNameOrder(nameOrder) {
  const r = await apiFetch('/api/v1/agency-settings/name-order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nameOrder: nameOrder || null }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateAgencyNameOrder failed: ${r.status}`);
  return json.data;
}

export async function updateAgencyTagLibrary(cambios) {
  const r = await apiFetch('/api/v1/agency-settings/tag-library', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ anadir: cambios?.anadir || [], quitar: cambios?.quitar || [] }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateAgencyTagLibrary failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Shared custom client-tab DEFINITIONS [{id,label,fields:[{id,label}]}] — its
// own endpoint (no agent-or-above guard) so SEATS with clients.edit can create
// custom tabs too. Values stay per-client; only the shared labels live here.
// (Was folded into the agent-or-above blob PUT → seats got a silent 403 and the
// tab vanished on refresh — Lissette 2026-07-01.)
export async function updateAgencyCustomTabDefs(customTabDefs) {
  const r = await apiFetch('/api/v1/agency-settings/custom-tab-defs', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customTabDefs: Array.isArray(customTabDefs) ? customTabDefs : [] }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateAgencyCustomTabDefs failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── HIPAA seat training + attestation (the agency's HIPAA seat bundle) ───────
// Config (videoUrl / agreementText / version / enabled) is read & written
// through getAgencySettings/updateAgencySettings under the `hipaaTraining` key.
export async function getHipaaStatus() {
  const r = await apiFetch('/api/v1/hipaa/status');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getHipaaStatus failed: ${r.status}`);
  return json.data;
}
export async function ackHipaa(signatureName, lang, cert, completedDate) {
  const r = await apiFetch('/api/v1/hipaa/ack', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signatureName, lang, certBlobUrl: cert?.blobUrl, certName: cert?.name, completedDate }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `ackHipaa failed: ${r.status}`);
  return json.data;
}
// The SEAT uploads their course-completion certificate (PDF/image) straight to
// Azure Blob via a short-lived SAS, then passes { blobUrl, name } to ackHipaa.
export async function uploadHipaaCert(file) {
  const sasRes = await apiFetch('/api/v1/hipaa/cert-sas', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  });
  const sasJson = await sasRes.json().catch(() => ({}));
  if (!sasRes.ok) throw new Error(sasJson.error || `cert-sas failed: ${sasRes.status}`);
  const sas = sasJson.data;
  const putRes = await fetch(sas.uploadUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);
  return { blobUrl: sas.blobUrl, name: file.name };
}
// Owner/agent: get a short-lived URL to view/download a seat's certificate.
export async function getHipaaCertUrl(ackId, { download = false } = {}) {
  const r = await apiFetch('/api/v1/hipaa/cert-read-sas', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ackId, download }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `cert-read-sas failed: ${r.status}`);
  return json.data.url;
}
export async function listHipaaAcks() {
  const r = await apiFetch('/api/v1/hipaa/acks');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listHipaaAcks failed: ${r.status}`);
  return json.data;
}
// Effective config for the agency (inherited platform default OR own override).
export async function getHipaaConfig() {
  const r = await apiFetch('/api/v1/hipaa/config');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getHipaaConfig failed: ${r.status}`);
  return json.data; // { config, overriding, platformDefault }
}
// Platform owner only: the generic default every agency inherits.
export async function getHipaaPlatformDefault() {
  const r = await apiFetch('/api/v1/hipaa/platform-default');
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getHipaaPlatformDefault failed: ${r.status}`);
  return json.data;
}
export async function saveHipaaPlatformDefault(payload) {
  const r = await apiFetch('/api/v1/hipaa/platform-default', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `saveHipaaPlatformDefault failed: ${r.status}`);
  return json.data;
}

// ── Import batches + Eligibility alerts (server-backed 2026-05-19) ───────
export async function listImportBatches() {
  const r = await apiFetch('/api/v1/import-batches');
  if (!r.ok) throw new Error(`listImportBatches failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createImportBatch(payload) {
  const r = await apiFetch('/api/v1/import-batches', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createImportBatch failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteImportBatch(id) {
  const r = await apiFetch(`/api/v1/import-batches/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteImportBatch failed: ${r.status}`);
  return true;
}
export async function listEligibilityAlerts() {
  const r = await apiFetch('/api/v1/eligibility-alerts');
  if (!r.ok) throw new Error(`listEligibilityAlerts failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createEligibilityAlert(payload) {
  const r = await apiFetch('/api/v1/eligibility-alerts', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createEligibilityAlert failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateEligibilityAlert(id, payload) {
  const r = await apiFetch(`/api/v1/eligibility-alerts/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateEligibilityAlert failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteEligibilityAlert(id) {
  const r = await apiFetch(`/api/v1/eligibility-alerts/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteEligibilityAlert failed: ${r.status}`);
  return true;
}

// ── Follow-ups / Seguimiento (gift cards + document submissions) ─────────
export async function listFollowups() {
  const r = await apiFetch('/api/v1/followups');
  if (!r.ok) throw new Error(`listFollowups failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createFollowup(payload) {
  const r = await apiFetch('/api/v1/followups', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createFollowup failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateFollowup(id, payload) {
  const r = await apiFetch(`/api/v1/followups/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateFollowup failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function deleteFollowup(id) {
  const r = await apiFetch(`/api/v1/followups/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteFollowup failed: ${r.status}`);
  return true;
}

// ── Appointments / "Citas" module (distinct from calendar events above) ───
export async function listCitas() {
  const r = await apiFetch('/api/v1/appointments');
  if (!r.ok) throw new Error(`listCitas failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createCita(payload) {
  const r = await apiFetch('/api/v1/appointments', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createCita failed: ${r.status}`);
  }
  // `correoAlCliente` dice si al cliente le llegó el correo con los datos de la
  // cita (enviado, sin_correo, sin_buzon...). Va aparte para que la pantalla lo
  // diga y no se quede guardado en la fila.
  const j = await r.json();
  return { ...j.data, correoAlCliente: j.correoAlCliente };
}
export async function updateCita(id, payload) {
  const r = await apiFetch(`/api/v1/appointments/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateCita failed: ${r.status}`);
  }
  const j = await r.json();
  return { ...j.data, correoAlCliente: j.correoAlCliente };
}
export async function deleteCita(id) {
  const r = await apiFetch(`/api/v1/appointments/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteCita failed: ${r.status}`);
  return true;
}

// ── Tickets (customer-service cases) ─────────────────────────────────────
export async function listTickets() {
  const r = await apiFetch('/api/v1/tickets');
  if (!r.ok) throw new Error(`listTickets failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createTicket(payload) {
  const r = await apiFetch('/api/v1/tickets', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createTicket failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateTicket(id, payload) {
  const r = await apiFetch(`/api/v1/tickets/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateTicket failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function addTicketMessage(id, payload) {
  const r = await apiFetch(`/api/v1/tickets/${id}/messages`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `addTicketMessage failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Corregir o retirar un mensaje del hilo de un ticket. El texto original se
// conserva en `originalBody`: un ticket puede acabar siendo la prueba de que se
// aviso a un cliente, asi que editar no puede significar reescribir la
// historia. Solo el autor (lo comprueba el servidor).
export async function editTicketMessage(id, msgId, body) {
  const r = await apiFetch(`/api/v1/tickets/${id}/messages/${msgId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `editTicketMessage failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function deleteTicketMessage(id, msgId) {
  const r = await apiFetch(`/api/v1/tickets/${id}/messages/${msgId}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteTicketMessage failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Papelera de tickets (2026-10-15): el borrado ya era suave, faltaba devolver.
export async function listDeletedTickets(days = 90) {
  const r = await apiFetch(`/api/v1/tickets/trash?days=${encodeURIComponent(days)}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listDeletedTickets failed: ${r.status}`);
  return json.data || [];
}

export async function restoreTicket(id) {
  const r = await apiFetch(`/api/v1/tickets/${encodeURIComponent(id)}/restore`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `restoreTicket failed: ${r.status}`);
  return json.data;
}

export async function deleteTicket(id) {
  const r = await apiFetch(`/api/v1/tickets/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteTicket failed: ${r.status}`);
  return true;
}
// Phase 3: send a client-facing email for a ticket (acknowledgement / resolution).
// payload: { subject, body, docs:[{documentId, mode:'link'|'attach'}], lang, acknowledgePhi }.
// Throws an Error carrying .code ('phi'|'no-email'|'suppressed'|'no-integration'…)
// and .findings so the caller can show a friendly message / PHI re-confirm.
export async function notifyTicketClient(id, payload) {
  const r = await apiFetch(`/api/v1/tickets/${id}/notify`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `notifyTicketClient failed: ${r.status}`);
    e.code = json.code; e.findings = json.findings;
    throw e;
  }
  return json;
}

// ── Doc templates + custom email templates (server-backed 2026-05-19) ────
function makeCrud(slug, name) {
  return {
    [`list${name}`]: async () => {
      const r = await apiFetch(`/api/v1/${slug}`);
      if (!r.ok) throw new Error(`list${name} failed: ${r.status}`);
      return (await r.json()).data || [];
    },
    [`create${name}`]: async (payload) => {
      const r = await apiFetch(`/api/v1/${slug}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `create${name} failed: ${r.status}`);
      }
      return (await r.json()).data;
    },
    [`update${name}`]: async (id, payload) => {
      const r = await apiFetch(`/api/v1/${slug}/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `update${name} failed: ${r.status}`);
      }
      return (await r.json()).data;
    },
    [`delete${name}`]: async (id) => {
      const r = await apiFetch(`/api/v1/${slug}/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`delete${name} failed: ${r.status}`);
      return true;
    },
  };
}
const docTpl = makeCrud('doc-templates', 'DocTpl');
export const listDocTpl = docTpl.listDocTpl;
export const createDocTpl = docTpl.createDocTpl;
export const updateDocTpl = docTpl.updateDocTpl;
export const deleteDocTpl = docTpl.deleteDocTpl;
/** Enlace de lectura (30 min) al PDF original de una plantilla, para pintar sus sitios de firma. */
export async function docTemplatePdfUrl(id) {
  const r = await apiFetch(`/api/v1/doc-templates/${encodeURIComponent(id)}/pdf`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `docTemplatePdfUrl failed: ${r.status}`);
  }
  return (await r.json()).data.url;
}
const emailTpl = makeCrud('custom-email-templates', 'EmailTpl');
export const listEmailTpl = emailTpl.listEmailTpl;
export const createEmailTpl = emailTpl.createEmailTpl;
export const updateEmailTpl = emailTpl.updateEmailTpl;
export const deleteEmailTpl = emailTpl.deleteEmailTpl;

export async function bulkReplaceCarrierAppointments(appointments, custom) {
  const r = await apiFetch('/api/v1/carrier-appointments/bulk', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ appointments, custom }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `bulkReplaceCarrierAppointments failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── My Profile (server-backed; was localStorage-only before 2026-05-19) ──
// El correo TAL CUAL lo recibe el cliente: cabecera de la agencia, un texto de
// ejemplo y la firma, armado en el servidor con el mismo código que cada envío
// de verdad (Lissette 2026-09-23: "lo importante es cómo queda en general la
// firma que ve el cliente"). `logoShape` es la forma recién tocada, que tarda
// 1,5 s en guardarse en las preferencias.
export async function vistaPreviaDelCorreo({ logoShape, lang } = {}) {
  const r = await apiFetch('/api/v1/emails/vista-previa', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logoShape, lang }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `vistaPreviaDelCorreo failed: ${r.status}`);
  }
  return (await r.json()).data; // { html, sinRemitente }
}

export async function updateMyProfile(payload) {
  const r = await apiFetch('/auth/me', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `updateMyProfile failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── Pasted conversations (F-N1, audit 2026-05-20 v2) ──
// WhatsApp / SMS pastes used to live in localStorage; now an encrypted
// row in the backend per (clientId, channel). 404-style empty payload
// when the row doesn't exist yet lets the SPA detect first-load and
// migrate any leftover localStorage data once.
export async function listPastedMessages(clientId, channel) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/pasted-messages?channel=${encodeURIComponent(channel)}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `listPastedMessages failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function appendPastedMessages(clientId, channel, messages) {
  const r = await apiFetch(`/api/v1/clients/${clientId}/pasted-messages`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel, messages }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `appendPastedMessages failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── Legal acceptance (L-N3, audit 2026-05-20 v2) ──
// /auth/legal-status returns the required documents and which ones the
// user still owes a fresh acceptance for. The SPA opens the re-acceptance
// modal whenever the response's requiresAcceptance list is non-empty.
export async function getLegalStatus() {
  const r = await apiFetch('/auth/legal-status');
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `getLegalStatus failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Persist the user's acceptance of one or more legal documents at their
// CURRENT canonical versions. Backend rejects mismatched versions, so the
// SPA must pass the version it just rendered.
export async function acceptLegal(acceptances) {
  const r = await apiFetch('/auth/accept-legal', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ acceptances }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `acceptLegal failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ── Email Sequences (Drip Campaigns) ────────────────────────────────────

export async function listSequences() {
  const r = await apiFetch('/api/v1/sequences');
  if (!r.ok) throw new Error(`listSequences failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function getSequence(id) {
  const r = await apiFetch(`/api/v1/sequences/${id}`);
  if (!r.ok) throw new Error(`getSequence failed: ${r.status}`);
  return (await r.json()).data;
}
export async function createSequence(payload) {
  const r = await apiFetch('/api/v1/sequences', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `createSequence failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function updateSequence(id, payload) {
  const r = await apiFetch(`/api/v1/sequences/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateSequence failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Borra la secuencia Y PARA sus envios pendientes. Devuelve `{ canceladas }`:
// cuantos correos que iban a salir se cancelaron, para poder decirlo en
// pantalla. "Borrada" y "borrada, y cancele 23 correos" no son la misma noticia
// (ver el ticket de Gabriel De Silva, 2026-09-15).
export async function deleteSequence(id) {
  const r = await apiFetch(`/api/v1/sequences/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteSequence failed: ${r.status}`);
  const json = await r.json().catch(() => ({}));
  return { canceladas: Number(json?.canceladas) || 0 };
}
export async function updateSequenceSteps(id, steps) {
  const r = await apiFetch(`/api/v1/sequences/${id}/steps`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ steps }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `updateSequenceSteps failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function enrollInSequence(sequenceId, payload) {
  const r = await apiFetch(`/api/v1/sequences/${sequenceId}/enroll`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `enrollInSequence failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function listSequenceEnrollments(sequenceId) {
  const r = await apiFetch(`/api/v1/sequences/${sequenceId}/enrollments`);
  if (!r.ok) throw new Error(`listSequenceEnrollments failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function pauseEnrollment(eid) {
  const r = await apiFetch(`/api/v1/sequences/enrollments/${eid}/pause`, { method: 'POST' });
  if (!r.ok) throw new Error(`pauseEnrollment failed: ${r.status}`);
  return (await r.json()).data;
}
export async function resumeEnrollment(eid) {
  const r = await apiFetch(`/api/v1/sequences/enrollments/${eid}/resume`, { method: 'POST' });
  if (!r.ok) throw new Error(`resumeEnrollment failed: ${r.status}`);
  return (await r.json()).data;
}
export async function stopEnrollment(eid) {
  const r = await apiFetch(`/api/v1/sequences/enrollments/${eid}/stop`, { method: 'POST' });
  if (!r.ok) throw new Error(`stopEnrollment failed: ${r.status}`);
  return (await r.json()).data;
}

// Client list reports — pre-built queries the agent can run from the
// Reports view. `params` is a plain object whose entries become query
// string args (`days=60`, `state=FL`, `type=AUTO`, ...).
// The backend translates `type` from the frontend lowercase id to the
// backend enum; we do that translation here for consistency.
export async function runClientReport(reportId, params = {}) {
  const qs = new URLSearchParams();
  qs.set('reportId', reportId);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    // Map the frontend lowercase policy-type id to the backend enum so
    // the report receives HEALTH instead of "aca", AUTO instead of "auto".
    if (k === 'type' && POLICY_TYPE_FRONT_TO_BACK[v]) {
      qs.set('type', POLICY_TYPE_FRONT_TO_BACK[v]);
    } else {
      qs.set(k, String(v));
    }
  }
  const r = await apiFetch(`/api/v1/reports/clients?${qs.toString()}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `runClientReport(${reportId}) failed: ${r.status}`);
  }
  const json = await r.json();
  return { rows: json.data || [], meta: json.meta || {} };
}

// Team performance reports — per-member roll-ups (production / hours / leads
// / tasks), optionally windowed by from/to (YYYY-MM-DD). Returns rows[].
export async function runTeamReport(metric, { from, to, policyTypes, policyStatuses, groupBy, dateField } = {}) {
  const qs = new URLSearchParams();
  qs.set('metric', metric);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  // Advanced "policies" metric filters.
  if (Array.isArray(policyTypes) && policyTypes.length) qs.set('policyTypes', policyTypes.join(','));
  if (Array.isArray(policyStatuses) && policyStatuses.length) qs.set('policyStatuses', policyStatuses.join(','));
  if (groupBy) qs.set('groupBy', groupBy);
  if (dateField) qs.set('dateField', dateField);
  const r = await apiFetch(`/api/v1/reports/team?${qs.toString()}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `runTeamReport(${metric}) failed: ${r.status}`);
  }
  const json = await r.json();
  return (json.data && json.data.rows) || [];
}

// Same call, but returns the whole payload instead of just the rows. The
// `funnel` metric ships an extra `leadsNoHistory` alongside its rows — leads
// with no recorded stage history, which the report has to show rather than
// quietly present a funnel that covers only part of the book.
export async function runTeamReportFull(metric, opts = {}) {
  const qs = new URLSearchParams();
  qs.set('metric', metric);
  for (const [k, v] of Object.entries(opts)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const r = await apiFetch(`/api/v1/reports/team?${qs.toString()}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `runTeamReport(${metric}) failed: ${r.status}`);
  }
  return (await r.json()).data || {};
}

// Saved team reports (Phase 2 — aggregation configs). The run reuses
// runTeamReport with the saved config's metric + date window.
export async function listTeamReports() {
  const r = await apiFetch('/api/v1/reports/team-custom');
  if (!r.ok) throw new Error(`listTeamReports failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createTeamReport(payload) {
  const r = await apiFetch('/api/v1/reports/team-custom', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `createTeamReport failed: ${r.status}`); }
  return (await r.json()).data;
}
export async function updateTeamReport(id, payload) {
  const r = await apiFetch(`/api/v1/reports/team-custom/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `updateTeamReport failed: ${r.status}`); }
  return (await r.json()).data;
}
export async function deleteTeamReport(id) {
  const r = await apiFetch(`/api/v1/reports/team-custom/${id}`, { method: 'DELETE' });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `deleteTeamReport failed: ${r.status}`); }
  return true;
}

// ── Custom reports ("Mis reportes" tab) ─────────────────────────────────
// Saved filter-builder reports. Each one has a name + filters JSON the
// backend evaluates against the Client table. Visibility scope is the
// same as GET /clients (agent/seat row-level visibility).

export async function listCustomReports() {
  const r = await apiFetch('/api/v1/reports/custom');
  if (!r.ok) throw new Error(`listCustomReports failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function createCustomReport({ name, description, filters, columns, sharedWithAgency, editorIds }) {
  // Build the payload explicitly so a missing field becomes "use the
  // backend default" rather than "set this to undefined and crash Zod".
  const payload = { name, description, filters };
  if (Array.isArray(columns)) payload.columns = columns;
  if (typeof sharedWithAgency === 'boolean') payload.sharedWithAgency = sharedWithAgency;
  // Quien puede editarlo ademas del dueno. Sin compartir no significa nada, pero
  // se manda igual: el servidor la limpia y guardarla deja la eleccion hecha
  // para cuando se comparta.
  if (Array.isArray(editorIds)) payload.editorIds = editorIds;
  const r = await apiFetch('/api/v1/reports/custom', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createCustomReport failed: ${r.status}`);
  return json.data;
}

export async function updateCustomReport(id, patch) {
  // `patch` may include any of: name, description, filters, columns,
  // sharedWithAgency, editorIds. The backend only updates the keys present so
  // callers can flip a single boolean without resending the full report.
  const r = await apiFetch(`/api/v1/reports/custom/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateCustomReport failed: ${r.status}`);
  return json.data;
}

export async function deleteCustomReport(id) {
  const r = await apiFetch(`/api/v1/reports/custom/${id}`, { method: 'DELETE' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `deleteCustomReport failed: ${r.status}`);
  }
}

// Execute a saved custom report. Same response shape as runClientReport
// so the existing results-table UI can render both.
export async function runCustomReport(id) {
  const r = await apiFetch(`/api/v1/reports/custom/${id}/run`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `runCustomReport failed: ${r.status}`);
  return { rows: json.data || [], meta: json.meta || {} };
}

// ── Time tracking ────────────────────────────────────────────────────
// Until 2026-05-17 the timelog lived only in the seat's browser
// (useLocalStorage "dkt6_timelog"), so the owner never saw a seat's
// hours. These hit the backend TimeEntry table. Wire shape:
//   { id, userId, date:"YYYY-MM-DD", clockIn:"HH:MM",
//     clockOut:"HH:MM"|null, hours:number|null, notes }
export async function listTimeEntries() {
  const r = await apiFetch('/api/v1/time-entries');
  if (!r.ok) throw new Error(`listTimeEntries failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function createTimeEntry(entry) {
  const r = await apiFetch('/api/v1/time-entries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      date: entry.date,
      clockIn: entry.clockIn,
      clockOut: entry.clockOut ?? null,
      hours: entry.hours ?? null,
      notes: entry.notes ?? '',
    }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createTimeEntry failed: ${r.status}`);
  return json.data;
}

export async function updateTimeEntry(id, patch) {
  const r = await apiFetch(`/api/v1/time-entries/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `updateTimeEntry failed: ${r.status}`);
  return json.data;
}

// "Sigo aquí" para la sesión abierta. Devuelve { data, closed }: closed=true
// cuando el servidor ya la dio por abandonada (sin latido) y hay que abrir otra.
export async function heartbeatTimeEntry(id) {
  const r = await apiFetch(`/api/v1/time-entries/${id}/heartbeat`, { method: 'POST' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `heartbeatTimeEntry failed: ${r.status}`);
  return { data: json.data, closed: !!json.closed };
}

export async function deleteTimeEntry(id) {
  const r = await apiFetch(`/api/v1/time-entries/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteTimeEntry failed: ${r.status}`);
  return true;
}

// L-N4 phase 2: EmailSuppression — agency-scoped opt-out list.
// Visible to all members; mutating endpoints require AGENCY_OWNER or SOLO.

export async function listEmailSuppressions({ search = '', limit = 100, offset = 0 } = {}) {
  const qs = new URLSearchParams();
  if (search) qs.set('q', search);
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  const r = await apiFetch(`/api/v1/email-suppressions?${qs.toString()}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listEmailSuppressions failed: ${r.status}`);
  return { data: json.data || [], total: json.total || 0 };
}

export async function addEmailSuppression({ email, reason }) {
  const r = await apiFetch(`/api/v1/email-suppressions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, reason: reason || null }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `addEmailSuppression failed: ${r.status}`);
  return json.data;
}

export async function removeEmailSuppression(id) {
  const r = await apiFetch(`/api/v1/email-suppressions/${id}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `removeEmailSuppression failed: ${r.status}`);
  return true;
}

// SMS do-not-contact list — agency-scoped mirror of email suppressions.
// Visible to all members; mutating endpoints require AGENCY_OWNER or SOLO.

export async function listSmsSuppressions({ search = '', limit = 100, offset = 0 } = {}) {
  const qs = new URLSearchParams();
  if (search) qs.set('q', search);
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  const r = await apiFetch(`/api/v1/sms-suppressions?${qs.toString()}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listSmsSuppressions failed: ${r.status}`);
  return { data: json.data || [], total: json.total || 0 };
}

export async function addSmsSuppression({ phone, reason }) {
  const r = await apiFetch(`/api/v1/sms-suppressions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, reason: reason || null }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `addSmsSuppression failed: ${r.status}`);
  return json.data;
}

export async function removeSmsSuppression(id) {
  const r = await apiFetch(`/api/v1/sms-suppressions/${id}`, { method: 'DELETE' });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `removeSmsSuppression failed: ${r.status}`);
  return true;
}

// ─── Agency logo (used on quotes / invoices / signed PDFs + print) ───
// Two-step upload (same pattern as documents): get a SAS URL, PUT the
// bytes straight to Azure, then POST the resulting blobUrl back so the
// server persists it on Agency.logoUrl. Helper handles both steps.
export async function uploadAgencyLogo(file) {
  if (!file) throw new Error('No file provided');
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('Only PNG, JPEG, or WebP images are accepted.');
  }
  if (file.size > 2 * 1024 * 1024) {
    throw new Error('Logo file is larger than 2 MB.');
  }
  const sasRes = await apiFetch(`/api/v1/agencies/me/logo-upload-sas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, contentType: file.type }),
  });
  const sasJson = await sasRes.json().catch(() => ({}));
  if (!sasRes.ok) throw new Error(sasJson.error || `uploadAgencyLogo SAS failed: ${sasRes.status}`);
  const { uploadUrl, blobUrl } = sasJson.data || {};
  if (!uploadUrl || !blobUrl) throw new Error('Bad SAS response');
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload to storage failed: ${put.status}`);
  const persistRes = await apiFetch(`/api/v1/agencies/me/logo`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blobUrl }),
  });
  const persistJson = await persistRes.json().catch(() => ({}));
  if (!persistRes.ok) throw new Error(persistJson.error || `uploadAgencyLogo persist failed: ${persistRes.status}`);
  return persistJson.data; // { id, name, logoUrl }
}

// Upload an inline image for an email template (campaign / birthday). Two-step
// SAS upload to the branding container; returns a displayable URL to drop into
// the HTML body. The send path re-stamps the read SAS so it never breaks.
export async function uploadEmailImage(file) {
  if (!file) throw new Error('No file provided');
  if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
    throw new Error('Only PNG, JPEG, WebP or GIF images are accepted.');
  }
  if (file.size > 2 * 1024 * 1024) throw new Error('Image is larger than 2 MB.');
  const sasRes = await apiFetch('/api/v1/agencies/me/email-image-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, contentType: file.type }),
  });
  const sasJson = await sasRes.json().catch(() => ({}));
  if (!sasRes.ok) throw new Error(sasJson.error || `email image SAS failed: ${sasRes.status}`);
  const { uploadUrl, displayUrl } = sasJson.data || {};
  if (!uploadUrl || !displayUrl) throw new Error('Bad SAS response');
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type }, body: file });
  if (!put.ok) throw new Error(`Upload to storage failed: ${put.status}`);
  return displayUrl;
}

// Sube un PDF para ENLAZARLO desde un correo masivo (Lissette 2026-09-01,
// punto 6). Mismo patron de dos pasos que `uploadEmailImage`: se pide un SAS de
// escritura, se sube el fichero directo a Azure, y vuelve una URL PUBLICA y
// PERMANENTE.
//
// Permanente y no firmada A PROPOSITO: la llave de delegacion de Azure dura
// ~1 hora, asi que un enlace firmado moriria dentro de la hora y quien abriera
// el correo por la tarde se encontraria un enlace roto. Es la misma leccion
// que ya se aprendio con las imagenes de correo.
export async function uploadEmailPdf(file) {
  if (!file) throw new Error('No file provided');
  if (file.type !== 'application/pdf') throw new Error('Solo se aceptan archivos PDF.');
  const sasRes = await apiFetch('/api/v1/agencies/me/email-doc-sas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, contentType: file.type }),
  });
  const sasJson = await sasRes.json().catch(() => ({}));
  if (!sasRes.ok) throw new Error(sasJson.error || `email doc SAS failed: ${sasRes.status}`);
  const { uploadUrl, displayUrl, maxBytes } = sasJson.data || {};
  if (!uploadUrl || !displayUrl) throw new Error('Bad SAS response');
  // El tope lo pone el SERVIDOR y se comprueba aqui para no subir 8 MB y que
  // los rechacen al final.
  if (maxBytes && file.size > maxBytes) {
    throw new Error(`El PDF pesa mas de ${(maxBytes / (1024 * 1024)).toFixed(0)} MB.`);
  }
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type }, body: file });
  if (!put.ok) throw new Error(`Upload to storage failed: ${put.status}`);
  return { url: displayUrl, name: file.name };
}

// Re-stamp inline storage images in an email body with a fresh read SAS — the
// SAME thing the send path does — so a template PREVIEW shows the image as it
// will arrive (the display SAS saved in the template expires). Returns the
// signed HTML; on any failure returns the input unchanged so text still shows.
export async function signEmailImages(html) {
  if (!html || !/blob\.core\.windows\.net/.test(html)) return html || '';
  try {
    const r = await apiFetch('/api/v1/agencies/me/sign-email-images', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) return html;
    return j?.data?.html || html;
  } catch {
    return html;
  }
}

export async function deleteAgencyLogo() {
  const r = await apiFetch(`/api/v1/agencies/me/logo`, { method: 'DELETE' });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `deleteAgencyLogo failed: ${r.status}`);
  }
  return true;
}

// The agency's timezone — what "cada lunes a las 8" means for the scheduled
// dashboard email. Returns { timezone, options }; the options list is closed on
// the backend because a mistyped zone would send the report at an unpredictable
// hour and nobody would notice until the mail arrived late.
// Cupo anual de firmas electrónicas y solicitudes de documentos, y cuánto va
// consumido este año. Devuelve
//   { year, signatures:{limit,used,remaining}, docRequests:{limit,used,remaining} }
// El backend solo informa: no bloquea al llegar al tope (esa es una decisión de
// negocio, no de este endpoint).
export async function getAgencyUsage() {
  const r = await apiFetch('/api/v1/agencies/me/usage');
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `getAgencyUsage failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function getAgencyTimezone() {
  const r = await apiFetch('/api/v1/agencies/me/timezone');
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `getAgencyTimezone failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function updateAgencyTimezone(timezone) {
  const r = await apiFetch('/api/v1/agencies/me/timezone', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ timezone }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `updateAgencyTimezone failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// Rename the current tenant's agency. Owner-only on the backend
// (agency_owner / solo_agent). Returns { id, name }. The caller should
// refresh /auth/me (or reload) so currentUser.agency.name updates
// everywhere it's rendered.
export async function updateAgencyName(name) {
  const r = await apiFetch(`/api/v1/agencies/me/name`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `updateAgencyName failed: ${r.status}`);
  }
  return (await r.json()).data;
}

// ─── Scheduled sends (campaigns + birthday/holiday greetings) ───
// Backend worker polls every minute. V1 supports email only.
// UN envio de la cola, con su asunto y su cuerpo (Lissette 2026-09-02: "no me
// deja ver lo que escribi en ese correo"). La LISTA de agencia no trae el
// cuerpo a proposito — decision suya del 2026-07-16: ahi se ve que hay un envio
// encolado, no lo que dice, porque puede ser de un companero. Esto es lo otro:
// leer lo tuyo propio. El servidor aplica la misma regla que para cancelarlo
// (el autor, o un dueno) y contesta 403 con code NOT_YOURS si no te toca.
export async function getScheduledSend(id) {
  const r = await apiFetch(`/api/v1/scheduled-sends/${encodeURIComponent(id)}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(json.error || `getScheduledSend failed: ${r.status}`);
    e.code = json.code;
    throw e;
  }
  return json.data || null;
}

export async function listScheduledSends({ status, source, scope, fromDate, toDate } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status); // one value, or comma-separated
  if (source) params.set('source', source);
  if (scope) params.set('scope', scope); // 'agency' → light agency-wide rows (markers/dedup)
  if (fromDate) params.set('fromDate', fromDate); // YYYY-MM-DD, agency-local, inclusive
  if (toDate) params.set('toDate', toDate);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const r = await apiFetch(`/api/v1/scheduled-sends${qs}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listScheduledSends failed: ${r.status}`);
  return json.data || [];
}

// Saludos enviados EN EL MOMENTO — los que nunca pasaron por la cola. Vienen
// con la misma forma que una fila de la cola (`runAt`, `status:'sent'`) y con
// `immediate:true`, para que el control de repetidos pueda contar las dos
// mitades con la misma llave. Sin esto el informe se dejaba fuera justo los
// envíos manuales, que son los del ticket original.
export async function listSentGreetings({ source, fromDate, toDate } = {}) {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (fromDate) params.set('fromDate', fromDate); // YYYY-MM-DD, agency-local, inclusive
  if (toDate) params.set('toDate', toDate);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const r = await apiFetch(`/api/v1/scheduled-sends/sent-greetings${qs}`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `listSentGreetings failed: ${r.status}`);
  return json.data || [];
}

export async function createScheduledSend(payload) {
  // payload: { clientId?, channel, recipient, subject?, body, runAt, source, templateId?, plantillaViva? }
  const r = await apiFetch(`/api/v1/scheduled-sends`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createScheduledSend failed: ${r.status}`);
  // The server refuses a SECOND greeting for the same recipient on the same
  // day and answers with the row that was already queued. That is a success —
  // the greeting is scheduled — so it must not throw; `_duplicate` lets the
  // caller say "ya estaba programado" instead of claiming it just queued it.
  return json.duplicate ? { ...json.data, _duplicate: true } : json.data;
}

// Sendable public link for ONE resource (paste into WhatsApp / SMS / email).
// The token is an HMAC minted server-side, so it cannot be built client-side.
// Returns { token, path, title } — prefix `path` with the site origin.
export async function getResourceShareLink(resourceId) {
  const r = await apiFetch(`/api/v1/resources/${encodeURIComponent(resourceId)}/share-link`);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `getResourceShareLink failed: ${r.status}`);
  return json.data;
}

// A read URL for a resource's file, signed seconds ago instead of whenever the
// list happened to load (E-085). Call it at the moment of opening.
//
// Only our own blob URLs need this. Base64 (`fileData`, legacy records) and
// external links (YouTube/Vimeo/Drive) are returned as-is without a round trip.
// If the call fails we hand back the URL we already had: a link that might
// still work beats a dead button.
export function isDeskteBlobUrl(url) {
  return /^https?:\/\/[^/]*\.blob\.core\.windows\.net\//i.test(String(url || ''));
}

// Re-sign stored file URLs the app has been holding since the list loaded
// (E-085). Works for ANY module's blob URL — the server checks the blob really
// belongs to the caller's agency. Returns a { original: fresh } map; anything
// it couldn't sign is simply absent, so callers keep what they had.
export async function freshBlobUrls(urls) {
  const wanted = [...new Set((urls || []).filter(isDeskteBlobUrl))];
  if (!wanted.length) return {};
  const out = {};
  // The endpoint caps a batch at 50; a busy chat screen can hold more.
  for (let i = 0; i < wanted.length; i += 50) {
    try {
      const r = await apiFetch('/api/v1/blob-url/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls: wanted.slice(i, i + 50) }),
      });
      const json = await r.json().catch(() => ({}));
      if (r.ok && json?.data) Object.assign(out, json.data);
    } catch { /* keep whatever we already resolved */ }
  }
  return out;
}

export async function freshBlobUrl(url) {
  if (!isDeskteBlobUrl(url)) return url;
  return (await freshBlobUrls([url]))[url] || url;
}

export async function freshResourceFileUrl(res) {
  const current = res?.fileUrl || res?.fileData || '';
  if (!res?.id || !isDeskteBlobUrl(res.fileUrl)) return current;
  try {
    const r = await apiFetch(`/api/v1/resources/${encodeURIComponent(res.id)}/file-url`);
    const json = await r.json().catch(() => ({}));
    if (!r.ok || !json?.data?.url) return current;
    return json.data.url;
  } catch {
    return current;
  }
}

// Queue MANY scheduled sends in one request. Replaces awaiting createScheduledSend
// in a loop, which for a large campaign meant hundreds of sequential round trips
// and started tripping the per-actor rate limiter (429) partway through, dropping
// the rest of the list. Returns { count }.
export async function createScheduledSendsBulk(items) {
  const r = await apiFetch(`/api/v1/scheduled-sends/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `createScheduledSendsBulk failed: ${r.status}`);
  return json.data;
}

// Cancelar. `scope: 'campaign'` para toda la campana y no fila por fila: una
// campana programada son una fila POR DESTINATARIO, asi que pararla de una en
// una serian cientos de clics (Lissette, punto 75: "desprogramarla").
// Devuelve cuantas se cancelaron.
export async function cancelScheduledSend(id, { scope } = {}) {
  const qs = scope === 'campaign' ? '?scope=campaign' : '';
  const r = await apiFetch(`/api/v1/scheduled-sends/${encodeURIComponent(id)}${qs}`, { method: 'DELETE' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(j.error || `cancelScheduledSend failed: ${r.status}`);
    e.code = j.code;
    throw e;
  }
  return j.data?.cancelados ?? 1;
}

// Cambiar un envio que TODAVIA no ha salido (Lissette, punto 75).
//
// La hora de una fila de campana mueve la campana ENTERA — las 340 copias
// tienen que salir a la misma hora. El texto solo se puede cambiar en un envio
// suelto: el de una campana se guarda ya personalizado por persona, asi que
// escribir el mismo cuerpo en todas les mandaria a todas el nombre de la
// misma. El servidor lo rechaza con code CAMPAIGN_BODY_IS_PERSONALIZED y la
// pantalla lo dice con esas palabras.
export async function updateScheduledSend(id, patch) {
  const r = await apiFetch(`/api/v1/scheduled-sends/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(j.error || `updateScheduledSend failed: ${r.status}`);
    e.code = j.code;
    throw e;
  }
  return j.data || { movidos: 1 };
}

// ── Internal Team Chat (Phase 1) ─────────────────────────────────────────────
// Agency-scoped chat. Message bodies are encrypted at rest on the backend;
// the API only ever returns/accepts the plaintext `body`.

export async function listChatMessages({ channel = 'general', limit = 50, before } = {}) {
  const qs = new URLSearchParams({ channel, limit: String(limit) });
  if (before) qs.set('before', before);
  const r = await apiFetch(`/api/v1/chat/messages?${qs.toString()}`);
  if (!r.ok) throw new Error(`listChatMessages failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

// attachment (optional): { dataUrl, name } — a base64 data URL of an image.
export async function sendChatMessage({ channel = 'general', body = '', attachment } = {}) {
  const r = await apiFetch('/api/v1/chat/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel, body, ...(attachment ? { attachment } : {}) }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `sendChatMessage failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

export async function editChatMessage(id, body) {
  const r = await apiFetch(`/api/v1/chat/messages/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `editChatMessage failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

export async function deleteChatMessage(id) {
  const r = await apiFetch(`/api/v1/chat/messages/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `deleteChatMessage failed: ${r.status}`);
  }
  return true;
}

// Fire-and-forget "is typing…" ping (throttled by the caller).
export function sendChatTyping(channel = 'general') {
  apiFetch('/api/v1/chat/typing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel }),
  }).catch(() => {});
}

export async function markChatRead({ channel = 'general' } = {}) {
  const r = await apiFetch('/api/v1/chat/read', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel }),
  });
  if (!r.ok) throw new Error(`markChatRead failed: ${r.status}`);
  const json = await r.json().catch(() => ({}));
  return json.data;
}

// Read receipts — other participants' lastReadAt for a channel, to render
// ✓✓ Leído on own messages. Returns [{ userId, lastReadAt }].
export async function listChatReadStates(channel = 'general') {
  const r = await apiFetch(`/api/v1/chat/read-states?channel=${encodeURIComponent(channel)}`);
  if (!r.ok) throw new Error(`listChatReadStates failed: ${r.status}`);
  const json = await r.json().catch(() => ({}));
  return json.data || [];
}

export async function getChatUnread() {
  const r = await apiFetch('/api/v1/chat/unread');
  if (!r.ok) throw new Error(`getChatUnread failed: ${r.status}`);
  const json = await r.json();
  return json.data || { counts: {}, total: 0 };
}

// Toggle an emoji reaction on a message; returns the message's aggregated
// reaction list [{emoji, count, userIds, names}].
export async function toggleChatReaction(id, emoji) {
  const r = await apiFetch(`/api/v1/chat/messages/${encodeURIComponent(id)}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `toggleChatReaction failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Thread: parent message + all replies (oldest first).
export async function getChatThread(id) {
  const r = await apiFetch(`/api/v1/chat/messages/${encodeURIComponent(id)}/thread`);
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `getChatThread failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Search messages (server decrypts + matches; accents/case-insensitive).
export async function searchChatMessages({ q, channel, limit = 30 } = {}) {
  const qs = new URLSearchParams({ q: q || '', limit: String(limit) });
  if (channel) qs.set('channel', channel);
  const r = await apiFetch(`/api/v1/chat/search?${qs.toString()}`);
  if (!r.ok) throw new Error(`searchChatMessages failed: ${r.status}`);
  const json = await r.json();
  return { results: json.data || [], meta: json.meta || {} };
}

// Presence snapshot: teammates with a live connection (online dots).
export async function getChatPresence() {
  const r = await apiFetch('/api/v1/chat/presence');
  if (!r.ok) throw new Error(`getChatPresence failed: ${r.status}`);
  const json = await r.json();
  return json.data || { online: [] };
}

// ── Web Push (chat notifications with Deskte closed) ────────────────────────

export async function getPushPublicKey() {
  const r = await apiFetch('/api/v1/push/public-key');
  if (!r.ok) throw new Error(`getPushPublicKey failed: ${r.status}`);
  const json = await r.json();
  return json.data || { enabled: false, key: null };
}

export async function subscribePush(subscription) {
  const r = await apiFetch('/api/v1/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subscription),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `subscribePush failed: ${r.status}`);
  }
  return true;
}

export async function unsubscribePush(endpoint) {
  const r = await apiFetch('/api/v1/push/unsubscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
  if (!r.ok) throw new Error(`unsubscribePush failed: ${r.status}`);
  return true;
}

// ── Chat: automatic event feed rules (Deskte bot → channel) ─────────────────

export async function getChatEventRules() {
  const r = await apiFetch('/api/v1/chat/event-rules');
  if (!r.ok) throw new Error(`getChatEventRules failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function saveChatEventRules(rules) {
  const r = await apiFetch('/api/v1/chat/event-rules', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rules }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `saveChatEventRules failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data || [];
}

// ── Platform Support Tickets ──────────────────────────────────────────────────
// An agency raises a ticket to the Deskte founder; both sides reply in a
// thread. Message bodies are encrypted at rest on the backend. The same
// endpoints serve both sides — the backend gates by role (platform owner
// sees all tickets; an agency sees only its own).

export async function listSupportTickets({ status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const r = await apiFetch(`/api/v1/support/tickets${qs}`);
  if (!r.ok) throw new Error(`listSupportTickets failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function createSupportTicket({ subject, category = 'question', body, attachments }) {
  const r = await apiFetch('/api/v1/support/tickets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, category, body, attachments }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `createSupportTicket failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Ask the first-line support AI (grounded on the Deskte manual). Returns
// { reply, needsHuman }. needsHuman=true means escalate to a ticket.
export async function askSupportAI({ message, history = [] }) {
  const r = await apiFetch('/api/v1/support/ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, history }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `askSupportAI failed: ${r.status}`);
  }
  return await r.json(); // { reply, needsHuman }
}

export async function getSupportTicket(id) {
  const r = await apiFetch(`/api/v1/support/tickets/${id}`);
  if (!r.ok) throw new Error(`getSupportTicket failed: ${r.status}`);
  const json = await r.json();
  return json.data || { ticket: null, messages: [] };
}

export async function sendSupportMessage(id, body, attachments) {
  const r = await apiFetch(`/api/v1/support/tickets/${id}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body, attachments }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `sendSupportMessage failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Platform owner only: change a ticket's status / priority.
export async function updateSupportTicket(id, patch) {
  const r = await apiFetch(`/api/v1/support/tickets/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `updateSupportTicket failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

export async function getSupportUnread() {
  const r = await apiFetch('/api/v1/support/unread');
  if (!r.ok) throw new Error(`getSupportUnread failed: ${r.status}`);
  const json = await r.json();
  return json.data || { total: 0 };
}

// ── Feature ideas board (public, cross-agency, votable) ───────────────────────

export async function listIdeas({ status, sort = 'top' } = {}) {
  const qs = new URLSearchParams({ sort });
  if (status) qs.set('status', status);
  const r = await apiFetch(`/api/v1/ideas?${qs.toString()}`);
  if (!r.ok) throw new Error(`listIdeas failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function createIdea({ title, details, category = 'general' }) {
  const r = await apiFetch('/api/v1/ideas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, details, category }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `createIdea failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Toggle the current user's vote. Returns { id, hasVoted, voteCount }.
export async function voteIdea(id) {
  const r = await apiFetch(`/api/v1/ideas/${id}/vote`, { method: 'POST' });
  if (!r.ok) throw new Error(`voteIdea failed: ${r.status}`);
  const json = await r.json();
  return json.data;
}

// Platform owner only: set status + official response.
export async function updateIdea(id, patch) {
  const r = await apiFetch(`/api/v1/ideas/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `updateIdea failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// ── Team Chat: channels & DMs (Phase 2 + 3) ──────────────────────────────────

export async function listChatChannels() {
  const r = await apiFetch('/api/v1/chat/channels');
  if (!r.ok) throw new Error(`listChatChannels failed: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

export async function createChatChannel({ name, description }) {
  const r = await apiFetch('/api/v1/chat/channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `createChatChannel failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

export async function archiveChatChannel(id) {
  const r = await apiFetch(`/api/v1/chat/channels/${encodeURIComponent(id)}/archive`, { method: 'POST' });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `archiveChatChannel failed: ${r.status}`);
  }
  return true;
}

// Resolve (or lazily open) the canonical 1-to-1 DM channel with another
// agency member. Returns { key, type:'dm', name, otherUserId }.
export async function resolveDmChannel(userId) {
  const r = await apiFetch(`/api/v1/chat/dm/${encodeURIComponent(userId)}`);
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `resolveDmChannel failed: ${r.status}`);
  }
  const json = await r.json();
  return json.data;
}

// Public booking management (reschedule / cancel) via the email token.
export async function getManageBooking(token) {
  const r = await fetch(`${API_BASE}/booking/manage/${encodeURIComponent(token)}`);
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `getManageBooking failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function rescheduleBooking(token, startUtc) {
  const r = await fetch(`${API_BASE}/booking/manage/${encodeURIComponent(token)}/reschedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startUtc }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `rescheduleBooking failed: ${r.status}`);
  return j.data;
}
export async function cancelBooking(token) {
  const r = await fetch(`${API_BASE}/booking/manage/${encodeURIComponent(token)}/cancel`, { method: 'POST' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `cancelBooking failed: ${r.status}`);
  return j.data;
}

// ─── Monday.com → Deskte migration ──────────────────────────────────────────

export async function mondayListBoards(mondayToken) {
  const r = await apiFetch('/api/v1/monday-import/boards', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: mondayToken }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'Error al conectar con Monday');
  return j.data; // [{ id, name, description, itemsCount }]
}

export async function mondayBoardStructure(mondayToken, boardId) {
  const r = await apiFetch('/api/v1/monday-import/structure', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: mondayToken, boardId }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'Error al leer el tablero');
  return j.data; // { columns: [{id, title, type}], subitemColumns: [{id, title, type}] }
}

export async function mondayFetchItems(mondayToken, boardId, cursor = null) {
  const r = await apiFetch('/api/v1/monday-import/items', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: mondayToken, boardId, ...(cursor != null ? { cursor } : {}) }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'Error al leer los ítems');
  return j.data; // { items, cursor, hasMore }
}

// ── Campañas Custom recurrentes (Lissette 2026-08-13) ──────────────────────
// El bloque RECURRENCIA de "Marketing > Recurrentes", dentro del modal de
// envío masivo de Campañas. SOLO plantillas Custom: cumpleaños y holidays
// tienen su propio camino y no pasan por aquí.
export async function listRecurringCampaigns() {
  const r = await apiFetch('/api/v1/recurring-campaigns');
  if (!r.ok) throw new Error(`listRecurringCampaigns failed: ${r.status}`);
  return (await r.json()).data || [];
}
export async function createRecurringCampaign(payload) {
  const r = await apiFetch('/api/v1/recurring-campaigns', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `createRecurringCampaign failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function pauseRecurringCampaign(id) {
  const r = await apiFetch(`/api/v1/recurring-campaigns/${id}/pause`, { method: 'POST' });
  if (!r.ok) throw new Error(`pauseRecurringCampaign failed: ${r.status}`);
  return (await r.json()).data;
}
export async function resumeRecurringCampaign(id) {
  const r = await apiFetch(`/api/v1/recurring-campaigns/${id}/resume`, { method: 'POST' });
  if (!r.ok) throw new Error(`resumeRecurringCampaign failed: ${r.status}`);
  return (await r.json()).data;
}
export async function deleteRecurringCampaign(id) {
  const r = await apiFetch(`/api/v1/recurring-campaigns/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`deleteRecurringCampaign failed: ${r.status}`);
  return true;
}

/**
 * Manda el resultado de un informe al modulo de Hojas.
 *
 * Lissette 2026-09-01: "poder tener la opcion de mover ese informe al modulo
 * de sheets sin tener que descargar y volver a subirlo alli".
 *
 * `filas` son las mismas que la agente tiene delante — el objeto que ya arma
 * la exportacion a Excel, con las etiquetas de columna como claves. La hoja
 * NACE PRIVADA, y eso lo decide el servidor: no hay parametro para pedir lo
 * contrario.
 */
export async function crearHojaDesdeInforme({ nombre, nombreDelInforme, filas }) {
  const lista = Array.isArray(filas) ? filas : [];
  // Las columnas salen de TODAS las filas, no de la primera: una fila puede
  // traer una clave que otra no, y quedarse con la primera perderia columnas
  // enteras sin avisar.
  const etiquetas = [...new Set(lista.flatMap((f) => Object.keys(f || {})))];
  // El id tiene que ser estable y no chocar. Si se usara la etiqueta como id y
  // dos columnas se llamaran igual, sus celdas se pisarian y una saldria vacia.
  const columnas = etiquetas.map((etiqueta, i) => ({ id: `c${i}`, name: etiqueta, type: 'text' }));
  const porEtiqueta = new Map(columnas.map((c) => [c.name, c.id]));
  const cuerpo = {
    name: nombre,
    reportName: nombreDelInforme || undefined,
    columns: columnas,
    rows: lista.map((f) => {
      const cells = {};
      for (const [etiqueta, valor] of Object.entries(f || {})) {
        const id = porEtiqueta.get(etiqueta);
        // Todo va como TEXTO: si la hoja reinterpretara los valores, un número
        // de póliza perdería los ceros de la izquierda y una fecha se correría
        // un día. Lo que se ve en el informe es lo que se guarda.
        if (id) cells[id] = valor == null ? '' : String(valor);
      }
      return { cells };
    }),
  };
  const r = await apiFetch('/api/v1/sheets/from-report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `No se pudo crear la hoja (${r.status})`);
  }
  return (await r.json()).data;
}

// ── La bandeja de Conversaciones ─────────────────────────────────────────────
//
// El servidor ya devuelve todo en la forma que la pantalla pinta (nombre del
// cliente resuelto, adelanto descifrado, fecha), asi que aqui no hay `map*`:
// solo se pasan los filtros y se devuelve lo que llega.
//
// La lista se pagina EN EL SERVIDOR a proposito. Es la tabla que mas va a
// crecer del producto, y traersela entera para filtrar en el navegador es el
// patron que el sondeo del 2026-08-30 midio haciendose seis veces mas lento
// al pasar de 10 personas a la vez a 15.

export async function listConversations({
  status, channel, assignee, unread, q, page = 1, limit = 25,
} = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) qs.set('status', status);
  if (channel) qs.set('channel', channel);
  if (assignee) qs.set('assignee', assignee);
  if (unread) qs.set('unread', '1');
  if (q) qs.set('q', q);
  const r = await apiFetch(`/api/v1/conversations?${qs.toString()}`);
  if (!r.ok) throw new Error(`listConversations failed: ${r.status}`);
  const json = await r.json();
  return { conversations: json.data || [], pagination: json.pagination };
}

export async function getConversationMessages(id, { before } = {}) {
  const qs = before ? `?before=${encodeURIComponent(before)}` : '';
  const r = await apiFetch(`/api/v1/conversations/${encodeURIComponent(id)}/messages${qs}`);
  if (!r.ok) throw new Error(`getConversationMessages failed: ${r.status}`);
  return (await r.json()).data;
}

/**
 * Contestar. Devuelve `{ ok }` — y cuando no se pudo, el MOTIVO, que la
 * pantalla traduce. Un "no se envio" sin decir por que obliga a adivinar entre
 * "no tiene telefono", "esta en No-Textear" y "fallo el proveedor", que son
 * tres cosas con tres soluciones distintas (regla de oro 83).
 */
export async function replyConversation(id, { body, subject } = {}) {
  const r = await apiFetch(`/api/v1/conversations/${encodeURIComponent(id)}/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body, subject }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) return { ok: false, reason: json.reason || json.error || `http_${r.status}` };
  return { ok: true };
}

export async function markConversationRead(id) {
  const r = await apiFetch(`/api/v1/conversations/${encodeURIComponent(id)}/read`, { method: 'POST' });
  if (!r.ok) throw new Error(`markConversationRead failed: ${r.status}`);
  return true;
}

export async function assignConversation(id, assigneeId) {
  const r = await apiFetch(`/api/v1/conversations/${encodeURIComponent(id)}/assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assigneeId: assigneeId || null }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `assignConversation failed: ${r.status}`);
  }
  return true;
}

export async function setConversationStatus(id, status, snoozedUntil) {
  const r = await apiFetch(`/api/v1/conversations/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, snoozedUntil: snoozedUntil || undefined }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `setConversationStatus failed: ${r.status}`);
  }
  return true;
}

// ── El embudo arrastrable ────────────────────────────────────────────────────
//
// El servidor devuelve el tablero ya armado —columnas, totales y las primeras
// tarjetas de cada una— porque agrupar aqui significaria traerse todos los
// leads de la agencia. Cada columna dice ademas su `total`, que es lo que se
// enseña en la cabecera aunque solo bajen las primeras 50 tarjetas.

export async function getPipelineBoard(pipelineId) {
  const qs = pipelineId ? `?pipelineId=${encodeURIComponent(pipelineId)}` : '';
  const r = await apiFetch(`/api/v1/pipelines/board${qs}`);
  if (!r.ok) throw new Error(`getPipelineBoard failed: ${r.status}`);
  return (await r.json()).data;
}

export async function listPipelines() {
  const r = await apiFetch('/api/v1/pipelines');
  if (!r.ok) throw new Error(`listPipelines failed: ${r.status}`);
  return (await r.json()).data || [];
}

/** Mover una tarjeta de columna. El servidor escribe la etapa Y el estado. */
export async function moveLeadToStage(leadId, stageId) {
  const r = await apiFetch(`/api/v1/leads/${encodeURIComponent(leadId)}/stage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stageId }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `moveLeadToStage failed: ${r.status}`);
  }
  return (await r.json()).data;
}

/** El valor y la fecha esperada de cierre de una tarjeta. */
export async function setLeadDeal(leadId, { value, expectedCloseAt } = {}) {
  const r = await apiFetch(`/api/v1/leads/${encodeURIComponent(leadId)}/deal`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value, expectedCloseAt }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `setLeadDeal failed: ${r.status}`);
  }
  return true;
}

export async function createPipelineStage(pipelineId, etapa) {
  const r = await apiFetch(`/api/v1/pipelines/${encodeURIComponent(pipelineId)}/stages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(etapa),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `createPipelineStage failed: ${r.status}`);
  }
  return (await r.json()).data;
}

export async function updatePipelineStage(stageId, cambios) {
  const r = await apiFetch(`/api/v1/pipelines/stages/${encodeURIComponent(stageId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cambios),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `updatePipelineStage failed: ${r.status}`);
  }
  return true;
}

/**
 * Borrar una columna. Los leads que estaban dentro NO se borran: se quedan sin
 * etapa y el servidor los recoloca por su estado. La respuesta dice cuantos
 * fueron, para poder decirselo a quien pulso.
 */
export async function deletePipelineStage(stageId) {
  const r = await apiFetch(`/api/v1/pipelines/stages/${encodeURIComponent(stageId)}`, { method: 'DELETE' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) return { ok: false, reason: j.reason || `http_${r.status}`, error: j.error };
  return { ok: true, leadsRecolocados: j.data?.leadsRecolocados ?? 0 };
}

// Sugerencias / correccion de direcciones para la ficha del cliente
// (deskte-api/src/routes/address.routes.ts). Solo lectura. Nunca lanza: sin
// servidor (demo) o con el proveedor caido devuelve [] y la ficha sigue a mano.
export async function suggestAddresses({ street, city, state, zip }) {
  const qs = new URLSearchParams();
  qs.set('street', street || '');
  if (city) qs.set('city', city);
  if (state) qs.set('state', state);
  if (zip) qs.set('zip', zip);
  try {
    const r = await apiFetch(`/api/v1/address/suggest?${qs.toString()}`);
    if (!r.ok) return [];
    const j = await r.json().catch(() => ({}));
    return Array.isArray(j.data) ? j.data : [];
  } catch {
    return [];
  }
}

// --- HealthSherpa -----------------------------------------------------------
// La conexión de CADA AGENTE con su propia cuenta de HealthSherpa. Por agente
// y no por agencia porque una llave de HealthSherpa se ata a UNA sola cuenta
// de agente: una agencia con cinco agentes que venden son cinco llaves.
//
// Esto es solo la puerta de entrada — guardar, ver y quitar la llave. Todavía
// no trae pólizas: eso necesita que HealthSherpa apruebe el acceso.
//
// La llave NUNCA vuelve del servidor. `getHealthSherpaConnection` solo dice en
// qué estado está, y `state` es lo que la pantalla pinta:
//   not_connected    — nunca pegó una llave
//   pending_approval — guardada y todavía sin aprobar; NO hay nada que hacer
//   connected        — funcionando
//   disconnected     — funcionaba y dejó de funcionar; hay que reconectar
export async function getHealthSherpaConnection() {
  const r = await apiFetch('/api/v1/healthsherpa');
  if (!r.ok) return null;
  return (await r.json().catch(() => ({}))).data ?? null;
}
// payload: { apiKey, accountEmail, acceptedTerms: true }
export async function connectHealthSherpa(payload) {
  const r = await apiFetch('/api/v1/healthsherpa/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `connectHealthSherpa failed: ${r.status}`);
  }
  return (await r.json()).data;
}
// Le pregunta a HealthSherpa si la llave guardada ya sirve, y actualiza el
// estado con la respuesta. Existe porque sin esto la pantalla se quedaba en
// "Esperando aprobación" para siempre: nada comprobaba nunca la llave.
//
// `unverified: true` en la respuesta significa "ni sí ni no" (su servidor no
// contestó). En ese caso NO se cambió nada: no es que la conexión se cayera.
//
// Devuelve además `diag` — la URL que se llamó, el código HTTP y lo que
// contestaron — para poder saber QUIÉN rechazó. Un 403 de HealthSherpa y un
// 403 de un proxy de salida son el mismo número y significan cosas opuestas.
// El cuerpo solo viene cuando algo falló: una respuesta buena trae pólizas.
export async function testHealthSherpa() {
  const r = await apiFetch('/api/v1/healthsherpa/test', { method: 'POST' });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `testHealthSherpa failed: ${r.status}`);
  }
  return (await r.json()).data;
}
export async function disconnectHealthSherpa() {
  const r = await apiFetch('/api/v1/healthsherpa', { method: 'DELETE' });
  if (!r.ok) throw new Error(`disconnectHealthSherpa failed: ${r.status}`);
  return r.json();
}
// EL VISTAZO. Trae las solicitudes de HealthSherpa SIN escribir nada: no toca
// ni un cliente, ni una póliza, ni una fila de nada. Existe porque hasta que
// la llave no quedó autorizada (2026-10-26) nunca habíamos visto una respuesta
// buena suya, y todo lo que viene después — traducir sus campos a una póliza,
// emparejar con el cliente correcto — depende de cómo se llamen esos campos.
//
// Devuelve `items` (las filas tal cual, para la propia agente), `resumen` (la
// forma: nombres de campo y tipos, sin valores personales) y `resumenTexto`,
// que es ese resumen listo para copiar y pegar.
// INSCRIPCIÓN. Deskte crea la sesión en HealthSherpa con un identificador
// propio y devuelve los enlaces. Cuando esa póliza aparezca luego en el
// estado, vendrá con ese identificador puesto: nace emparejada y nadie tiene
// que adivinar nada. Es la vía que HealthSherpa documenta para un CRM.
//
// `consentimiento` no es una casilla decorativa: su contrato exige
// consentimiento documentado del consumidor antes de arrancar una sesión.
export async function createHealthSherpaEnrollmentSession({ clientId, flujo, planYear, planId }) {
  const r = await apiFetch('/api/v1/healthsherpa/enrollment-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId, flujo, planYear, planId, consentimiento: true }),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || `createHealthSherpaEnrollmentSession failed: ${r.status}`);
  return body.data;
}
// Devuelve las sesiones YA creadas de ese cliente y, aparte, dos listas de
// ETIQUETAS (nunca valores): `prerelleno`, los datos de su ficha que viajarían
// a HealthSherpa, y `faltante`, los que no — con el motivo, que es lo que el
// agente puede arreglar antes de crear el enlace.
export async function getHealthSherpaEnrollmentSessions(clientId) {
  const r = await apiFetch(`/api/v1/healthsherpa/enrollment-session?clientId=${encodeURIComponent(clientId)}`);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || `getHealthSherpaEnrollmentSessions failed: ${r.status}`);
  return {
    sesiones: body.data || [],
    prerelleno: body.prerelleno || [],
    faltante: body.faltante || [],
    // A nombre de QUE AGENTE saldría la inscripción: el dueño del cliente.
    // Importa cuando quien teclea es un asiento que trabaja dos libros.
    aNombreDe: body.aNombreDe || null,
  };
}
// EMPAREJAR. Trae las pólizas de HealthSherpa, las del CRM, y las parejas
// PROPUESTAS — nada queda atado hasta que la persona lo confirma. HealthSherpa
// no manda nombres, así que la propuesta se apoya en la aseguradora y la fecha
// de efecto, y eso no siempre alcanza: cada candidata dice si es segura.
export async function getHealthSherpaEmparejar() {
  const r = await apiFetch('/api/v1/healthsherpa/emparejar');
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(body.error || `getHealthSherpaEmparejar failed: ${r.status}`);
    e.diag = body.diag || null;
    throw e;
  }
  return body.data;
}
// Guarda los enlaces confirmados. No toca el estado de ninguna póliza: atar es
// decir "esta es esta", y traer el estado es otro paso.
export async function saveHealthSherpaEmparejar(enlaces) {
  const r = await apiFetch('/api/v1/healthsherpa/emparejar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enlaces }),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || `saveHealthSherpaEmparejar failed: ${r.status}`);
  return body.data;
}
export async function unlinkHealthSherpaPolicy(policyId) {
  const r = await apiFetch(`/api/v1/healthsherpa/emparejar/${encodeURIComponent(policyId)}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`unlinkHealthSherpaPolicy failed: ${r.status}`);
  return r.json();
}
export async function getHealthSherpaApplications(limit, detalles) {
  const q = new URLSearchParams();
  if (limit) q.set('limit', String(limit));
  // `detalles` es cuántas pólizas se leen a fondo. Cada una es UNA llamada
  // contra la cuota de la agente, así que el valor lo decide quien llama, no
  // la pantalla por su cuenta.
  if (detalles !== undefined && detalles !== null) q.set('detalles', String(detalles));
  const sufijo = q.toString() ? `?${q}` : '';
  const r = await apiFetch(`/api/v1/healthsherpa/applications${sufijo}`);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(body.error || `getHealthSherpaApplications failed: ${r.status}`);
    // El diagnóstico viaja con el error: es lo único que dice si el rechazo lo
    // puso HealthSherpa o algo nuestro por el camino.
    e.diag = body.diag || null;
    throw e;
  }
  return body.data;
}

// ── ENCUESTAS AL CLIENTE (Lissette 2026-10-15) ─────────────────────────────
// La encuesta es de la agencia; las respuestas, de cada cliente (y salen con
// el mismo alcance que su ficha). Ver deskte-api/src/routes/encuestas.routes.ts.
async function encuestaJson(r, que) {
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `${que} failed: ${r.status}`);
  return json;
}
export async function listEncuestas() {
  return (await encuestaJson(await apiFetch('/api/v1/encuestas'), 'listEncuestas')).data || [];
}
export async function getEncuesta(id) {
  return (await encuestaJson(await apiFetch(`/api/v1/encuestas/${encodeURIComponent(id)}`), 'getEncuesta')).data;
}
export async function createEncuesta(payload) {
  const r = await apiFetch('/api/v1/encuestas', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  return (await encuestaJson(r, 'createEncuesta')).data;
}
export async function updateEncuesta(id, payload) {
  const r = await apiFetch(`/api/v1/encuestas/${encodeURIComponent(id)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  return (await encuestaJson(r, 'updateEncuesta')).data;
}
export async function deleteEncuesta(id) {
  await encuestaJson(await apiFetch(`/api/v1/encuestas/${encodeURIComponent(id)}`, { method: 'DELETE' }), 'deleteEncuesta');
}
export async function enlaceDeEncuestaParaCliente(id, clientId) {
  const r = await apiFetch(`/api/v1/encuestas/${encodeURIComponent(id)}/enlace`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ clientId }),
  });
  return (await encuestaJson(r, 'enlaceDeEncuesta')).data;
}
export async function encuestasDelCliente(clientId) {
  return (await encuestaJson(await apiFetch(`/api/v1/encuestas/cliente/${encodeURIComponent(clientId)}`), 'encuestasDelCliente')).data || [];
}
// Página pública: sin sesión, el token es la llave.
export async function getEncuestaPublica(token) {
  const r = await fetch(`${API_BASE}/encuesta-publica/${encodeURIComponent(token)}`);
  return (await encuestaJson(r, 'getEncuestaPublica')).data;
}
export async function responderEncuestaPublica(token, respuestas, idioma) {
  const r = await fetch(`${API_BASE}/encuesta-publica/${encodeURIComponent(token)}/responder`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ respuestas, idioma }),
  });
  return encuestaJson(r, 'responderEncuesta');
}

// Quitar UNA etiqueta en masa (Lissette 2026-10-22, ticket de German Pomares).
// Sin `ids` = de todas las fichas que la llevan (las que quien pide puede ver);
// con `ids` = solo de esas. Devuelve { quitadas, ids } con las que cambiaron.
async function quitarEtiquetaEnMasa(ruta, tag, ids) {
  const r = await apiFetch(ruta, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag, ...(Array.isArray(ids) && ids.length ? { ids } : {}) }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `quitarEtiqueta failed: ${r.status}`);
  return json.data || { quitadas: 0, ids: [] };
}
export const quitarEtiquetaDeClientes = (tag, ids) => quitarEtiquetaEnMasa('/api/v1/clients/tags/remove', tag, ids);
export const quitarEtiquetaDePolizas = (tag, ids) => quitarEtiquetaEnMasa('/api/v1/policies/tags/remove', tag, ids);
